<template>
    <div class="materials-table">    
        <b-card class="request-glass-card">
            <b-card-header class="d-flex flex-wrap justify-content-between align-items-center">
                <b-alert v-if="requestSaveError" show variant="danger" role="alert" class="w-100 mb-2">{{ requestSaveError }}</b-alert>
                <h6 class="mb-0">
                    <i :class="catalogIcon"></i>
                    {{ catalogTitle }}
                </h6>
                <div>
                    <b-button
                        v-if="selfAssignPreviewEnabled && !isMultiExecutor"
                        variant="outline-secondary"
                        size="sm"
                        class="mr-2"
                        :disabled="reassigning"
                        @click="$emit('self-assign-preview-cancelled')"
                    >
                        <i class="fas fa-times"></i> Скрыть построчные действия
                    </b-button>
                    <b-button
                        v-if="showSelfAssignBulkButton"
                        variant="outline-primary"
                        size="sm"
                        class="mr-2"
                        :disabled="reassigning"
                        @click="confirmSelfAssignBulk"
                    >
                        <i class="fas fa-hand-paper"></i> Взять все свободные
                    </b-button>
                    <b-button
                        v-if="showBulkReassignButton"
                        variant="outline-secondary"
                        size="sm"
                        class="mr-2"
                        :disabled="reassigning"
                        @click="openBulkReassignModal"
                    >
                        <i class="fas fa-users-cog"></i> Переназначить строки
                    </b-button>
                    <!-- Режим редактирования отгрузки/приемки -->
                    <template v-if="!canEditListEffective">
                        <b-button
                            v-if="canEdit && !isEditing"
                            variant="primary"
                            size="sm"
                            @click="startEditing"
                            class="mr-2"
                        >
                            <i class="fas fa-edit"></i> {{ editButtonText }}
                        </b-button>
                        <b-button
                            v-if="isEditing"
                            variant="success"
                            size="sm"
                            @click="saveChanges"
                            :disabled="!hasChanges || saving"
                            class="mr-2"
                        >
                            <b-spinner small v-if="saving" class="mr-1"></b-spinner>
                            <i class="fas fa-save" v-if="!saving"></i> 
                            {{ saving ? 'Сохранение...' : 'Сохранить' }}
                        </b-button>
                        <b-button
                            v-if="isEditing"
                            variant="secondary"
                            size="sm"
                            @click="cancelEditing"
                            :disabled="saving"
                        >
                            <i class="fas fa-times"></i> Отменить
                        </b-button>
                    </template>
                    
                    <!-- Режим редактирования списка материалов -->
                  <template v-else>
                    <b-button
                        v-if="!isEditingList"
                        variant="primary"
                        size="sm"
                        @click="startEditingList"
                        :disabled="editingListStarting"
                        class="mr-2"
                    >
                      <b-spinner v-if="editingListStarting" small class="mr-1"></b-spinner>
                      <i v-else class="fas fa-list-ul"></i>
                      {{ editingListStarting ? 'Подготовка...' : editListButtonText }}
                    </b-button>

                    <template v-else>
                      <b-button
                          v-if="isMechanicAssetCatalog"
                          variant="success"
                          size="sm"
                          @click="requestSaveList"
                          :disabled="!(hasMaterialsListChanges || hasRequestChanges) || savingRequest"
                          class="mr-2"
                      >
                        <b-spinner small v-if="savingRequest" class="mr-1"></b-spinner>
                        <i class="fas fa-save" v-if="!savingRequest"></i>
                        {{ savingRequest ? 'Сохранение...' : 'Сохранить' }}
                      </b-button>

                      <b-button
                          v-if="!isMechanicShipmentMode && !ppePlannerActive"
                          variant="outline-primary"
                          size="sm"
                          @click="openMaterialSelector"
                          class="mr-2"
                      >
                        <i class="fas fa-list"></i> Из справочника
                      </b-button>

                      <b-button
                          v-if="!isMechanicShipmentMode && canAddCustomCatalogItem && !ppePlannerActive"
                          variant="outline-success"
                          size="sm"
                          @click="showAddCustomMaterial = true"
                          class="mr-2"
                      >
                        <i class="fas fa-plus"></i> Вручную
                      </b-button>

                      <b-button
                          variant="outline-secondary"
                          size="sm"
                          @click="cancelEditingList"
                      >
                        <i class="fas fa-times"></i> Закрыть
                      </b-button>
                    </template>
                  </template>
                </div>
            </b-card-header>
            <b-card-body class="p-0">
                <b-alert
                    v-if="hasFuelCardRowsWithoutCard && canManageFuelCardSelection"
                    show
                    variant="warning"
                    class="m-3 mb-0"
                >
                    <i class="fas fa-gas-pump mr-1"></i>
                    Перед завершением заявки выберите топливную карту для каждого получателя.
                </b-alert>
                <b-alert
                    v-if="hasFuelCardRowsWithoutTargetObject && canManageFuelCardSelection"
                    show
                    variant="warning"
                    class="m-3 mb-0"
                >
                    <i class="fas fa-map-marker-alt mr-1"></i>
                    Перед завершением заявки выберите объект назначения для каждой перемещаемой карты.
                </b-alert>
                <b-alert
                    v-if="hasMechanicRowsWithoutAsset && canManageMechanicAssetSelection"
                    show
                    variant="warning"
                    class="m-3 mb-0"
                >
                    <i class="fas fa-tools mr-1"></i>
                    Перед завершением заявки выберите конкретную единицу техники по каждой позиции.
                </b-alert>
                <b-alert
                    v-if="hasMechanicRowsWithoutTargetObject && canManageMechanicAssetSelection"
                    show
                    variant="warning"
                    class="m-3 mb-0"
                >
                    <i class="fas fa-map-marker-alt mr-1"></i>
                    Перед завершением заявки выберите объект назначения по каждой позиции техники.
                </b-alert>
                <div
                    v-if="editingListStarting"
                    class="materials-edit-loading m-3"
                >
                    <b-spinner small class="mr-2"></b-spinner>
                    Подготавливаю таблицу редактирования...
                </div>
                <div
                    v-if="showMechanicShipmentBulkControls"
                    class="mechanic-shipment-bulk-controls p-3 border-bottom bg-light"
                >
                    <div class="d-flex flex-column flex-lg-row align-items-lg-end justify-content-lg-end">
                        <b-form-group
                            v-if="mechanicAssetNeedsTargetUser && (!isMechanicRepairMode || hasMechanicRepairAcceptedRows)"
                            label="МОЛ / получатель на все позиции"
                            class="mechanic-shipment-bulk-controls__field mb-2 mb-lg-0 mr-lg-3"
                        >
                            <v-select
                                :value="null"
                                :options="mechanicAssetUserOptions"
                                label="text"
                                :reduce="option => option.value"
                                placeholder="Начните вводить сотрудника..."
                                :clearable="true"
                                append-to-body
                                :calculate-position="positionModalSelectDropdown"
                                @input="applyMechanicBulkTargetUser"
                            >
                                <template #no-options>
                                    Сотрудники не найдены
                                </template>
                            </v-select>
                        </b-form-group>
                        <b-form-group
                            v-if="mechanicAssetNeedsObject || hasMechanicReturnObjectRows"
                            :label="mechanicReturnObjectBulkLabel"
                            class="mechanic-shipment-bulk-controls__field mb-0"
                        >
                            <v-select
                                :value="null"
                                :options="mechanicAssetObjectOptions"
                                label="text"
                                :reduce="option => option.value"
                                placeholder="Начните вводить объект..."
                                :clearable="true"
                                :disabled="mechanicAssetShowsReturnObject && mechanicAssetReturnObjectOptionsLoading"
                                :loading="mechanicAssetShowsReturnObject && mechanicAssetReturnObjectOptionsLoading"
                                append-to-body
                                :calculate-position="positionModalSelectDropdown"
                                @open="ensureMechanicAssetReturnObjectOptions"
                                @input="applyMechanicBulkTargetObject"
                            >
                                <template #no-options>
                                    {{ mechanicAssetReturnObjectOptionsLoading ? 'Загрузка объектов...' : 'Объекты не найдены' }}
                                </template>
                            </v-select>
                        </b-form-group>
                        <b-form-group
                            v-if="mechanicAssetNeedsWarehouse && (!isMechanicRepairMode || hasMechanicRepairAcceptedRows)"
                            :label="mechanicWarehouseBulkLabel"
                            class="mechanic-shipment-bulk-controls__field mb-0 ml-lg-3"
                        >
                            <v-select
                                :value="null"
                                :options="mechanicAssetWarehouseTargetOptions"
                                label="text"
                                :reduce="option => option.value"
                                placeholder="Начните вводить сценарий..."
                                :clearable="true"
                                append-to-body
                                :calculate-position="positionModalSelectDropdown"
                                @input="applyMechanicBulkTargetWarehouse"
                            >
                                <template #no-options>
                                    Сценарии не найдены
                                </template>
                            </v-select>
                        </b-form-group>
                    </div>
                </div>

                <!-- Режим редактирования списка материалов - используем компонент MaterialsList -->
                <div v-if="isEditingList && !isFuelCardCatalog && !isMechanicAssetCatalog" class="p-3">
                  <RequestPpePlanner v-if="catalogType === 'materials'" ref="ppePlanner" :items="localMaterials" :request-id="requestId" :request-type-id="requestTypeId"
                      @update:items="updatePpePlan" @active="ppePlannerActive = $event" @managed-items="ppeManagedItems = $event" @remove="onMaterialListRemove">
                      <template #add-items>
                          <div>
                              <b-button size="sm" variant="primary" class="mr-2" @click="openMaterialSelector">+ Добавить вещь</b-button>
                              <b-button v-if="canAddCustomCatalogItem" size="sm" variant="link" @click="showAddCustomMaterial = true">Нет в списке? Ввести название</b-button>
                          </div>
                      </template>
                  </RequestPpePlanner>
                  <MaterialsList
                      :materials="localMaterials"
                      :employee-catalog="isEmployeeCatalog"
                      :hidden-materials="ppePlannerActive ? ppeManagedItems : []"
                      :editable="true"
                      :lock-directory-names="true"
                      :show-remove-button="true"
                      :show-quantity="showCatalogItemQuantity"
                      :empty-text="`${catalogLabel} не выбраны`"
                      @update:material="onMaterialListUpdate"
                      @remove:material="onMaterialListRemove"
                  >
                      <template #item-extra="{ material }">
                          <RequestItemPpe default-open :value="material.ppe_data" :item="material" :editable="!ppePlannerActive" :request-id="requestId" :request-type-id="requestTypeId"
                              @input="updatePpePlan(localMaterials.map(row => row === material ? {...row, ppe_data: $event} : row))" />
                      </template>
                  </MaterialsList>
                </div>

                <!-- Режим просмотра и редактирования отгрузки/приемки - используем таблицу -->
              <div v-else class="table-responsive table-scroll" ref="materialsScroll">
                    <b-table
                        stacked="md"
                        :items="displayedMaterials"
                        :fields="tableFields"
                        :tbody-tr-class="rowClass"
                        small
                        striped
                        hover
                        class="mb-0"
                        thead-class="bg-light"
                    >
                        <!-- № п/п -->
                        <template #cell(index)="{ index }">
                            <span class="text-muted">{{ index + 1 }}</span>
                        </template>

                        <!-- Наименование -->
                        <template #cell(item_name)="{ item }">
                            <div v-if="isEditingList" class="material-name-edit">
                                <template v-if="isMechanicAssetCatalog">
                                    <div class="d-flex align-items-center mb-1">
                                        <b-form-input
                                            v-if="isMechanicRequestEditMode && isMechanicManualEditable(item)"
                                            v-model="getEditedItem(item).item_name"
                                            placeholder="Название позиции"
                                            size="sm"
                                            :state="validateItemName(item)"
                                            @input="onMaterialFieldChange"
                                            class="flex-grow-1 mr-2"
                                        />
                                        <strong v-else class="mechanic-requested-name">
                                            {{ getMechanicRequestedDisplayName(item) }}
                                        </strong>
                                        <b-badge
                                            v-if="isMechanicManualEditable(item)"
                                            variant="warning"
                                            class="flex-shrink-0 ml-2"
                                        >
                                            Вручную
                                        </b-badge>
                                    </div>
                                    <small
                                        v-if="getEditedItem(item).catalog_name"
                                        class="d-block text-muted mb-1"
                                    >
                                        Группа: {{ getEditedItem(item).catalog_name }}
                                    </small>
                                </template>
                                <template v-else>
                                    <!-- Первая строка: название и badge -->
                                    <div class="d-flex align-items-center mb-1">
                                        <b-form-input
                                            v-model="getEditedItem(item).item_name"
                                            placeholder="Название материала"
                                            size="sm"
                                            :disabled="!isNameEditable(item)"
                                            :state="validateItemName(item)"
                                            @input="onMaterialFieldChange"
                                            class="flex-grow-1 mr-2"
                                            v-b-tooltip.hover
                                            :title="!isNameEditable(item) ? 'Материал из справочника — наименование менять нельзя' : ''"
                                        />
                                        <b-badge v-if="item.is_custom || getEditedItem(item).isCustom" variant="warning" class="flex-shrink-0">
                                            <i class="fas fa-exclamation-circle"></i> Вручную
                                        </b-badge>
                                    </div>
                                </template>
                                <!-- Вторая строка: примечания на всю ширину -->
                                <AppTextarea
                                    v-if="!isMechanicAssetCatalog || isMechanicRequestEditMode"
                                    v-model="getEditedItem(item).notes"
                                    :placeholder="isMechanicAssetCatalog ? 'Примечание по позиции' : 'Примечания к материалу (необязательно)'"
                                    size="sm"
                                    class="w-100"
                                    maxlength="1000"
                                    @input="onMaterialFieldChange"
                                />
                                <small
                                    v-else-if="getEditedItem(item).notes"
                                    class="d-block text-muted font-italic mt-1 linkified-text"
                                    v-html="formatLinkedText(getEditedItem(item).notes)"
                                ></small>
                            </div>
                            <div v-else class="material-name">
                                <strong v-if="isFuelCardCatalog && !item.catalog_item_id">Карта не выбрана</strong>
                                <EmployeeLink
                                    v-else-if="isCatalogEmployeeItem(item)"
                                    class="font-weight-bold"
                                    :user="getCatalogEmployeeLinkUser(item)"
                                    :fallback="item.item_name || 'Сотрудник'"
                                    name-order="last-first"
                                />
                                <strong v-else>{{ item.item_name }}</strong>
                                <small v-if="item.item_description" class="d-block text-muted">
                                    {{ item.item_description }}
                                </small>                     
                                <small v-if="item.notes" class="d-block text-muted font-italic mt-1">
                                    <i class="fas fa-comment"></i>
                                    <span class="linkified-text" v-html="formatLinkedText(item.notes)"></span>
                                </small>
                            <b-badge v-if="item.is_custom" variant="warning">
                                Вручную
                             </b-badge>
                            </div>
                            <RequestItemPpe default-open :value="isEditingList ? getEditedItem(item).ppe_data : item.ppe_data"
                                :item="isEditingList ? getEditedItem(item) : item" :editable="isEditingList && canEditList"
                                :request-id="requestId" :request-type-id="requestTypeId"
                                @input="$set(getEditedItem(item), 'ppe_data', $event); onMaterialFieldChange()" />
                        </template>

                        <template #cell(notes)="{ item }">
                            <span v-if="item.notes" class="linkified-text" v-html="formatLinkedText(item.notes)"></span>
                            <span v-else class="text-muted">-</span>
                        </template>

                        <template #cell(mechanic_asset_selection)="{ item }">
                            <div
                                v-if="isMechanicShipmentMode"
                                :class="[
                                    'mechanic-asset-selection-cell',
                                    {
                                        'mechanic-asset-selection-cell--required': shouldHighlightMechanicAssetSelection(item),
                                        'mechanic-asset-selection-cell--manual': isMechanicManualEditable(item),
                                        'mechanic-asset-selection-cell--suggested': hasMechanicPendingRequestedAsset(item)
                                    }
                                ]"
                            >
                                <div
                                    v-if="shouldHighlightMechanicAssetSelection(item) && !hasMechanicPendingRequestedAsset(item)"
                                    class="mechanic-asset-selection-hint"
                                >
                                    <i class="fas fa-exclamation-circle mr-1"></i>
                                    Сопоставьте позицию
                                </div>
                                <div
                                    v-else-if="isMechanicManualEditable(item)"
                                    class="mechanic-asset-selection-hint mechanic-asset-selection-hint--manual"
                                >
                                    <i class="fas fa-pen mr-1"></i>
                                    Ручной ввод
                                </div>
                                <div
                                    v-if="hasMechanicPendingRequestedAsset(item)"
                                    class="mechanic-requested-asset-suggestion"
                                >
                                    <div class="mechanic-requested-asset-suggestion__body">
                                        <b-badge variant="warning" class="mr-1">
                                            Предложено инициатором
                                        </b-badge>
                                        <strong>{{ getMechanicRequestedAssetName(item) }}</strong>
                                        <small
                                            v-if="getMechanicRequestedAssetDetails(item)"
                                            class="d-block text-muted mt-1"
                                        >
                                            {{ getMechanicRequestedAssetDetails(item) }}
                                        </small>
                                    </div>
                                    <b-button
                                        size="sm"
                                        variant="outline-primary"
                                        class="mechanic-requested-asset-suggestion__action"
                                        @click="confirmMechanicRequestedAsset(item)"
                                    >
                                        <i class="fas fa-check mr-1"></i>
                                        Взять в отгрузку
                                    </b-button>
                                </div>
                                <div
                                    v-if="shouldShowMechanicMainAssetSelect(item)"
                                    class="mechanic-asset-picker-control"
                                >
                                    <div
                                        v-if="getEditedMechanicAssetId(item)"
                                        class="mechanic-asset-picker-control__summary"
                                    >
                                        <div class="mechanic-asset-picker-control__title">
                                            {{ getMechanicAssetSelectedName(item) }}
                                        </div>
                                    </div>
                                    <div v-else class="mechanic-asset-picker-control__empty">
                                        {{ mechanicAssetSelectPlaceholder(item) }}
                                    </div>
                                    <div class="mechanic-asset-picker-control__actions">
                                        <b-button
                                            size="sm"
                                            :variant="getEditedMechanicAssetId(item) ? 'outline-primary' : 'primary'"
                                            @click="openMechanicAssetPicker(item)"
                                        >
                                            <i class="fas fa-search mr-1"></i>
                                            {{ getEditedMechanicAssetId(item) ? 'Заменить' : 'Выбрать' }}
                                        </b-button>
                                        <b-button
                                            v-if="getEditedMechanicAssetId(item)"
                                            size="sm"
                                            variant="outline-secondary"
                                            title="Очистить выбор"
                                            @click="setEditedMechanicAssetSelection(item, null)"
                                        >
                                            <i class="fas fa-times"></i>
                                        </b-button>
                                    </div>
                                </div>
                                <mechanic-asset-transfer-route
                                    v-if="shouldShowMechanicMainAssetSelect(item) && isMechanicAssetSelectedTransfer(item)"
                                    :from="getMechanicTransferFromText(item)"
                                    :to="getMechanicTransferToText(item)"
                                    :extra="getMechanicTransferExtraText(item)"
                                />
                                <div
                                    v-else-if="shouldShowMechanicMainAssetSelect(item) && getMechanicAssetSelectedDetails(item)"
                                    class="mechanic-asset-details-line"
                                >
                                    <span>{{ getMechanicAssetSelectedDetailsText(item) }}</span>
                                </div>
                                <div v-if="shouldShowMechanicMainAssetSelect(item) && getMechanicAssetSelectedOption(item)" class="mechanic-asset-selected-meta">
                                    <router-link
                                        v-if="getEditedMechanicAssetId(item)"
                                        :to="mechanicAssetCardRoute(getEditedMechanicAssetId(item))"
                                        class="mechanic-asset-card-link"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Открыть карточку в справочнике"
                                        @click.native.stop
                                    >
                                        <i class="fas fa-external-link-alt mr-1"></i>
                                        Карточка
                                    </router-link>
                                    <b-badge
                                        v-if="getMechanicAssetSelectedInventoryNumber(item)"
                                        variant="secondary"
                                        class="mr-1"
                                    >
                                        инв. {{ getMechanicAssetSelectedInventoryNumber(item) }}
                                    </b-badge>
                                    <b-badge
                                        v-if="shouldShowMechanicAssetStatus(item) && getMechanicAssetOptionStatusText(getMechanicAssetSelectedOption(item)) && !isMechanicTransferDetails(getMechanicAssetSelectedDetails(item))"
                                        :variant="getMechanicAssetOptionStatusVariant(getMechanicAssetSelectedOption(item))"
                                        class="mr-1"
                                    >
                                        {{ getMechanicAssetOptionStatusText(getMechanicAssetSelectedOption(item)) }}
                                    </b-badge>
                                    <b-badge
                                        v-if="isMechanicQuantityRequest(item)"
                                        variant="info"
                                        class="mr-1"
                                    >
                                        Сопоставлено
                                    </b-badge>
                                    <b-badge
                                        v-if="getMechanicShipmentSelectionBadgeText(item)"
                                        :variant="getMechanicShipmentSelectionBadgeVariant(item)"
                                        class="mr-1"
                                    >
                                        {{ getMechanicShipmentSelectionBadgeText(item) }}
                                    </b-badge>
                                    <b-badge
                                        v-if="isMechanicAssetUsedMoreThanOnce(item)"
                                        variant="warning"
                                        class="mr-1"
                                    >
                                        Уже используется в заявке
                                    </b-badge>
                                </div>
                                <div
                                    v-if="shouldUseMechanicFulfillments(item)"
                                    class="mechanic-fulfillments"
                                >
                                    <div
                                        v-for="(fulfillment, fulfillmentIndex) in getEditedMechanicFulfillments(item)"
                                        :key="fulfillment._key || fulfillment.id || fulfillmentIndex"
                                        class="mechanic-fulfillment-row"
                                    >
                                        <div class="mechanic-fulfillment-picker">
                                            <div
                                                v-if="getMechanicFulfillmentOption(fulfillment)"
                                                class="mechanic-fulfillment-picker__summary"
                                            >
                                                <div class="mechanic-fulfillment-picker__title">
                                                    {{ getMechanicFulfillmentDisplayName(fulfillment) }}
                                                </div>
                                            </div>
                                            <div v-else class="mechanic-fulfillment-picker__empty">
                                                Техника не выбрана
                                            </div>
                                            <div class="mechanic-fulfillment-picker__actions">
                                                <b-button
                                                    size="sm"
                                                    :variant="getMechanicFulfillmentOption(fulfillment) ? 'outline-primary' : 'primary'"
                                                    @click="openMechanicAssetPicker(item, fulfillmentIndex)"
                                                >
                                                    <i class="fas fa-search mr-1"></i>
                                                    {{ getMechanicFulfillmentOption(fulfillment) ? 'Заменить' : 'Выбрать' }}
                                                </b-button>
                                                <b-button
                                                    v-if="getMechanicFulfillmentOption(fulfillment)"
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    title="Очистить выбор"
                                                    @click="setMechanicFulfillmentAsset(item, fulfillmentIndex, null)"
                                                >
                                                    <i class="fas fa-times"></i>
                                                </b-button>
                                            </div>
                                        </div>
                                        <b-button
                                            variant="outline-danger"
                                            size="sm"
                                            title="Удалить сопоставление"
                                            @click="removeMechanicFulfillment(item, fulfillmentIndex)"
                                        >
                                            <i class="fas fa-times"></i>
                                        </b-button>
                                        <div v-if="getMechanicFulfillmentOption(fulfillment)" class="mechanic-fulfillment-meta">
                                            <router-link
                                                v-if="getMechanicFulfillmentAssetId(fulfillment)"
                                                :to="mechanicAssetCardRoute(getMechanicFulfillmentAssetId(fulfillment))"
                                                class="mechanic-asset-card-link"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="Открыть карточку в справочнике"
                                                @click.native.stop
                                            >
                                                <i class="fas fa-external-link-alt mr-1"></i>
                                                Карточка
                                            </router-link>
                                            <b-badge
                                                v-if="getMechanicFulfillmentInventoryNumber(fulfillment)"
                                                variant="secondary"
                                                class="mr-1"
                                            >
                                                инв. {{ getMechanicFulfillmentInventoryNumber(fulfillment) }}
                                            </b-badge>
                                            <b-badge
                                                v-if="getMechanicAssetOptionStatusText(getMechanicFulfillmentOption(fulfillment))"
                                                :variant="getMechanicAssetOptionStatusVariant(getMechanicFulfillmentOption(fulfillment))"
                                                class="mr-1"
                                            >
                                                {{ getMechanicAssetOptionStatusText(getMechanicFulfillmentOption(fulfillment)) }}
                                            </b-badge>
                                            <b-badge
                                                v-if="isMechanicFulfillmentQuantity(fulfillment)"
                                                variant="info"
                                                class="mr-1"
                                            >
                                                Количественная
                                            </b-badge>
                                            <b-badge
                                                v-if="isMechanicFulfillmentAssetUsedMoreThanOnce(fulfillment)"
                                                variant="warning"
                                                class="mr-1"
                                            >
                                                Уже используется в заявке
                                            </b-badge>
                                            <small
                                                v-if="getMechanicFulfillmentOption(fulfillment).assignment_details"
                                                class="text-muted"
                                            >
                                                {{ getMechanicFulfillmentOption(fulfillment).assignment_details }}
                                            </small>
                                        </div>
                                        <b-input-group
                                            v-if="isMechanicFulfillmentQuantity(fulfillment)"
                                            size="sm"
                                            class="mechanic-fulfillment-quantity"
                                        >
                                            <b-form-input
                                                :value="fulfillment.shipped_quantity"
                                                type="number"
                                                min="0"
                                                step="0.001"
                                                @input="setMechanicFulfillmentQuantity(item, fulfillmentIndex, $event)"
                                            />
                                            <b-input-group-append>
                                                <b-input-group-text class="text-muted">
                                                    {{ getMechanicFulfillmentUnit(fulfillment) }}
                                                </b-input-group-text>
                                            </b-input-group-append>
                                        </b-input-group>
                                    </div>
                                    <b-button
                                        variant="outline-primary"
                                        size="sm"
                                        class="mt-1"
                                        @click="addMechanicFulfillment(item)"
                                    >
                                        <i class="fas fa-plus mr-1"></i>
                                        Добавить ещё технику
                                    </b-button>
                                    <small class="d-block text-muted mt-1">
                                        {{ getMechanicFulfillmentRowsSummary(item) }}
                                    </small>
                                </div>
                                <b-badge
                                    v-if="(!shouldUseMechanicFulfillments(item) && !getEditedMechanicAssetId(item)) ||
                                        (shouldUseMechanicFulfillments(item) &&
                                            !getEditedMechanicAssetId(item) &&
                                            getMechanicFulfillmentSelectedCount(item) === 0)"
                                    :variant="isMechanicSelectionRequired(item) ? 'warning' : 'secondary'"
                                    class="mt-1"
                                >
                                    {{ isMechanicSelectionRequired(item) ? 'Ожидает выбора' : 'Не сопоставлено' }}
                                </b-badge>
                            </div>
                            <div v-else-if="hasMechanicFulfillments(item)" class="mechanic-fulfillments-readonly">
                                <div
                                    v-if="shouldShowMechanicReadonlyMainFulfillment(item)"
                                    class="mechanic-fulfillment-readonly-row"
                                >
                                    <div class="mechanic-asset-selected-title">
                                        <router-link
                                            v-if="getEditedMechanicAssetId(item)"
                                            :to="mechanicAssetCardRoute(getEditedMechanicAssetId(item))"
                                            class="mechanic-asset-card-link mechanic-asset-card-link--name"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Открыть карточку в справочнике"
                                            @click.native.stop
                                        >
                                            {{ getMechanicAssetSelectedName(item) }}
                                        </router-link>
                                        <strong v-else>{{ getMechanicAssetSelectedName(item) }}</strong>
                                        <b-badge
                                            v-if="getMechanicAssetSelectedInventoryNumber(item)"
                                            variant="secondary"
                                            class="ml-1"
                                        >
                                            инв. {{ getMechanicAssetSelectedInventoryNumber(item) }}
                                        </b-badge>
                                        <b-badge
                                            v-if="isMechanicAssetUsedMoreThanOnce(item)"
                                            variant="warning"
                                            class="ml-1"
                                        >
                                            Уже используется в заявке
                                        </b-badge>
                                    </div>
                                    <b-badge
                                        v-if="shouldShowMechanicAssetStatus(item) && getMechanicAssetOptionStatusText(getMechanicAssetSelectedOption(item)) && !isMechanicTransferDetails(getMechanicAssetSelectedDetails(item))"
                                        :variant="getMechanicAssetOptionStatusVariant(getMechanicAssetSelectedOption(item))"
                                        class="ml-1"
                                    >
                                        {{ getMechanicAssetOptionStatusText(getMechanicAssetSelectedOption(item)) }}
                                    </b-badge>
                                    <mechanic-asset-transfer-route
                                        v-if="isMechanicAssetSelectedTransfer(item)"
                                        :from="getMechanicTransferFromText(item)"
                                        :to="getMechanicTransferToText(item)"
                                        :extra="getMechanicTransferExtraText(item)"
                                    />
                                    <div
                                        v-else-if="getMechanicAssetSelectedDetails(item)"
                                        class="mechanic-asset-details-line"
                                    >
                                        <span>{{ getMechanicAssetSelectedDetailsText(item) }}</span>
                                    </div>
                                </div>
                                <div
                                    v-for="(fulfillment, fulfillmentIndex) in item.mechanic_fulfillments"
                                    :key="fulfillment.id || fulfillment.asset_unit_id || fulfillmentIndex"
                                    class="mechanic-fulfillment-readonly-row"
                                >
                                    <div class="mechanic-asset-selected-title">
                                        <router-link
                                            v-if="getMechanicFulfillmentAssetId(fulfillment)"
                                            :to="mechanicAssetCardRoute(getMechanicFulfillmentAssetId(fulfillment))"
                                            class="mechanic-asset-card-link mechanic-asset-card-link--name"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Открыть карточку в справочнике"
                                            @click.native.stop
                                        >
                                            {{ getMechanicFulfillmentDisplayName(fulfillment) }}
                                        </router-link>
                                        <strong v-else>{{ getMechanicFulfillmentDisplayName(fulfillment) }}</strong>
                                        <b-badge
                                            v-if="getMechanicFulfillmentInventoryNumber(fulfillment)"
                                            variant="secondary"
                                            class="ml-1"
                                        >
                                            инв. {{ getMechanicFulfillmentInventoryNumber(fulfillment) }}
                                        </b-badge>
                                        <b-badge
                                            v-if="isMechanicFulfillmentAssetUsedMoreThanOnce(fulfillment)"
                                            variant="warning"
                                            class="ml-1"
                                        >
                                            Уже используется в заявке
                                        </b-badge>
                                    </div>
                                    <b-badge
                                        v-if="getMechanicFulfillmentStatusText(fulfillment)"
                                        :variant="getMechanicFulfillmentStatusVariant(fulfillment)"
                                        class="ml-1"
                                    >
                                        {{ getMechanicFulfillmentStatusText(fulfillment) }}
                                    </b-badge>
                                    <small v-if="fulfillment.assignment_details" class="d-block text-muted">
                                        {{ fulfillment.assignment_details }}
                                    </small>
                                    <mechanic-asset-transfer-route
                                        v-if="fulfillment.active_transfer && fulfillment.active_transfer.active"
                                        :from="getMechanicTransferPointText(fulfillment.active_transfer.from)"
                                        :to="getMechanicTransferPointText(fulfillment.active_transfer.to)"
                                        :extra="getMechanicFulfillmentTransferExtraText(fulfillment)"
                                    />
                                </div>
                            </div>
                            <div v-else-if="getMechanicAssetSelectedName(item)">
                                <div class="mechanic-asset-selected-title">
                                    <router-link
                                        v-if="getEditedMechanicAssetId(item)"
                                        :to="mechanicAssetCardRoute(getEditedMechanicAssetId(item))"
                                        class="mechanic-asset-card-link mechanic-asset-card-link--name"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Открыть карточку в справочнике"
                                        @click.native.stop
                                    >
                                        {{ getMechanicAssetSelectedName(item) }}
                                    </router-link>
                                    <span v-else>{{ getMechanicAssetSelectedName(item) }}</span>
                                    <b-badge
                                        v-if="getMechanicAssetSelectedInventoryNumber(item)"
                                        variant="secondary"
                                        class="ml-1"
                                    >
                                        инв. {{ getMechanicAssetSelectedInventoryNumber(item) }}
                                    </b-badge>
                                    <b-badge
                                        v-if="isMechanicAssetUsedMoreThanOnce(item)"
                                        variant="warning"
                                        class="ml-1"
                                    >
                                        Уже используется в заявке
                                    </b-badge>
                                </div>
                                <mechanic-asset-transfer-route
                                    v-if="isMechanicAssetSelectedTransfer(item)"
                                    :from="getMechanicTransferFromText(item)"
                                    :to="getMechanicTransferToText(item)"
                                    :extra="getMechanicTransferExtraText(item)"
                                />
                                <div
                                    v-else-if="getMechanicAssetSelectedDetails(item)"
                                    class="mechanic-asset-details-line"
                                >
                                    <span>{{ getMechanicAssetSelectedDetailsText(item) }}</span>
                                </div>
                                <div v-if="getMechanicAssetSelectedOption(item)" class="mt-1">
                                    <b-badge
                                        v-if="shouldShowMechanicAssetStatus(item) && getMechanicAssetOptionStatusText(getMechanicAssetSelectedOption(item)) && !isMechanicTransferDetails(getMechanicAssetSelectedDetails(item))"
                                        :variant="getMechanicAssetOptionStatusVariant(getMechanicAssetSelectedOption(item))"
                                        class="mr-1"
                                    >
                                        {{ getMechanicAssetOptionStatusText(getMechanicAssetSelectedOption(item)) }}
                                    </b-badge>
                                </div>
                            </div>
                            <span v-else :class="isMechanicSelectionRequired(item) ? 'text-warning' : 'text-muted'">
                                {{ isMechanicSelectionRequired(item) ? 'Ожидает выбора' : 'Не сопоставлено' }}
                            </span>
                        </template>

                        <template #cell(target_user)="{ item }">
                            <div
                                v-if="isEditingList && isMechanicAssetCatalog && isMechanicShipmentMode && mechanicAssetNeedsTargetUser && (!isMechanicRepairMode || isMechanicRepairAcceptedVisible(item))"
                                class="fuel-card-target-user-cell"
                            >
                                <v-select
                                    :value="getEditedItem(item).target_user_id"
                                    :options="getMechanicAssetUserOptions(item)"
                                    label="text"
                                    :reduce="option => option.value"
                                    placeholder="Начните вводить сотрудника..."
                                    :clearable="true"
                                    append-to-body
                                    :calculate-position="positionModalSelectDropdown"
                                    @input="setEditedField(item, 'target_user_id', normalizeSelectId($event))"
                                >
                                    <template #no-options>
                                        Сотрудники не найдены
                                    </template>
                                </v-select>
                                <EmployeeSelectionPreview :user-id="getEditedItem(item).target_user_id" :options="getMechanicAssetUserOptions(item)" />
                            </div>
                            <div
                                v-else-if="isEditingList && fuelCardNeedsRecipient"
                                class="fuel-card-target-user-cell"
                            >
                                <b-form-select
                                    size="sm"
                                    :value="getEditedItem(item).target_user_id"
                                    :options="getFuelCardRecipientOptions(item)"
                                    @change="setEditedFuelCardTargetUser(item, $event)"
                                />
                                <EmployeeSelectionPreview :user-id="getEditedItem(item).target_user_id" :options="getFuelCardRecipientOptions(item)" />
                            </div>
                            <span v-else-if="isMechanicRepairMode && !isMechanicRepairAcceptedVisible(item)" class="text-muted">-</span>
                            <EmployeeLink
                                v-else-if="item.target_user"
                                :user="item.target_user"
                                fallback="Не указан"
                                name-order="last-first"
                            />
                            <EmployeeLink
                                v-else-if="item.target_user_id"
                                :user-id="item.target_user_id"
                                :fallback="`Пользователь #${item.target_user_id}`"
                                name-order="last-first"
                            />
                            <span v-else class="text-muted">-</span>
                        </template>

                        <template #cell(fuel_card_selection)="{ item }">
                            <div v-if="canManageFuelCardSelection && item.id" class="fuel-card-selection-cell">
                                <b-form-select
                                    size="sm"
                                    :value="getFuelCardSelectionValue(item)"
                                    :options="fuelCardSelectOptions"
                                    :disabled="fuelCardOptionsLoading || fuelCardSelectionBusy[item.id]"
                                    @change="selectFuelCardForItem(item, $event)"
                                />
                            </div>
                            <span v-else-if="item.catalog_item_id" class="text-muted">Выбрана</span>
                            <span v-else class="text-warning">Ожидает выбора</span>
                        </template>

                        <template #cell(target_building_process)="{ item }">
                            <div v-if="isMechanicShipmentMode && mechanicAssetNeedsObject" class="fuel-card-target-object-cell">
                                <v-select
                                    :value="getEditedItem(item).target_building_process_id"
                                    :options="mechanicAssetObjectOptions"
                                    label="text"
                                    :reduce="option => option.value"
                                    placeholder="Начните вводить объект..."
                                    :clearable="true"
                                    :loading="mechanicAssetShowsReturnObject && mechanicAssetReturnObjectOptionsLoading"
                                    append-to-body
                                    :calculate-position="positionModalSelectDropdown"
                                    @open="ensureMechanicAssetReturnObjectOptions"
                                    @input="setEditedField(item, 'target_building_process_id', normalizeSelectId($event))"
                                >
                                    <template #no-options>
                                        {{ mechanicAssetReturnObjectOptionsLoading ? 'Загрузка объектов...' : 'Объекты не найдены' }}
                                    </template>
                                </v-select>
                            </div>
                            <div v-else-if="isMechanicShipmentMode && mechanicAssetShowsReturnObject && isMechanicReturnObjectVisible(item)" class="fuel-card-target-object-cell">
                                <v-select
                                    :value="getEditedItem(item).target_building_process_id"
                                    :options="mechanicAssetObjectOptions"
                                    label="text"
                                    :reduce="option => option.value"
                                    placeholder="Начните вводить объект..."
                                    :clearable="true"
                                    :disabled="mechanicAssetShowsReturnObject && mechanicAssetReturnObjectOptionsLoading"
                                    :loading="mechanicAssetShowsReturnObject && mechanicAssetReturnObjectOptionsLoading"
                                    append-to-body
                                    :calculate-position="positionModalSelectDropdown"
                                    @open="ensureMechanicAssetReturnObjectOptions"
                                    @input="setEditedField(item, 'target_building_process_id', normalizeSelectId($event))"
                                >
                                    <template #no-options>
                                        {{ mechanicAssetReturnObjectOptionsLoading ? 'Загрузка объектов...' : 'Объекты не найдены' }}
                                    </template>
                                </v-select>
                            </div>
                            <div v-else-if="canManageFuelCardSelection && item.id" class="fuel-card-target-object-cell">
                                <b-form-select
                                    size="sm"
                                    :value="getFuelCardTargetObjectValue(item)"
                                    :options="fuelCardTargetObjectSelectOptions"
                                    :disabled="fuelCardTargetObjectOptionsLoading || fuelCardTargetObjectBusy[item.id]"
                                    @change="selectFuelCardTargetObjectForItem(item, $event)"
                                />
                            </div>
                            <span v-else-if="item.target_building_process" class="text-muted">
                                {{ item.target_building_process.object_name }}
                            </span>
                            <span v-else-if="item.target_building_process_id" class="text-muted">
                                {{ getMechanicTargetObjectName(item) }}
                            </span>
                            <span v-else-if="isMechanicRepairMode" class="text-muted">-</span>
                            <span v-else class="text-warning">Ожидает выбора</span>
                        </template>

                        <template #cell(target_warehouse)="{ item }">
                            <div v-if="isMechanicShipmentMode && mechanicAssetNeedsWarehouse && (!isMechanicRepairMode || isMechanicRepairAcceptedVisible(item))">
                                <v-select
                                    :value="getMechanicTargetWarehouseValue(item)"
                                    :options="mechanicAssetWarehouseTargetOptions"
                                    label="text"
                                    :reduce="option => option.value"
                                    placeholder="Начните вводить сценарий..."
                                    :clearable="true"
                                    append-to-body
                                    :calculate-position="positionModalSelectDropdown"
                                    @input="setMechanicTargetWarehouse(item, $event)"
                                >
                                    <template #no-options>
                                        Сценарии не найдены
                                    </template>
                                </v-select>
                            </div>
                            <span v-else-if="isMechanicRepairMode && !isMechanicRepairAcceptedVisible(item)" class="text-muted">
                                -
                            </span>
                            <span v-else-if="isMechanicRepairMode && !item.target_warehouse_id" class="text-muted">
                                {{ getMechanicRepairScenarioLabel(item) }}
                            </span>
                            <span v-else-if="item.target_warehouse">
                                {{ item.target_warehouse.name }}
                            </span>
                            <span v-else-if="item.target_warehouse_id" class="text-muted">
                                Склад #{{ item.target_warehouse_id }}
                            </span>
                            <span v-else class="text-muted">-</span>
                        </template>

                        <template #cell(mechanic_meter)="{ item }">
                            <div v-if="isMechanicShipmentMode && mechanicAssetRequiresMeterValue" class="mechanic-meter-cell">
                                <b-form-select
                                    size="sm"
                                    :value="getEditedItem(item).meter_type"
                                    :options="mechanicAssetMeterTypeOptions"
                                    class="mb-1"
                                    @change="setEditedField(item, 'meter_type', $event || null)"
                                />
                                <b-form-input
                                    size="sm"
                                    type="number"
                                    min="0"
                                    step="0.001"
                                    :value="getEditedItem(item).meter_value"
                                    @input="setEditedField(item, 'meter_value', $event)"
                                />
                            </div>
                            <span v-else-if="item.meter_value !== null && item.meter_value !== undefined">
                                {{ item.meter_value }} {{ getMechanicMeterTypeText(item.meter_type) }}
                            </span>
                            <span v-else class="text-muted">-</span>
                        </template>

                        <template #cell(condition)="{ item }">
                            <DiscussionEditor
                                v-if="isMechanicShipmentMode && mechanicAssetRequiresCondition"
                                size="sm"
                                :value="getEditedItem(item).condition"
                                @input="setEditedField(item, 'condition', $event)"
                            />
                            <span v-else-if="item.condition">{{ item.condition }}</span>
                            <span v-else class="text-muted">-</span>
                        </template>

                        <template #cell(rental)="{ item }">
                            <div v-if="isMechanicShipmentMode && mechanicAssetNeedsRental" class="mechanic-rental-cell">
                                <b-form-input
                                    size="sm"
                                    placeholder="Арендатор"
                                    class="mb-1"
                                    :value="getEditedItem(item).rental_counterparty"
                                    @input="setEditedField(item, 'rental_counterparty', $event)"
                                />
                                <b-form-select
                                    size="sm"
                                    :value="getEditedItem(item).rental_operator_user_id"
                                    :options="mechanicAssetUserOptions"
                                    class="mb-1"
                                    @change="setEditedField(item, 'rental_operator_user_id', normalizeSelectId($event))"
                                />
                                <EmployeeSelectionPreview :user-id="getEditedItem(item).rental_operator_user_id" :options="mechanicAssetUserOptions" />
                                <b-form-input
                                    size="sm"
                                    type="date"
                                    :value="formatDateInput(getEditedItem(item).rental_expected_return_at)"
                                    @input="setEditedField(item, 'rental_expected_return_at', $event || null)"
                                />
                            </div>
                            <div v-else-if="item.rental_counterparty || item.rental_operator">
                                <div v-if="item.rental_counterparty">{{ item.rental_counterparty }}</div>
                                <small v-if="item.rental_operator" class="text-muted">
                                    Оператор:
                                    <EmployeeLink
                                        :user="item.rental_operator"
                                        fallback="Не указан"
                                        name-order="last-first"
                                    />
                                </small>
                            </div>
                            <span v-else class="text-muted">-</span>
                        </template>

                        <template #cell(fuel_limits)="{ item }">
                            <div class="fuel-card-limits-cell d-flex align-items-start justify-content-between">
                                <div>
                                    <div v-if="formatFuelLimits(getFuelCardLimitsValue(item))">
                                        {{ formatFuelLimits(getFuelCardLimitsValue(item)) }}
                                    </div>
                                    <span v-else :class="fuelCardLimitsRequired ? 'text-warning' : 'text-muted'">
                                        {{ fuelCardLimitsRequired ? 'Не указаны' : 'Без изменений' }}
                                    </span>
                                </div>
                                <b-button
                                    v-if="canEditFuelCardLimits && (item.id || item._tempId)"
                                    size="sm"
                                    variant="outline-primary"
                                    class="ml-2"
                                    title="Изменить суточные лимиты"
                                    @click="openFuelCardLimitsModal(item)"
                                >
                                    <i class="fas fa-pen"></i>
                                </b-button>
                            </div>
                        </template>

                        <!-- Запрошено -->
                        <template #cell(quantity)="{ item }">
                            <span v-if="isMechanicAssetCatalog && !isMechanicQuantityRequest(item)" class="text-muted">
                                -
                            </span>
                            <div
                                v-else-if="isEditingList && (!isMechanicAssetCatalog || (isMechanicRequestEditMode && isMechanicQuantityRequest(item)))"
                                class="quantity-edit d-flex align-items-center"
                            >
                                <b-input-group size="sm" style="max-width: 140px;">
                                    <b-form-input
                                        :ref="`quantity-${item.id || item._tempId}`"
                                        v-model.number="getEditedItem(item).quantity"
                                        type="number"
                                        min="0"
                                        step="0.001"
                                        placeholder="0"
                                        :state="validateQuantity(item)"
                                        @focus="onQuantityFocus($event, item)"
                                        @blur="onQuantityBlur(item)"
                                        @input="onMaterialFieldChange"
                                    />
                                    <b-input-group-append>
                                        <b-input-group-text class="text-muted">
                                            {{ getEditedItem(item).unit }}
                                        </b-input-group-text>
                                    </b-input-group-append>
                                </b-input-group>
                            </div>
                            <span v-else class="badge badge-secondary quantity-badge">
                                {{ formatNumber(item.quantity) }} {{ item.unit }}
                            </span>
                        </template>

                        <!-- Исполнитель строки (в режиме multi-executor) -->
                        <template #cell(executor)="{ item }">
                            <div class="d-flex align-items-center justify-content-between item-executor-cell">
                                <div class="item-executor-name">
                                    <template v-if="item.executor">
                                        <EmployeeLink
                                            :user="item.executor"
                                            fallback="Не назначен"
                                            name-order="last-first"
                                            :class="{ 'font-weight-bold': isOwnItem(item) }"
                                        />
                                    </template>
                                    <span v-else class="text-danger">
                                        <i class="fas fa-exclamation-triangle"></i> Не назначен
                                    </span>
                                </div>
                                <b-button
                                    v-if="canReassignItemExecutor && !isEditing && !isEditingList"
                                    variant="outline-secondary"
                                    size="sm"
                                    class="ml-2"
                                    v-b-tooltip.hover
                                    title="Переназначить исполнителя на эту строку"
                                    @click="openReassignModal(item)"
                                >
                                    <i class="fas fa-pen"></i>
                                </b-button>
                                <b-button
                                    v-if="canShowSelfAssign(item) && !isEditing && !isEditingList"
                                    variant="outline-primary"
                                    size="sm"
                                    class="ml-2"
                                    v-b-tooltip.hover
                                    :title="item.executor ? 'Перехватить строку себе' : 'Взять строку себе'"
                                    @click="confirmSelfAssign(item)"
                                >
                                    <i class="fas fa-hand-paper"></i>
                                </b-button>
                                <b-button
                                    v-if="canShowSelfRelease(item) && !isEditing && !isEditingList"
                                    variant="outline-warning"
                                    size="sm"
                                    class="ml-2"
                                    v-b-tooltip.hover
                                    title="Снять назначение со своей строки"
                                    @click="confirmSelfRelease(item)"
                                >
                                    <i class="fas fa-undo"></i>
                                </b-button>
                            </div>
                        </template>

                        <!-- Отгружено -->
                        <template #cell(shipped_quantity)="{ item }">
                            <div
                                v-if="shouldShowMechanicFulfillmentAutoShipment(item)"
                            >
                                <span :class="getQuantityClass(getMechanicFulfillmentShippedCount(item), getRequestedQuantity(item))">
                                    {{ formatNumber(getMechanicFulfillmentShippedCount(item)) }}
                                </span>
                                <small class="text-muted">
                                    / {{ formatNumber(getRequestedQuantity(item)) }} {{ getEditedItem(item).unit || item.unit }}
                                </small>
                                <small class="d-block text-muted">
                                    Автоматически по строкам отгрузки
                                </small>
                                <DiscussionEditor
                                    v-if="isOperationCommentOpen(item, 'shipping')"
                                    :value="getEditedItem(item).shipping_comment"
                                    placeholder="Комментарий к отгрузке"
                                    maxlength="1000"
                                    size="sm"
                                    class="mt-1 operation-comment-input"
                                    @input="setEditedField(item, 'shipping_comment', $event)"
                                />
                                <b-button
                                    v-else
                                    variant="link"
                                    size="sm"
                                    class="operation-comment-toggle mt-1 p-0"
                                    @click="toggleOperationComment(item, 'shipping')"
                                >
                                    <i class="fas fa-comment-medical"></i> Комментарий
                                </b-button>
                            </div>
                            <div
                                v-else-if="isMechanicAssetCatalog && !isMechanicQuantityRequest(item)"
                                class="mechanic-inventory-flag-cell"
                            >
                                <b-form-checkbox
                                    v-if="isMechanicShipmentMode"
                                    :checked="isMechanicInventoryFlagChecked(item, 'shipped_quantity')"
                                    switch
                                    @change="setMechanicInventoryFlag(item, 'shipped_quantity', $event)"
                                >
                                    {{ isMechanicRepairMode ? 'Принято' : 'Отгружено' }}
                                </b-form-checkbox>
                                <b-badge
                                    v-else
                                    :variant="isMechanicInventoryFlagChecked(item, 'shipped_quantity') ? 'success' : 'secondary'"
                                >
                                    {{ isMechanicInventoryFlagChecked(item, 'shipped_quantity') ? 'Да' : 'Нет' }}
                                </b-badge>
                                <DiscussionEditor
                                    v-if="isMechanicShipmentMode && isOperationCommentOpen(item, 'shipping')"
                                    :value="getEditedItem(item).shipping_comment"
                                    placeholder="Комментарий к отгрузке"
                                    maxlength="1000"
                                    size="sm"
                                    class="mt-1 operation-comment-input"
                                    @input="setEditedField(item, 'shipping_comment', $event)"
                                />
                                <b-button
                                    v-else-if="isMechanicShipmentMode"
                                    variant="link"
                                    size="sm"
                                    class="operation-comment-toggle mt-1 p-0"
                                    @click="toggleOperationComment(item, 'shipping')"
                                >
                                    <i class="fas fa-comment-medical"></i> Комментарий
                                </b-button>
                                <small v-if="!isMechanicShipmentMode && item.shipping_comment" class="d-block text-muted font-italic mt-1 operation-comment-text">
                                    <i class="fas fa-comment"></i>
                                    <DiscussionText :value="item.shipping_comment" :author-id="item.shipped_by" />
                                </small>
                            </div>
                            <div v-else-if="isMechanicAssetCatalog && isMechanicShipmentMode && (isMechanicQuantityRequest(item) || shouldEditMechanicFulfillmentShippedQuantity(item))">
                                <b-input-group size="sm" style="max-width: 140px;">
                                    <b-form-input
                                        :value="getMechanicShipmentQuantityEditValue(item)"
                                        type="number"
                                        min="0"
                                        step="0.001"
                                        :state="validateShippedQuantity(item)"
                                        @input="setMechanicShipmentQuantityFromColumn(item, $event)"
                                    />
                                    <b-input-group-append>
                                        <b-input-group-text class="text-muted">
                                            {{ getMechanicShipmentQuantityUnit(item) }}
                                        </b-input-group-text>
                                    </b-input-group-append>
                                </b-input-group>
                                <DiscussionEditor
                                    v-if="isOperationCommentOpen(item, 'shipping')"
                                    :value="getEditedItem(item).shipping_comment"
                                    placeholder="Комментарий к отгрузке"
                                    maxlength="1000"
                                    size="sm"
                                    class="mt-1 operation-comment-input"
                                    @input="setEditedField(item, 'shipping_comment', $event)"
                                />
                                <b-button
                                    v-else
                                    variant="link"
                                    size="sm"
                                    class="operation-comment-toggle mt-1 p-0"
                                    @click="toggleOperationComment(item, 'shipping')"
                                >
                                    <i class="fas fa-comment-medical"></i> Комментарий
                                </b-button>
                            </div>
                            <div v-else-if="isEditing && canShip">
                                <b-form-input
                                    v-model.number="editedItems[item.id].shipped_quantity"
                                    type="number"
                                    min="0"
                                    step="0.001"
                                    size="sm"
                                    class="shipped-input"
                                    :disabled="!canShipItem(item)"
                                    v-b-tooltip.hover
                                    :title="!canShipItem(item) ? 'Вы не назначены исполнителем на эту строку' : ''"
                                    @input="onShippedChange(item)"
                                />
                                <DiscussionEditor
                                    v-if="isOperationCommentOpen(item, 'shipping')"
                                    v-model="editedItems[item.id].shipping_comment"
                                    placeholder="Комментарий к отгрузке"
                                    maxlength="1000"
                                    size="sm"
                                    class="mt-1 operation-comment-input"
                                    :disabled="!canShipItem(item)"
                                />
                                <b-button
                                    v-else
                                    variant="link"
                                    size="sm"
                                    class="operation-comment-toggle mt-1 p-0"
                                    :disabled="!canShipItem(item)"
                                    @click="toggleOperationComment(item, 'shipping')"
                                >
                                    <i class="fas fa-comment-medical"></i> Комментарий
                                </b-button>
                            </div>
                            <div v-else>
                                <span :class="getQuantityClass(item.shipped_quantity, item.quantity)">
                                    {{ formatNumber(item.shipped_quantity || 0) }}
                                </span>
                                <small v-if="item.shipping_comment" class="d-block text-muted font-italic mt-1 operation-comment-text">
                                    <i class="fas fa-comment"></i>
                                    <DiscussionText :value="item.shipping_comment" :author-id="item.shipped_by" />
                                </small>
                            </div>
                        </template>

                        <!-- Принято -->
                        <template #cell(accepted_quantity)="{ item }">
                            <div
                                v-if="isMechanicAssetCatalog && !isMechanicQuantityRequest(item)"
                                class="mechanic-inventory-flag-cell"
                            >
                                <b-form-checkbox
                                    v-if="isMechanicAcceptanceMode || isMechanicRepairCompletionMode"
                                    :checked="isMechanicInventoryAcceptedChecked(item)"
                                    switch
                                    @change="setMechanicInventoryAcceptedFlag(item, $event)"
                                >
                                    {{ mechanicAcceptedFlagLabel }}
                                </b-form-checkbox>
                                <b-badge
                                    v-else
                                    :variant="isMechanicInventoryFlagChecked(item, 'accepted_quantity') ? 'success' : 'secondary'"
                                >
                                    {{ isMechanicInventoryFlagChecked(item, 'accepted_quantity') ? 'Да' : 'Нет' }}
                                </b-badge>
                            </div>
                            <span
                                v-else-if="isMechanicAssetCatalog && isMechanicShipmentMode && isMechanicQuantityRequest(item) && !isMechanicRepairCompletionMode"
                                :class="getQuantityClass(getEditedItem(item).accepted_quantity || 0, getEditedItem(item).shipped_quantity || item.shipped_quantity)"
                            >
                                {{ formatNumber(getEditedItem(item).accepted_quantity || 0) }}
                            </span>
                            <div v-else-if="isMechanicAssetCatalog && isMechanicRepairCompletionMode && isMechanicQuantityRequest(item)">
                                <b-input-group size="sm" style="max-width: 140px;">
                                    <b-form-input
                                        :value="getMechanicRepairAcceptedInputValue(item)"
                                        type="number"
                                        min="0"
                                        :max="getMechanicRepairAcceptedInputMax(item)"
                                        step="1"
                                        @input="setMechanicRepairAcceptedQuantity(item, normalizeWholeNumberInput($event))"
                                    />
                                    <b-input-group-append>
                                        <b-input-group-text class="text-muted">
                                            {{ getEditedItem(item).unit || item.unit }}
                                        </b-input-group-text>
                                    </b-input-group-append>
                                </b-input-group>
                            </div>
                            <div v-else-if="isEditing && canAccept">
                                <b-form-input
                                    v-model.number="editedItems[item.id].accepted_quantity"
                                    type="number"
                                    min="0"
                                    :max="editedItems[item.id].shipped_quantity || item.shipped_quantity"
                                    step="0.001"
                                    size="sm"
                                    class="accepted-input"
                                />
                                <DiscussionEditor
                                    v-if="isOperationCommentOpen(item, 'acceptance')"
                                    v-model="editedItems[item.id].acceptance_comment"
                                    placeholder="Комментарий к приемке"
                                    maxlength="1000"
                                    size="sm"
                                    class="mt-1 operation-comment-input"
                                />
                                <b-button
                                    v-else
                                    variant="link"
                                    size="sm"
                                    class="operation-comment-toggle mt-1 p-0"
                                    @click="toggleOperationComment(item, 'acceptance')"
                                >
                                    <i class="fas fa-comment-medical"></i> Комментарий
                                </b-button>
                            </div>
                            <div v-else>
                                <span :class="getQuantityClass(item.accepted_quantity, item.shipped_quantity)">
                                    {{ formatNumber(item.accepted_quantity || 0) }}
                                </span>
                                <small v-if="item.acceptance_comment" class="d-block text-muted font-italic mt-1 operation-comment-text">
                                    <i class="fas fa-comment"></i>
                                    <DiscussionText :value="item.acceptance_comment" :author-id="item.accepted_by" />
                                </small>
                            </div>
                        </template>

                        <!-- % отгрузки -->
                        <template #cell(shipped_percent)="{ item }">
                            <span>{{ getShippedPercent(item) }}%</span>
                        </template>

                        <!-- % приемки -->
                        <template #cell(accepted_percent)="{ item }">
                            <span>{{ getAcceptedPercent(item) }}%</span>
                        </template>

                        <!-- Статус -->
                        <template #cell(status)="{ item }">
                            <b-badge :variant="getStatusVariant(item)" pill class="px-3 py-2 status-badge">
                                {{ getStatusText(item) }}
                            </b-badge>
                        </template>

                        <!-- Действия (только в режиме редактирования списка) -->
                        <template #cell(actions)="{ item }">
                            <div v-if="isEditingList" class="d-flex align-items-center justify-content-center">
                                <b-button
                                    variant="outline-danger"
                                    size="sm"
                                    @click="markForDeletion(item)"
                                    v-b-tooltip.hover
                                    :title="isMechanicAssetCatalog ? 'Удалить позицию' : 'Удалить материал'"
                                >
                                    <i class="fas fa-times"></i>
                                </b-button>
                            </div>
                        </template>

                        <!-- Заголовок колонки быстрого заполнения -->
                        <template #head(actions_quick_fill)>
                            <div class="text-center">
                                <b-button
                                    v-if="showQuickFillControls"
                                    variant="outline-primary"
                                    size="sm"
                                    @click="fillAllQuantities"
                                    v-b-tooltip.hover
                                    :title="quickFillAllTitle"
                                    class="quick-fill-all-btn"
                                >
                                    <i class="fas fa-check-double"></i>
                                </b-button>
                            </div>
                        </template>

                        <!-- Ячейки быстрого заполнения -->
                        <template #cell(actions_quick_fill)="{ item }">
                            <div v-if="showQuickFillControls" class="text-center">
                                <b-button
                                    variant="outline-success"
                                    size="sm"
                                    @click="fillRowQuantity(item)"
                                    v-b-tooltip.hover.right
                                    :title="getQuickFillRowTitle(item)"
                                    class="quick-fill-row-btn"
                                    :disabled="!canQuickFillItem(item)"
                                >
                                    <i class="fas fa-check"></i>
                                </b-button>
                            </div>
                        </template>
                    </b-table>
                </div>

              <div v-if="hasHorizontalScroll && !isEditingList" class="scroll-hint scroll-hint--touch">
                <i class="fas fa-arrows-alt-h mr-1"></i>
                Таблица шире экрана — проведите пальцем влево/вправо, чтобы увидеть все колонки.
              </div>

              <div v-if="hasHorizontalScroll && !isEditingList" class="scroll-hint scroll-hint--mouse">
                <i class="fas fa-mouse-pointer mr-1"></i>
                Таблица шире окна — зажмите левую кнопку мыши и потяните влево/вправо.
              </div>

                <!-- Информация о последних изменениях -->
                <div v-if="materials.length > 0 && !isEditingList" class="alert alert-info mb-0 rounded-0 border-left-0 border-right-0 border-bottom-0">
                    <small>
                        <i class="fas fa-info-circle mr-1"></i>
                        <strong>Подсказка:</strong>
                        <span v-if="isMultiExecutor && canShip && !canAccept">
                            Режим «Несколько исполнителей»: вы можете редактировать отгрузку только по строкам, на которых назначены исполнителем.
                        </span>
                        <span v-else-if="canShip && !canAccept">
                            Вы можете редактировать отгруженные количества как исполнитель заявки.
                        </span>
                        <span v-else-if="!canShip && canAccept">
                            Вы можете редактировать принятые количества как инициатор заявки.
                        </span>
                        <span v-else-if="canShip && canAccept">
                            Вы можете редактировать как отгруженные, так и принятые количества.
                        </span>
                        <span v-else-if="canEditListEffective">
                            <template v-if="isMechanicAssetCatalog && canManageMechanicAssetSelection">
                                Вы можете заполнить отгрузку по запрошенным позициям.
                            </template>
                            <template v-else-if="isMechanicAssetCatalog">
                                Вы можете редактировать только запрошенные позиции.
                            </template>
                            <template v-else>
                                Вы можете редактировать список {{ itemListName }}.
                            </template>
                            После этого нажмите «Сохранить изменения» в нижней панели заявки.
                        </span>
                        <span v-else>
                            У вас нет прав на редактирование материалов.
                        </span>
                    </small>
                </div>
                
                <!-- Сообщение в режиме редактирования списка -->
                <div v-if="isEditingList" class="alert alert-warning mb-0 rounded-0 border-left-0 border-right-0 border-bottom-0">
                    <small>
                        <i class="fas fa-exclamation-triangle mr-1"></i>
                        <template v-if="isMechanicShipmentMode">
                            <strong>Режим отгрузки:</strong>
                            Запрошенные позиции не меняются. Заполните, что отгружаем, МОЛ, объект назначения и отметки отгрузки/приемки.
                        </template>
                        <template v-else-if="isMechanicRequestEditMode">
                            <strong>Режим редактирования запрошенного:</strong>
                            Можно менять только исходные запрошенные позиции. Отгрузка, сопоставление и приемка недоступны согласующему.
                        </template>
                        <template v-else>
                            <strong>Режим редактирования:</strong>
                            Изменения сохранятся по кнопке «Сохранить изменения» в нижней панели заявки.
                            Вы можете добавлять, редактировать и удалять {{ itemListName }}.
                        </template>
                    </small>
                </div>
            </b-card-body>
        </b-card>

        <!-- Модальное окно добавления материала вручную -->
        <b-modal content-class="argus-glass-dialog" ignore-enforce-focus-selector=".request-employee-panel"
            v-model="showAddCustomMaterial"
            :title="`Добавить вручную: ${catalogTitle}`"
            @ok="addCustomMaterial"
            @hidden="resetCustomMaterialForm"
            ok-title="Добавить"
            cancel-title="Отмена"
            scrollable
        >
            <b-form @submit.prevent="addCustomMaterial">
                <b-form-group :label="`${isEmployeeCatalog ? 'ФИО сотрудника' : (isMechanicAssetCatalog ? 'Название позиции' : 'Название материала')} *`" label-for="custom-material-name">
                    <b-form-input
                        id="custom-material-name"
                        v-model="customMaterial.name"
                        :placeholder="isEmployeeCatalog ? 'Например: Иванов Иван' : (isMechanicAssetCatalog ? 'Например: масло моторное или сверло 12 мм' : 'Например: Цемент М500')"
                        required
                    />
                </b-form-group>

                <b-row v-if="!isEmployeeCatalog">
                    <b-col md="6">
                        <b-form-group label="Количество *" label-for="custom-material-quantity">
                            <b-form-input
                                id="custom-material-quantity"
                                v-model.number="customMaterial.quantity"
                                type="number"
                                min="0"
                                step="0.001"
                                placeholder="100"
                                required
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="6">
                        <b-form-group label="Единица измерения *" label-for="custom-material-unit">
                            <b-form-input
                                id="custom-material-unit"
                                v-model="customMaterial.unit_of_measurement"
                                placeholder="кг, м, шт и т.д."
                                required
                            />
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-form-group label="Примечания (необязательно)" label-for="custom-material-notes">
                    <AppTextarea
                        id="custom-material-notes"
                        v-model="customMaterial.notes"
                        placeholder="Примечания к материалу"
                        maxlength="1000"
                    />
                </b-form-group>

                <b-alert variant="info" show>
                    <i class="fas fa-info-circle"></i>
                    <strong>Внимание:</strong> Позиция добавляется только в эту заявку.
                </b-alert>
            </b-form>
        </b-modal>

        <!-- Модальное окно выбора материалов -->
        <b-modal content-class="argus-glass-dialog" ignore-enforce-focus-selector=".request-employee-panel"
            v-if="!isMechanicAssetCatalog"
            id="material-selector-modal"
            v-model="showMaterialSelector"
            :title="`Выбор: ${catalogLabel}`"
            size="lg"
            scrollable
            @ok="addSelectedMaterials"
            @cancel="clearMaterialSelection"
            ok-title="Добавить выбранные"
            cancel-title="Отмена"
        >
            <div v-if="materialsLoading" class="text-center text-muted">
                <i class="fas fa-spinner fa-spin"></i> Загрузка справочника...
            </div>
            <div v-else-if="availableMaterials.length">
                <b-form-input
                    v-model="materialSearch"
                    :placeholder="`Поиск: ${catalogLabel.toLowerCase()}...`"
                    class="mb-3"
                />
                
                <div class="border rounded" style="max-height: 35vh; overflow-y: auto;">
                    <div v-for="material in filteredAvailableMaterials" :key="material.id" 
                         class="d-flex justify-content-between align-items-center p-2 border-bottom">
                        <div>
                            <div><strong>{{ material.name }}</strong></div>
                            <small v-if="isMechanicAssetCatalog && material.catalog_name" class="d-block text-muted">
                                Группа: {{ material.catalog_name }}
                            </small>
                            <small v-if="isMechanicAssetCatalog && material.inventory_number" class="d-block text-muted">
                                Инв.: {{ material.inventory_number }}
                            </small>
                            <small v-if="isMechanicAssetCatalog && material.accounting_type === 'quantity'" class="d-block text-muted">
                                Доступно: {{ formatNumber(material.available_quantity || 0) }} {{ material.unit_of_measurement }}
                            </small>
                            <small v-if="isMechanicAssetCatalog && material.assignment_details" class="d-block text-muted">
                                {{ material.assignment_details }}
                            </small>
                            <small v-if="!isEmployeeCatalog && !isMechanicAssetCatalog" class="text-muted">{{ material.unit_of_measurement }}</small>
                        </div>
                        <b-form-checkbox
                            v-model="material.selected"
                            :value="true"
                        />
                    </div>
                </div>
            </div>
            <div v-else class="text-center text-muted">
                <i class="fas fa-exclamation-triangle"></i> Позиции не найдены
            </div>
        </b-modal>

        <mechanic-asset-picker-modal
            v-if="isMechanicAssetCatalog"
            v-model="showMechanicMaterialSelector"
            :title="`Выбор: ${catalogLabel}`"
            :request-type-id="requestTypeId"
            :request-id="requestId"
            :context="getItemCatalogRequestContext()"
            :used-ids="mechanicMaterialSelectorUsedIds"
            multiple
            :clearable="false"
            @confirm="addMechanicCatalogPickerMaterials"
        />

        <mechanic-asset-picker-modal
            v-if="isMechanicAssetCatalog"
            v-model="mechanicAssetPicker.show"
            :title="mechanicAssetPickerTitle"
            :row-title="mechanicAssetPickerRowTitle"
            :request-type-id="requestTypeId"
            :request-id="requestId"
            :request-item-id="mechanicAssetPickerRequestItemId"
            :requested-quantity="mechanicAssetPickerRequestedQuantity"
            :requested-unit="mechanicAssetPickerRequestedUnit"
            :receipt-enabled="mechanicAssetPickerReceiptEnabled"
            :context="getItemCatalogRequestContext()"
            :selected-id="mechanicAssetPickerSelectedId"
            :excluded-ids="mechanicAssetPickerExcludedIds"
            :used-ids="mechanicAssetPickerUsedIds"
            @select="selectMechanicAssetFromPicker"
            @clear="clearMechanicAssetPickerSelection"
        />

        <b-modal content-class="argus-glass-dialog" ignore-enforce-focus-selector=".request-employee-panel"
            v-model="showFuelCardLimitsModal"
            title="Суточные лимиты топливной карты"
            ok-title="Сохранить"
            cancel-title="Отмена"
            :ok-disabled="!canSaveFuelCardLimits"
            :busy="fuelCardLimitsBusy"
            @ok.prevent="saveFuelCardLimits"
            @hidden="resetFuelCardLimitsModal"
        >
            <div v-if="fuelCardLimitsTarget" class="mb-3">
                <div class="text-muted small mb-1">Топливная карта</div>
                <div class="font-weight-bold">{{ fuelCardLimitsTarget.item_name }}</div>
            </div>

            <div
                v-for="(limit, index) in fuelCardLimitsDraft"
                :key="`fuel-card-limit-draft-${index}`"
                class="d-flex align-items-center mb-2"
            >
                <b-form-select
                    v-model.number="limit.fuel_type_id"
                    :options="fuelLimitTypeOptions"
                    size="sm"
                    class="mr-2"
                    :disabled="fuelCardLimitsBusy"
                    :data-fuel-limit-modal-id="_uid"
                    data-fuel-limit-draft-type
                    :data-fuel-limit-draft-index="index"
                    @change="setFuelLimitType(limit, $event)"
                />
                <b-form-input
                    v-model.number="limit.value"
                    type="number"
                    min="0"
                    step="0.001"
                    size="sm"
                    placeholder="Лимит в сутки"
                    class="mr-2"
                    :disabled="fuelCardLimitsBusy"
                    :data-fuel-limit-modal-id="_uid"
                    data-fuel-limit-draft-value
                    :data-fuel-limit-draft-index="index"
                    @input="setFuelLimitValue(limit, $event)"
                    @change="setFuelLimitValue(limit, $event)"
                />
                <b-button
                    size="sm"
                    variant="outline-danger"
                    title="Удалить лимит"
                    :disabled="fuelCardLimitsBusy"
                    @click="removeFuelCardLimitDraftRow(index)"
                >
                    <i class="fas fa-times"></i>
                </b-button>
            </div>

            <b-button
                size="sm"
                variant="outline-primary"
                :disabled="fuelCardLimitsBusy"
                @click="addFuelCardLimitDraftRow"
            >
                <i class="fas fa-plus mr-1"></i>
                Добавить лимит
            </b-button>

            <small class="text-muted d-block mt-2">
                Значение указывается в сутки, в единицах выбранного вида топлива.
            </small>
            <b-alert v-if="fuelCardLimitsRequired && !sanitizedFuelCardLimitsDraft.length" show variant="warning" class="mt-3 mb-0">
                Укажите хотя бы один вид топлива и суточный лимит.
            </b-alert>
            <b-alert v-else-if="!fuelCardLimitsRequired && !sanitizedFuelCardLimitsDraft.length" show variant="info" class="mt-3 mb-0">
                Если лимит не указать, действующие лимиты карты не изменятся.
            </b-alert>
        </b-modal>

        <!-- Модальное окно массового переназначения исполнителя строк -->
        <b-modal content-class="argus-glass-dialog" ignore-enforce-focus-selector=".request-employee-panel"
            v-model="showBulkReassignModal"
            title="Переназначить строки"
            ok-title="Переназначить"
            cancel-title="Отмена"
            :ok-disabled="!canConfirmBulkReassign"
            :busy="reassigning"
            @ok.prevent="confirmBulkReassign"
            @hidden="resetBulkReassignForm"
        >
            <b-form-group label="Текущий исполнитель" label-class="font-weight-bold">
                <v-select
                    v-model="bulkReassignFromExecutorId"
                    :options="bulkReassignSourceOptions"
                    label="text"
                    :reduce="option => option.value"
                    :disabled="reassigning"
                    :clearable="false"
                    append-to-body
                    :calculate-position="positionModalSelectDropdown"
                />
                <EmployeeSelectionPreview :user-id="bulkReassignFromExecutorId" :options="bulkReassignSourceOptions" />
            </b-form-group>

            <b-form-group label="Новый исполнитель" label-class="font-weight-bold">
                <v-select
                    v-model="bulkReassignExecutorId"
                    :options="bulkReassignExecutorOptions"
                    label="text"
                    :reduce="option => option.value"
                    :disabled="reassigning"
                    placeholder="Начните вводить имя исполнителя..."
                    :clearable="true"
                    append-to-body
                    :calculate-position="positionModalSelectDropdown"
                >
                    <template #no-options>
                        Исполнители не найдены
                    </template>
                </v-select>
                <EmployeeSelectionPreview :user-id="bulkReassignExecutorId" :options="bulkReassignExecutorOptions" />
            </b-form-group>

            <div class="d-flex align-items-center justify-content-between mb-2">
                <div class="font-weight-bold">Строки</div>
                <div>
                    <b-button
                        variant="link"
                        size="sm"
                        class="p-0 mr-3"
                        :disabled="!bulkReassignCandidateItems.length || reassigning"
                        @click="selectOpenBulkReassignItems"
                    >
                        Незакрытые
                    </b-button>
                    <b-button
                        variant="link"
                        size="sm"
                        class="p-0"
                        :disabled="!bulkReassignCandidateItems.length || reassigning"
                        @click="selectAllBulkReassignItems"
                    >
                        Все
                    </b-button>
                </div>
            </div>

            <div class="bulk-reassign-list border rounded p-2 mb-2">
                <b-form-checkbox-group
                    v-model="bulkReassignSelectedIds"
                    :options="bulkReassignItemOptions"
                    stacked
                    :disabled="reassigning"
                />
                <div v-if="!bulkReassignItemOptions.length" class="text-muted small">
                    У выбранного исполнителя нет строк для переназначения.
                </div>
            </div>

            <b-alert
                v-if="bulkReassignSelectedWithShipmentCount > 0"
                show
                variant="warning"
                class="mb-0"
            >
                В выбранных строках есть отгрузка: {{ bulkReassignSelectedWithShipmentCount }}.
            </b-alert>
        </b-modal>

        <!-- Модальное окно переназначения исполнителя на строку -->
        <b-modal content-class="argus-glass-dialog" ignore-enforce-focus-selector=".request-employee-panel"
            v-model="showReassignModal"
            title="Переназначить исполнителя"
            ok-title="Переназначить"
            cancel-title="Отмена"
            :ok-disabled="!canConfirmReassign"
            :busy="reassigning"
            @ok.prevent="confirmReassign"
            @hidden="resetReassignForm"
        >
            <div v-if="reassignTarget" class="mb-3">
                <div class="text-muted small mb-1">Материал</div>
                <div class="font-weight-bold">{{ reassignTarget.item_name }}</div>
                <div class="small text-muted">
                    {{ formatNumber(reassignTarget.quantity) }} {{ reassignTarget.unit }}
                </div>
            </div>

            <div v-if="reassignTarget && reassignTarget.executor" class="mb-3">
                <div class="text-muted small mb-1">Текущий исполнитель</div>
                <div>
                    <i class="fas fa-user mr-1"></i>
                    <EmployeeLink :user="reassignTarget.executor" compact name-order="last-first" />
                </div>
            </div>

            <b-form-group label="Новый исполнитель" label-class="font-weight-bold">
                <v-select
                    v-model="reassignExecutorId"
                    :options="reassignExecutorOptions"
                    label="text"
                    :reduce="option => option.value"
                    :disabled="reassigning"
                    placeholder="Начните вводить имя исполнителя..."
                    :clearable="true"
                    append-to-body
                    :calculate-position="positionModalSelectDropdown"
                >
                    <template #no-options>
                        Исполнители не найдены
                    </template>
                </v-select>
                <EmployeeSelectionPreview :user-id="reassignExecutorId" :options="reassignExecutorOptions" />
            </b-form-group>

            <b-alert v-if="reassignTarget && reassignExecutorId === currentExecutorIdForTarget" show variant="warning" class="mb-0">
                <small>Это тот же исполнитель, что и сейчас. Выберите другого.</small>
            </b-alert>
        </b-modal>
    </div>
</template>

<script>
import EmployeeSelectionPreview from '@/components/EmployeeSelectionPreview.vue';
import RequestItemPpe from '@/components/RequestItemPpe.vue';
import RequestPpePlanner from '@/components/RequestPpePlanner.vue';
import MaterialsList from '@/components/MaterialsList/MaterialsList.vue';
import EmployeeLink from '@/components/EmployeeLink.vue';
import MechanicAssetPickerModal from './MechanicAssetPickerModal.vue';
import MechanicAssetTransferRoute from './MechanicAssetTransferRoute.vue';
import { positionVueSelectDropdown } from '@/utils/vueSelectPositioning';
import linkifyText from '@/utils/Urlify';
import api from '@/utils/api';
import {
  buildFuelTypeById,
  buildFuelTypeOptions,
  cloneFuelLimits as cloneFuelLimitsValue,
  formatFuelLimits as formatFuelLimitsValue,
  sanitizeFuelLimits as sanitizeFuelLimitsValue,
  setFuelLimitType as setFuelLimitTypeValue,
  setFuelLimitValue as setFuelLimitAmountValue,
  syncFuelLimitControls
} from '@/utils/fuelLimits';

const MECHANIC_REPAIR_SCENARIO_SELF_ON_OBJECT = 'self_on_object';
const MECHANIC_REPAIR_SCENARIO_SPECIALIST_ON_OBJECT = 'specialist_on_object';

export default {
  name: 'MaterialsTable',
  components: {
        EmployeeSelectionPreview,
    RequestItemPpe,
    RequestPpePlanner,
    EmployeeLink,
    MechanicAssetPickerModal,
    MaterialsList,
    MechanicAssetTransferRoute
  },
  props: {
    fulfillmentControlled: { type: Boolean, default: false },
    materials: {
      type: Array,
      required: true,
      default: () => []
    },
    requestId: {
      type: [Number, String],
      required: true
    },
    requestTypeId: {
      type: [Number, String],
      default: null
    },
    requestBuildingProcessId: {
      type: [Number, String],
      default: null
    },
    requestBuildingObjectName: {
      type: String,
      default: ''
    },
    catalogLabel: {
      type: String,
      default: 'Материалы'
    },
    catalogType: {
      type: String,
      default: 'materials'
    },
    allowCustomCatalogItems: {
      type: Boolean,
      default: true
    },
    // Права доступа
    canShip: {
      type: Boolean,
      default: false
    },
    canAccept: {
      type: Boolean,
      default: false
    },
    // Статус заявки
    requestStatus: {
      type: String,
      default: ''
    },
    // Может ли редактировать список материалов (инициатор + draft)
    canEditList: {
      type: Boolean,
      default: false
    },
    // Multi-executor режим (несколько исполнителей по строкам)
    isMultiExecutor: {
      type: Boolean,
      default: false
    },
    executors: {
      type: Array,
      default: () => []
    },
    canReassignItemExecutor: {
      type: Boolean,
      default: false
    },
    canSelfAssignItemExecutor: {
      type: Boolean,
      default: false
    },
    canSelfReleaseItemExecutor: {
      type: Boolean,
      default: false
    },
    canSelfAssignBulk: {
      type: Boolean,
      default: false
    },
    canManageFuelCardSelection: {
      type: Boolean,
      default: false
    },
    canManageFuelCardLimits: {
      type: Boolean,
      default: false
    },
    canManageMechanicAssetSelection: {
      type: Boolean,
      default: false
    },
    savingRequest: {
      type: Boolean,
      default: false
    },
    requestSaveError: {type: String, default: ''},
    hasRequestChanges: {type: Boolean, default: false},
    savedMaterialsRevision: {type: Number, default: 0},
    fuelCardOperation: {
      type: String,
      default: null
    },
    mechanicAssetAction: {
      type: Object,
      default: () => ({})
    },
    mechanicAssetTableMode: {
      type: String,
      default: 'movement'
    },
    selfAssignPreviewEnabled: {
      type: Boolean,
      default: false
    },
    adminGodModeEnabled: {
      type: Boolean,
      default: false
    },
    availableExecutors: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      hasHorizontalScroll: false,
      enableHorizontalDrag: false,
      // Режим редактирования отгрузки/приемки
      isEditing: false,
      editedItems: {},
      originalItems: {},
      expandedOperationComments: {},
      saving: false,

      // Режим редактирования списка материалов
      isEditingList: false,
      ppePlannerActive: false,
      ppeManagedItems: [],
      editingListStarting: false,
      editingListPrepared: false,
      localMaterials: [],
      editedListItems: {},
      materialSearch: '',
      showAddCustomMaterial: false,
      showMaterialSelector: false,
      showMechanicMaterialSelector: false,
      customMaterial: {
        name: '',
        quantity: 0,
        unit_of_measurement: '',
        notes: ''
      },
      availableMaterials: [],
      loadedCatalogContext: null,
      materialsLoading: false,
      materialsLoadPromise: null,
      fuelCardOptions: [],
      fuelCardOptionsLoading: false,
      fuelCardSelectionBusy: {},
      fuelCardTargetObjectOptions: [],
      fuelCardTargetObjectOptionsLoading: false,
      fuelCardTargetObjectBusy: {},
      mechanicAssetReturnObjectOptions: [],
      mechanicAssetReturnObjectOptionsLoading: false,
      mechanicAssetReturnObjectOptionsPromise: null,
      mechanicAssetPicker: {
        show: false,
        item: null,
        fulfillmentIndex: null
      },
      showFuelCardLimitsModal: false,
      fuelCardLimitsTarget: null,
      fuelCardLimitsDraft: [],
      fuelCardLimitsBusy: false,

      // === Multi-executor: переназначение исполнителя на строку ===
      showReassignModal: false,
      reassignTarget: null,
      reassignExecutorId: null,
      showBulkReassignModal: false,
      bulkReassignFromExecutorId: null,
      bulkReassignExecutorId: null,
      bulkReassignSelectedIds: [],
      reassigning: false
    };
  },
  computed: {
    canEdit() {
      if (this.isEmployeeCatalog || this.isFuelCardCatalog) {
        return false;
      }

      // Если заявка в финальном статусе - редактирование запрещено
      const finalStatuses = ['completed', 'rejected', 'cancelled'];
      if (finalStatuses.includes(this.requestStatus)) {
        return false;
      }

      if (this.isMechanicAssetCatalog) {
        return this.canAccept && !this.canManageMechanicAssetSelection;
      }

      return this.canShip || this.canAccept;
    },
    canEditListEffective() {
      return this.canEditList ||
          (this.isMechanicAssetCatalog && this.canManageMechanicAssetSelection);
    },
    isMechanicShipmentMode() {
      return this.isMechanicAssetCatalog &&
          this.isEditingList &&
          this.canManageMechanicAssetSelection;
    },
    isMechanicRequestEditMode() {
      return this.isMechanicAssetCatalog &&
          this.isEditingList &&
          !this.canManageMechanicAssetSelection &&
          this.canEditList;
    },
    isMechanicAcceptanceMode() {
      return this.isMechanicAssetCatalog &&
          this.isEditing &&
          this.canAccept &&
          !this.canManageMechanicAssetSelection;
    },
    isMechanicRepairCompletionMode() {
      return this.isMechanicRepairMode && this.isMechanicShipmentMode;
    },
    requestBuildingProcessIdValue() {
      return this.normalizeSelectId(this.requestBuildingProcessId);
    },
    hasMaterialsListChanges() {
      if (!this.isEditingList) {
        return false;
      }
      const changes = this.getMaterialsChanges();
      return Boolean(changes.added.length || changes.updated.length || changes.deleted.length);
    },
    showQuickFillControls() {
      return this.isMechanicShipmentMode ||
          this.isMechanicAcceptanceMode ||
          (this.isEditing && (this.canShip || this.canAccept));
    },
    showMechanicShipmentBulkControls() {
      return this.isMechanicShipmentMode &&
          (this.mechanicAssetNeedsTargetUser ||
              this.mechanicAssetNeedsObject ||
              this.mechanicAssetShowsReturnObject ||
              this.mechanicAssetNeedsWarehouse) &&
          (!this.isMechanicRepairMode ||
              this.hasMechanicRepairAcceptedRows ||
              this.hasMechanicReturnObjectRows) &&
          Array.isArray(this.localMaterials) &&
          this.localMaterials.length > 0;
    },
    quickFillAllTitle() {
      if (this.isMechanicRepairCompletionMode) {
        return 'Быстро заполнить этап ремонта по всем строкам';
      }
      if (this.isMechanicShipmentMode) {
        return 'Установить отгрузку в размере «Запрошено» по всем строкам';
      }
      if (this.isMechanicAcceptanceMode || (this.canAccept && !this.canShip)) {
        return 'Установить приемку в размере «Запрошено» по всем строкам';
      }
      return 'Установить отгрузку в размере «Запрошено» по всем строкам';
    },
    editListButtonText() {
      if (this.isMechanicAssetCatalog) {
        if (this.isMechanicRepairMode) {
          return this.canManageMechanicAssetSelection ? 'Ремонт' : 'Редактировать ремонт';
        }
        return this.canManageMechanicAssetSelection ? 'Отгрузка' : 'Редактировать запрошено';
      }
      return 'Редактировать список';
    },
    tableFields() {
      if (this.isEmployeeCatalog || this.isFuelCardCatalog || this.isMechanicAssetCatalog) {
        const canShowMechanicExecutionColumns = this.isMechanicAssetCatalog && this.canManageMechanicAssetSelection;
        const canShowMechanicShipmentColumns = this.isMechanicAssetCatalog;
        const isMechanicRequestEditing = this.isMechanicAssetCatalog &&
            this.isEditingList &&
            !this.canManageMechanicAssetSelection;
        const fields = [
          {key: 'index', label: '№', thStyle: {width: '50px'}},
          {
            key: 'item_name',
            label: this.isFuelCardCatalog
                ? 'Топливная карта'
                : (this.isMechanicAssetCatalog
                    ? (this.isMechanicRepairMode ? 'Что ремонтируем' : 'Запрошено')
                    : 'Сотрудник'),
            tdClass: this.isMechanicAssetCatalog ? 'mechanic-mobile-full-cell' : ''
          }
        ];

        if (this.isMechanicAssetCatalog && this.mechanicAssetNeedsTargetUser && !this.isMechanicRepairMode) {
          fields.push({
            key: 'target_user',
            label: this.isMechanicRepairMode ? 'Ответственный за ремонт' : 'МОЛ / получатель',
            tdClass: 'mechanic-mobile-full-cell',
            thStyle: {width: '240px'}
          });
        }

        if (canShowMechanicShipmentColumns && !isMechanicRequestEditing && this.showMechanicAssetSelectionColumn) {
          fields.push({
            key: 'mechanic_asset_selection',
            label: this.isMechanicRepairMode ? 'Техника / инструмент' : 'Отгружаем',
            thStyle: {width: '360px'}
          });
        }

        if (this.isMechanicAssetCatalog && this.showMechanicAssetQuantityColumn) {
          fields.push({
            key: 'quantity',
            label: 'Кол-во',
            tdClass: 'mechanic-mobile-quantity-cell',
            thStyle: {width: '170px'}
          });
        }

        if (!isMechanicRequestEditing && (this.isMultiExecutor || this.selfAssignPreviewEnabled)) {
          fields.push({key: 'executor', label: 'Исполнитель', thStyle: {width: '220px'}});
        }

        if (this.isMechanicAssetCatalog && this.isMechanicShipmentMode) {
          fields.push({key: 'actions_quick_fill', label: 'Действия', thStyle: {width: '120px'}});
        }

        if (canShowMechanicShipmentColumns && !isMechanicRequestEditing) {
          fields.push({
            key: 'shipped_quantity',
            label: this.isMechanicRepairMode ? 'В ремонт' : 'Отгружено',
            thStyle: {width: '170px'}
          });
          if (this.isMechanicRepairMode && this.hasMechanicRepairAcceptedRows) {
            if (this.mechanicAssetNeedsTargetUser) {
              fields.push({
                key: 'target_user',
                label: 'Ответственный за ремонт',
                tdClass: 'mechanic-mobile-full-cell',
                thStyle: {width: '240px'}
              });
            }
            if (this.mechanicAssetNeedsWarehouse) {
              fields.push({
                key: 'target_warehouse',
                label: 'Сценарий ремонта',
                thStyle: {width: '260px'}
              });
            }
          }
          if (this.isMechanicAssetCatalog && this.isMechanicAcceptanceMode) {
            fields.push({key: 'actions_quick_fill', label: 'Действия', thStyle: {width: '120px'}});
          }
          fields.push({
            key: 'accepted_quantity',
            label: this.mechanicAcceptedColumnLabel,
            thStyle: {width: '170px'}
          });
        }

        if (!this.isMechanicAssetCatalog) {
          fields.push({key: 'notes', label: 'Примечание'});
        }

        if (this.isFuelCardCatalog) {
          if (this.isFuelCardIssueToRecipient || this.isFuelCardMoveToRecipient) {
            fields.push({key: 'target_user', label: 'Получатель', thStyle: {width: '220px'}});
          }
          if (this.isFuelCardIssueToRecipient) {
            fields.push({key: 'fuel_card_selection', label: 'Выбор карты', thStyle: {width: '420px'}});
          }
          if (this.isFuelCardMoveToRecipient) {
            fields.push({key: 'target_building_process', label: 'Объект назначения', thStyle: {width: '300px'}});
          }
          if (this.fuelCardCanEditLimits) {
            fields.push({
              key: 'fuel_limits',
              label: this.isFuelCardChangeLimits ? 'Новые лимиты в сутки' : 'Лимиты в сутки',
              thStyle: {width: '320px'}
            });
          }
        }

        if (this.isMechanicAssetCatalog) {
          if (canShowMechanicShipmentColumns &&
              !isMechanicRequestEditing &&
              (this.mechanicAssetNeedsObject ||
                  (this.mechanicAssetShowsReturnObject && this.hasMechanicReturnObjectRows))) {
            fields.push({
              key: 'target_building_process',
              label: this.mechanicAssetShowsReturnObject ? 'Вернуть на объект' : 'Объект назначения',
              thStyle: {width: '300px'}
            });
          }
          if (canShowMechanicShipmentColumns &&
              !isMechanicRequestEditing &&
              this.mechanicAssetNeedsWarehouse &&
              !this.isMechanicRepairMode) {
            fields.push({
              key: 'target_warehouse',
              label: this.isMechanicRepairMode ? 'Сценарий ремонта' : 'Куда: склад / ремзона',
              thStyle: {width: '260px'}
            });
          }
          if (canShowMechanicExecutionColumns && this.mechanicAssetNeedsRental) {
            fields.push({key: 'rental', label: 'Аренда', thStyle: {width: '280px'}});
          }
          if (canShowMechanicExecutionColumns && this.mechanicAssetRequiresMeterValue) {
            fields.push({key: 'mechanic_meter', label: 'Пробег / моточасы', thStyle: {width: '200px'}});
          }
          if (canShowMechanicExecutionColumns && this.mechanicAssetRequiresCondition) {
            fields.push({key: 'condition', label: 'Состояние', thStyle: {width: '260px'}});
          }
        }

        if (!isMechanicRequestEditing) {
          fields.push({key: 'status', label: 'Этап', thStyle: {width: '220px'}});
        }

        if (this.isEditingList && (!this.isMechanicAssetCatalog || isMechanicRequestEditing)) {
          fields.push({key: 'actions', label: 'Действия', thStyle: {width: '100px'}});
        }

        return fields;
      }

      const fields = [
        {key: 'index', label: '№', thStyle: {width: '50px'}},
        {key: 'item_name', label: 'Наименование', thStyle: {width: '25%'}},
        {key: 'quantity', label: 'Запрошено', thStyle: {width: '150px'}}
      ];

      // В режиме редактирования отгрузки/приемки добавляем колонку быстрого заполнения
      if (this.isEditing && (this.canShip || this.canAccept)) {
        fields.push({key: 'actions_quick_fill', label: 'Действия', thStyle: {width: '120px'}});
      }

      // В режиме редактирования списка не показываем колонки отгрузки/приемки
      if (!this.isEditingList) {
        // В режиме multi-executor добавляем колонку «Исполнитель»
        if (this.isMultiExecutor || this.selfAssignPreviewEnabled) {
          fields.push({key: 'executor', label: 'Исполнитель', thStyle: {width: '220px'}});
        }
        fields.push(
            {key: 'shipped_quantity', label: 'Отгружено', thStyle: {width: '190px'}},
            {key: 'accepted_quantity', label: 'Принято', thStyle: {width: '190px'}},
            {key: 'shipped_percent', label: '% отгрузки', thStyle: {width: '140px'}},
            {key: 'accepted_percent', label: '% приемки', thStyle: {width: '140px'}},
            {key: 'status', label: 'Статус', thStyle: {width: '120px'}}
        );
      }

      // В режиме редактирования списка добавляем колонку действий
      if (this.isEditingList) {
        fields.push({key: 'actions', label: 'Действия', thStyle: {width: '100px'}});
      }

      return fields;
    },
    hasChanges() {
      if (!this.isEditing) return false;

      return this.materials.some(material => {
        const edited = this.editedItems[material.id];
        return (
            edited.shipped_quantity !== material.shipped_quantity ||
            edited.accepted_quantity !== material.accepted_quantity ||
            this.normalizeCommentValue(edited.shipping_comment) !== this.normalizeCommentValue(material.shipping_comment) ||
            this.normalizeCommentValue(edited.acceptance_comment) !== this.normalizeCommentValue(material.acceptance_comment)
        );
      });
    },
    filteredAvailableMaterials() {
      if (!this.materialSearch) return this.availableMaterials;
      const search = this.materialSearch.toLowerCase();
      return this.availableMaterials.filter(material =>
          material.name.toLowerCase().includes(search)
      );
    },
    isEmployeeCatalog() {
      return this.catalogType === 'employees';
    },
    isFuelCardCatalog() {
      return this.catalogType === 'fuel_cards';
    },
    isMechanicAssetCatalog() {
      return this.catalogType === 'mechanic_assets';
    },
    isFuelCardIssueToRecipient() {
      return this.isFuelCardCatalog && this.fuelCardOperation === 'issue_to_recipient';
    },
    isFuelCardMoveToRecipient() {
      return this.isFuelCardCatalog && this.fuelCardOperation === 'move_to_recipient';
    },
    fuelCardNeedsRecipient() {
      return this.isFuelCardIssueToRecipient || this.isFuelCardMoveToRecipient;
    },
    isFuelCardChangeLimits() {
      return this.isFuelCardCatalog && this.fuelCardOperation === 'change_limits';
    },
    fuelCardCanEditLimits() {
      return this.isFuelCardCatalog &&
          ['issue_to_recipient', 'move_to_recipient', 'change_limits'].includes(this.fuelCardOperation);
    },
    fuelCardLimitsRequired() {
      return this.isFuelCardIssueToRecipient || this.isFuelCardChangeLimits;
    },
    mechanicAssetOperation() {
      return this.mechanicAssetAction && this.mechanicAssetAction.operation
          ? this.mechanicAssetAction.operation
          : 'move_to_object';
    },
    mechanicAssetActionEnabled() {
      return this.mechanicAssetAction && this.mechanicAssetAction.enabled === true;
    },
    isMechanicRepairMode() {
      return this.isMechanicAssetCatalog &&
          (this.mechanicAssetTableMode === 'repair' ||
              ['accept_to_repair', 'finish_repair'].includes(this.mechanicAssetOperation));
    },
    mechanicAssetUsesHeaderObject() {
      return this.isMechanicAssetCatalog &&
          this.mechanicAssetActionEnabled &&
          this.mechanicAssetOperation === 'issue_to_object';
    },
    mechanicAssetNeedsObject() {
      return this.isMechanicAssetCatalog &&
          this.mechanicAssetActionEnabled &&
          this.mechanicAssetOperation === 'move_to_object';
    },
    mechanicAssetShowsReturnObject() {
      return this.isMechanicRepairMode;
    },
    hasMechanicRepairAcceptedRows() {
      if (!this.isMechanicRepairMode) {
        return false;
      }
      const sourceItems = this.isEditingList && Array.isArray(this.localMaterials)
          ? this.localMaterials
          : this.materials;
      return Array.isArray(sourceItems) &&
          sourceItems.some(item => this.isMechanicRepairAcceptedVisible(item));
    },
    hasMechanicReturnObjectRows() {
      if (!this.mechanicAssetShowsReturnObject) {
        return false;
      }
      const sourceItems = this.isEditingList && Array.isArray(this.localMaterials)
          ? this.localMaterials
          : this.materials;
      return Array.isArray(sourceItems) &&
          sourceItems.some(item => this.isMechanicReturnObjectVisible(item));
    },
    mechanicAssetNeedsTargetUser() {
      return this.isMechanicAssetCatalog &&
          this.mechanicAssetActionEnabled &&
          (this.isMechanicRepairMode ||
              ['issue_to_object', 'move_to_object'].includes(this.mechanicAssetOperation));
    },
    mechanicAssetNeedsWarehouse() {
      return this.isMechanicAssetCatalog &&
          this.mechanicAssetActionEnabled &&
          (this.isMechanicRepairMode ||
              ['move_to_warehouse', 'return_to_warehouse', 'return_from_rent'].includes(this.mechanicAssetOperation));
    },
    mechanicAssetNeedsRental() {
      return this.isMechanicAssetCatalog &&
          this.mechanicAssetActionEnabled &&
          this.mechanicAssetOperation === 'rent_out';
    },
    mechanicAssetRequiresMeterValue() {
      return this.isMechanicAssetCatalog &&
          this.mechanicAssetActionEnabled &&
          this.mechanicAssetAction.requireMeterValue === true;
    },
    mechanicAssetRequiresCondition() {
      return this.isMechanicAssetCatalog &&
          this.mechanicAssetActionEnabled &&
          this.mechanicAssetAction.requireCondition === true;
    },
    canEditFuelCardLimits() {
      return this.canManageFuelCardLimits || (this.isEditingList && this.canEditList && this.fuelCardCanEditLimits);
    },
    fuelCardRecipientOptions() {
      const users = (this.$store.state.users || [])
          .filter(user => this.isActiveHumanUser(user))
          .map(user => ({
            value: Number(this.getUserId(user)),
            text: this.formatUserName(user)
          }))
          .filter(option => option.value && option.text)
          .sort((a, b) => a.text.localeCompare(b.text, 'ru'));

      return [{ value: null, text: 'Выберите сотрудника' }].concat(users);
    },
    fuelTypeOptions() {
      return buildFuelTypeOptions(this.$store.state.fuelTypes || []);
    },
    fuelTypeById() {
      return buildFuelTypeById(this.fuelTypeOptions);
    },
    fuelLimitTypeOptions() {
      return [{ value: null, text: 'Выберите топливо' }].concat(this.fuelTypeOptions);
    },
    sanitizedFuelCardLimitsDraft() {
      return this.sanitizeFuelLimits(this.fuelCardLimitsDraft);
    },
    canSaveFuelCardLimits() {
      return this.canEditFuelCardLimits &&
          !this.fuelCardLimitsBusy &&
          !!(this.fuelCardLimitsTarget && (this.fuelCardLimitsTarget.id || this.fuelCardLimitsTarget._tempId)) &&
          (!this.fuelCardLimitsRequired || this.sanitizedFuelCardLimitsDraft.length > 0);
    },
    showCatalogItemQuantity() {
      return !this.isEmployeeCatalog && !this.isFuelCardCatalog && !this.isMechanicAssetCatalog;
    },
    showMechanicAssetQuantityColumn() {
      if (!this.isMechanicAssetCatalog) {
        return false;
      }
      const rows = this.isEditingList ? this.localMaterials : this.materials;
      return Array.isArray(rows) && rows.some(item => this.isMechanicQuantityRequest(item));
    },
    showMechanicAssetSelectionColumn() {
      if (!this.isMechanicAssetCatalog) {
        return false;
      }
      if (this.isMechanicShipmentMode) {
        return true;
      }
      const rows = this.isEditingList ? this.localMaterials : this.materials;
      return Array.isArray(rows) && rows.some(item =>
          item &&
          (!this.isMechanicQuantityRequest(item) ||
              this.normalizeSelectId(item.catalog_item_id) ||
              this.hasMechanicShipmentSelection(item))
      );
    },
    canAddCustomCatalogItem() {
      if (this.isFuelCardCatalog) {
        return false;
      }
      if (this.isMechanicAssetCatalog) {
        return true;
      }
      if (this.isEmployeeCatalog) {
        return this.allowCustomCatalogItems === true;
      }
      return true;
    },
    mechanicAssetOptions() {
      if (!this.isMechanicAssetCatalog) {
        return [];
      }
      return (this.availableMaterials || [])
          .map(asset => ({
            value: Number(asset.id),
            text: asset.name || `Техника #${asset.id}`,
            assignment_details: asset.assignment_details || null,
            unit_of_measurement: asset.unit_of_measurement || 'ед.',
            status: asset.status || null,
            status_label: asset.status_label || null,
            active_transfer: asset.active_transfer || null,
            catalog_name: asset.catalog_name || asset.catalog_group_name || null,
            inventory_number: asset.inventory_number || null,
            accounting_type: asset.accounting_type || 'inventory',
            available_quantity: asset.available_quantity === undefined ? asset.quantity : asset.available_quantity,
            current_meter_type: asset.current_meter_type || null,
            current_meter_value: asset.current_meter_value === undefined ? null : asset.current_meter_value,
            receipt_quantity: asset.receipt_quantity === undefined ? null : asset.receipt_quantity
          }))
          .filter(option => Number.isInteger(option.value) && option.value > 0 && option.text);
    },
    mechanicAssetPickerTargetItem() {
      return this.mechanicAssetPicker && this.mechanicAssetPicker.item
          ? this.mechanicAssetPicker.item
          : null;
    },
    mechanicAssetPickerTargetFulfillment() {
      const item = this.mechanicAssetPickerTargetItem;
      if (!item || this.mechanicAssetPicker.fulfillmentIndex === null || this.mechanicAssetPicker.fulfillmentIndex === undefined) {
        return null;
      }
      const rows = this.getEditedMechanicFulfillments(item);
      return rows[this.mechanicAssetPicker.fulfillmentIndex] || null;
    },
    mechanicAssetPickerRequestItemId() {
      const item = this.mechanicAssetPickerTargetItem;
      const id = Number(item && item.id);
      return Number.isInteger(id) && id > 0 ? id : null;
    },
    mechanicAssetPickerRequestedQuantity() {
      const item = this.mechanicAssetPickerTargetItem;
      return item ? this.getRequestedQuantity(item) : 1;
    },
    mechanicAssetPickerRequestedUnit() {
      const item = this.mechanicAssetPickerTargetItem;
      if (!item) {
        return 'шт';
      }
      const edited = this.getEditedItem(item);
      return edited.unit || item.unit || 'шт';
    },
    mechanicAssetPickerReceiptEnabled() {
      const item = this.mechanicAssetPickerTargetItem;
      return !!(
        item &&
        this.mechanicAssetPickerRequestItemId &&
        this.isMechanicShipmentMode &&
        this.canManageMechanicAssetSelection &&
        this.canShipItem(item)
      );
    },
    mechanicAssetPickerSelectedId() {
      const item = this.mechanicAssetPickerTargetItem;
      if (!item) {
        return null;
      }
      const fulfillment = this.mechanicAssetPickerTargetFulfillment;
      if (fulfillment) {
        return this.normalizeSelectId(fulfillment.asset_unit_id || fulfillment.catalog_item_id);
      }
      return this.getEditedMechanicAssetId(item);
    },
    mechanicAssetPickerExcludedIds() {
      const item = this.mechanicAssetPickerTargetItem;
      if (!item) {
        return [];
      }
      const fulfillment = this.mechanicAssetPickerTargetFulfillment;
      const usedIds = this.getMechanicShipmentSameRowUsedAssetIds({
        currentItem: item,
        currentFulfillment: fulfillment,
        ignoreCurrentItemMain: !fulfillment
      });
      return Array.from(usedIds);
    },
    mechanicAssetPickerUsedIds() {
      const item = this.mechanicAssetPickerTargetItem;
      if (!item) {
        return [];
      }
      return Array.from(this.getMechanicShipmentOtherRowUsedAssetIds({ currentItem: item }));
    },
    mechanicAssetPickerTitle() {
      return this.mechanicAssetPickerTargetFulfillment
          ? 'Выбор дополнительной техники'
          : 'Выбор техники';
    },
    mechanicAssetPickerRowTitle() {
      const item = this.mechanicAssetPickerTargetItem;
      return item ? this.getMechanicRequestedDisplayName(item) : '';
    },
    mechanicMaterialSelectorUsedIds() {
      if (!this.isMechanicAssetCatalog) {
        return [];
      }
      return Array.from(this.getMechanicShipmentUsedAssetIds());
    },
    fuelCardSelectOptions() {
      const options = (this.fuelCardOptions || []).map(card => ({
        value: card.id,
        text: card.name || card.card_number || `Карта #${card.id}`
      }));

      return [
        { value: null, text: this.fuelCardOptionsLoading ? 'Загрузка...' : 'Выберите карту' },
        ...options
      ];
    },
    fuelCardTargetObjectSelectOptions() {
      const options = (this.fuelCardTargetObjectOptions || []).map(process => ({
        value: process.building_process_id || process.id || process.value,
        text: process.text || process.object_name || process.name || `Объект #${process.building_process_id || process.id}`
      }));

      return [
        { value: null, text: this.fuelCardTargetObjectOptionsLoading ? 'Загрузка...' : 'Выберите объект' },
        ...options
      ];
    },
    mechanicAssetUserOptions() {
      const users = this.$store && this.$store.state ? (this.$store.state.users || []) : [];
      const options = users
          .filter(user => this.isActiveHumanUser(user))
          .map(user => ({
            value: user.user_id || user.userId || user.id,
            text: this.formatUserName(user)
          }))
          .filter(option => option.value && option.text)
          .sort((a, b) => a.text.localeCompare(b.text, 'ru'));
      return [{ value: null, text: 'Выберите сотрудника' }].concat(options);
    },
    mechanicAssetWarehouseOptions() {
      const warehouses = this.$store && this.$store.state && this.$store.state.mechanicAssets
          ? (this.$store.state.mechanicAssets.warehouses || [])
          : [];
      const options = warehouses.map(warehouse => ({
        value: warehouse.id,
        text: warehouse.name
      }));
      return [{ value: null, text: 'Выберите склад/ремзону' }].concat(options);
    },
    mechanicAssetWarehouseTargetOptions() {
      if (!this.isMechanicRepairMode) {
        return this.mechanicAssetWarehouseOptions;
      }
      const options = this.mechanicAssetWarehouseOptions.slice(1)
          .map(option => Object.assign({}, option, {text: `На базу: ${option.text}`}));
      return [
        {value: MECHANIC_REPAIR_SCENARIO_SELF_ON_OBJECT, text: 'Самостоятельный ремонт на объекте'},
        {value: MECHANIC_REPAIR_SCENARIO_SPECIALIST_ON_OBJECT, text: 'Выезд специалиста на объект'}
      ].concat(options);
    },
    mechanicWarehouseBulkLabel() {
      return this.isMechanicRepairMode
          ? 'Сценарий ремонта на все позиции'
          : 'Склад / ремзона на все позиции';
    },
    mechanicReturnObjectBulkLabel() {
      return this.isMechanicRepairMode
          ? 'Вернуть на объект для всех позиций'
          : 'Объект назначения на все позиции';
    },
    mechanicAcceptedColumnLabel() {
      if (!this.isMechanicRepairMode) {
        return 'Принято';
      }
      return this.canManageMechanicAssetSelection ? 'Готово' : 'Принято на объекте';
    },
    mechanicAcceptedFlagLabel() {
      if (this.isMechanicRepairCompletionMode) {
        return 'Готово';
      }
      return this.isMechanicRepairMode ? 'Принято на объекте' : 'Принято';
    },
    mechanicAssetObjectOptions() {
      const storeProcesses = this.$store && this.$store.state ? (this.$store.state.objectsInProcess || []) : [];
      const processes = this.mechanicAssetShowsReturnObject
          ? this.mechanicAssetReturnObjectOptions
          : storeProcesses;
      const optionsById = new Map();
      const addOption = option => {
        const value = this.normalizeSelectId(option && option.value);
        const text = String(option && option.text || '').trim();
        if (value && text && !optionsById.has(value)) {
          optionsById.set(value, {value, text});
        }
      };

      processes.forEach(process => {
        const value = process.building_process_id || process.value || process.id;
        addOption({
          value,
          text: process.text ||
              process.object_name ||
              (process.bo && process.bo.name) ||
              process.name ||
              `Объект #${value}`
        });
      });

      addOption({
        value: this.requestBuildingProcessIdValue,
        text: this.requestBuildingObjectName || (this.requestBuildingProcessIdValue
            ? `Объект #${this.requestBuildingProcessIdValue}`
            : '')
      });

      const sourceItems = this.isEditingList && Array.isArray(this.localMaterials)
          ? this.localMaterials
          : this.materials;
      (Array.isArray(sourceItems) ? sourceItems : []).forEach(item => {
        const process = item && item.target_building_process;
        const value = item && item.target_building_process_id;
        addOption({
          value,
          text: process && (process.object_name || process.name || process.text)
              ? (process.object_name || process.name || process.text)
              : (value ? `Объект #${value}` : '')
        });
      });

      const options = Array.from(optionsById.values())
          .sort((a, b) => a.text.localeCompare(b.text, 'ru'));
      const placeholder = this.mechanicAssetShowsReturnObject && this.mechanicAssetReturnObjectOptionsLoading
          ? 'Загрузка объектов...'
          : (this.mechanicAssetShowsReturnObject && this.mechanicAssetReturnObjectOptions.length === 0
              ? 'Откройте список для загрузки объектов'
              : 'Выберите объект');
      return [{ value: null, text: placeholder }].concat(options);
    },
    mechanicAssetMeterTypeOptions() {
      return [
        { value: null, text: 'Выберите счетчик' },
        { value: 'km', text: 'Километры' },
        { value: 'moto_hour', text: 'Моточасы' }
      ];
    },
    hasFuelCardRowsWithoutCard() {
      return this.isFuelCardIssueToRecipient &&
          Array.isArray(this.materials) &&
          this.materials.some(item => item && item.target_user_id && !item.catalog_item_id);
    },
    hasFuelCardRowsWithoutTargetObject() {
      return this.isFuelCardMoveToRecipient &&
          Array.isArray(this.materials) &&
          this.materials.some(item => item && item.catalog_item_id && item.target_user_id && !item.target_building_process_id);
    },
    hasMechanicRowsWithoutAsset() {
      return this.isMechanicAssetCatalog &&
          Array.isArray(this.materials) &&
          this.materials.some(item =>
              item &&
              !this.isMechanicManualEditable(item) &&
              !this.isMechanicQuantityRequest(item) &&
              !this.hasMechanicShipmentSelection(item)
          );
    },
    hasMechanicRowsWithoutTargetObject() {
      return this.isMechanicAssetCatalog &&
          this.mechanicAssetNeedsObject &&
          Array.isArray(this.materials) &&
          this.materials.some(item => item && !item.target_building_process_id);
    },
    catalogIcon() {
      if (this.isFuelCardCatalog) {
        return 'fas fa-gas-pump';
      }
      if (this.isMechanicAssetCatalog) {
        return 'fas fa-tools';
      }
      return this.isEmployeeCatalog ? 'fas fa-clipboard-list' : 'fas fa-box-open';
    },
    itemListName() {
      if (this.isFuelCardCatalog) {
        return 'топливные карты';
      }
      if (this.isMechanicAssetCatalog) {
        return 'технику';
      }
      return this.isEmployeeCatalog ? 'сотрудников' : 'материалы';
    },
    catalogTitle() {
      const label = String(this.catalogLabel || '').trim();
      if (label) {
        return label;
      }

      if (this.isFuelCardCatalog) {
        return 'Топливные карты';
      }
      if (this.isMechanicAssetCatalog) {
        return 'Техника';
      }
      return this.isEmployeeCatalog ? 'Сотрудники' : 'Материалы';
    },
    editButtonText() {
      // Формируем текст кнопки в зависимости от прав пользователя
      if (this.canShip && this.canAccept) {
        return 'Выполнить отгрузку/приемку';
      } else if (this.canShip) {
        return 'Выполнить отгрузку';
      } else if (this.canAccept) {
        return 'Выполнить приемку';
      }
      return 'Редактировать';
    },
    currentUserId() {
      const cu = this.$store && this.$store.getters && this.$store.getters.currentUser;
      return cu ? (cu.userId || cu.user_id || cu.id) : null;
    },
    reassignExecutorOptions() {
      return (this.availableExecutors || []).map(u => ({
        value: Number(u.user_id || u.id),
        text: `${u.last_name || ''} ${u.first_name || ''}`.trim() || `#${u.user_id || u.id}`
      }));
    },
    showBulkReassignButton() {
      return this.isMultiExecutor
          && this.canReassignItemExecutor
          && !this.isEditing
          && !this.isEditingList
          && this.bulkReassignSourceOptions.length > 0;
    },
    bulkReassignSourceOptions() {
      const sourceById = new Map();
      (this.materials || []).forEach(item => {
        const executorId = this.getItemExecutorId(item);
        if (!executorId) return;
        const current = sourceById.get(executorId) || {
          value: executorId,
          name: this.getItemExecutorName(item),
          total: 0,
          open: 0
        };
        current.total += 1;
        if (this.isBulkReassignOpenItem(item)) {
          current.open += 1;
        }
        sourceById.set(executorId, current);
      });

      return Array.from(sourceById.values()).map(source => ({
        value: source.value,
        text: `${source.name} (${source.open}/${source.total} незакрыто)`
      }));
    },
    bulkReassignExecutorOptions() {
      return this.reassignExecutorOptions.filter(option =>
          Number(option.value) !== Number(this.bulkReassignFromExecutorId)
      );
    },
    bulkReassignCandidateItems() {
      if (!this.bulkReassignFromExecutorId) {
        return [];
      }
      return (this.materials || []).filter(item =>
          this.getItemExecutorId(item) === Number(this.bulkReassignFromExecutorId)
      );
    },
    bulkReassignItemOptions() {
      return this.bulkReassignCandidateItems.map(item => ({
        value: item.id,
        text: `${item.item_name || `Строка #${item.id}`} — ${this.getBulkReassignItemProgress(item)}`
      }));
    },
    bulkReassignSelectedItems() {
      const selected = new Set((this.bulkReassignSelectedIds || []).map(id => String(id)));
      return this.bulkReassignCandidateItems.filter(item => selected.has(String(item.id)));
    },
    bulkReassignSelectedWithShipmentCount() {
      return this.bulkReassignSelectedItems.filter(item => this.getNumericValue(item.shipped_quantity) > 0).length;
    },
    canConfirmBulkReassign() {
      return !!this.bulkReassignExecutorId
          && Number(this.bulkReassignExecutorId) !== Number(this.bulkReassignFromExecutorId)
          && Array.isArray(this.bulkReassignSelectedIds)
          && this.bulkReassignSelectedIds.length > 0
          && !this.reassigning;
    },
    currentExecutorIdForTarget() {
      if (!this.reassignTarget) return null;
      return this.reassignTarget.executor
          ? this.reassignTarget.executor.user_id
          : (this.reassignTarget.executor_id || null);
    },
    canConfirmReassign() {
      return !!this.reassignExecutorId
          && this.reassignExecutorId !== this.currentExecutorIdForTarget
          && !this.reassigning;
    },
    canSelfAssignBulkAction() {
      return this.canSelfAssignBulk
          || this.selfAssignPreviewEnabled;
    },
    canSelfAssignRows() {
      return this.canSelfAssignItemExecutor
          || this.selfAssignPreviewEnabled;
    },
    showSelfAssignBulkButton() {
      return this.canSelfAssignBulkAction
          && this.hasFreeExecutorRows
          && !this.isEditing
          && !this.isEditingList;
    },
    hasFreeExecutorRows() {
      return Array.isArray(this.materials)
          && this.materials.some(item => {
            const executorId = item && ((item.executor && item.executor.user_id) || item.executor_id);
            return !executorId;
          });
    },
    /**
     * В multi-режиме «свои» строки (на которые назначен текущий пользователь) поднимаем наверх.
     * В обычном режиме и в режиме редактирования списка — порядок сохраняется как есть.
     */
    displayedMaterials() {
      if (this.isEditingList) {
        return Array.isArray(this.localMaterials) ? this.localMaterials : [];
      }
      if (!Array.isArray(this.materials)) {
        return [];
      }
      if (!this.isMultiExecutor) {
        return this.materials;
      }
      // Стабильная сортировка: «свои» строки наверх, остальные — в исходном порядке
      return this.materials
          .map((item, idx) => ({ item, idx }))
          .sort((a, b) => {
            const aOwn = this.isOwnItem(a.item) ? 1 : 0;
            const bOwn = this.isOwnItem(b.item) ? 1 : 0;
            if (aOwn !== bOwn) return bOwn - aOwn;
            return a.idx - b.idx;
          })
          .map(({ item }) => item);
    }
  },
  methods: {
    formatLinkedText(text) {
      return linkifyText(text);
    },

    canUseHorizontalDrag() {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false;
      }

      return window.matchMedia('(pointer: coarse)').matches;
    },

    destroyHorizontalDrag() {
      if (!this._dragHandlers) return;

      const {el, onMouseDown, onMouseMove, endDrag} = this._dragHandlers;
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mousemove', onMouseMove);
      el.removeEventListener('mouseleave', endDrag);
      document.removeEventListener('mouseup', endDrag);
      el.classList.remove('dragging');

      this._dragHandlers = null;
      this._dragActive = false;
    },

    startEditing() {
      this.isEditing = true;
      this.expandedOperationComments = {};
      // Создаем копии для редактирования
      this.materials.forEach(material => {
        this.$set(this.editedItems, material.id, {
          shipped_quantity: material.shipped_quantity || 0,
          accepted_quantity: material.accepted_quantity || 0,
          shipping_comment: material.shipping_comment || '',
          acceptance_comment: material.acceptance_comment || ''
        });
        this.$set(this.originalItems, material.id, {
          shipped_quantity: material.shipped_quantity || 0,
          accepted_quantity: material.accepted_quantity || 0,
          shipping_comment: material.shipping_comment || '',
          acceptance_comment: material.acceptance_comment || ''
        });
        if (this.normalizeCommentValue(material.shipping_comment)) {
          this.$set(this.expandedOperationComments, this.operationCommentKey(material, 'shipping'), true);
        }
        if (this.normalizeCommentValue(material.acceptance_comment)) {
          this.$set(this.expandedOperationComments, this.operationCommentKey(material, 'acceptance'), true);
        }
      });
    },
    async cancelEditing() {
      // Проверяем, есть ли несохраненные изменения
      if (this.hasChanges) {
        // Подтверждение отмены
        const confirmed = await this.$bvModal.msgBoxConfirm(
            'Вы уверены, что хотите отменить редактирование? Все внесенные изменения будут потеряны.',
            { contentClass: 'argus-glass-dialog',
              title: 'Отмена редактирования',
              size: 'sm',
              buttonSize: 'sm',
              okVariant: 'warning',
              okTitle: 'Да, отменить',
              cancelTitle: 'Продолжить редактирование',
              footerClass: 'p-2',
              hideHeaderClose: false,
              centered: true
            }
        );

        if (!confirmed) return;
      }

      this.isEditing = false;
      this.editedItems = {};
      this.originalItems = {};
      this.expandedOperationComments = {};
    },
    onShippedChange(item) {
      const edited = this.editedItems[item.id];
      // Если принятое количество больше отгруженного, корректируем
      if (edited.accepted_quantity > edited.shipped_quantity) {
        edited.accepted_quantity = edited.shipped_quantity;
      }
    },
    canShipItem(item) {
      // В обычном режиме — общий canShip
      if (!this.isMultiExecutor) {
        return !!this.canShip;
      }
      // В multi-режиме сервер для админа может вернуть canShip=true для всех строк.
      // В обычной ролевой проекции админ должен видеть только свои строки.
      if (typeof item.canShip === 'boolean') {
        if (!this.adminGodModeEnabled) {
          const executorId = (item.executor && item.executor.user_id) || item.executor_id;
          return executorId === this.currentUserId && this.canShip;
        }
        return item.canShip && this.canShip;
      }
      // Fallback: совпадение текущего пользователя и executor_id строки
      if (item.executor_id && this.currentUserId) {
        return item.executor_id === this.currentUserId && this.canShip;
      }
      return false;
    },
    canQuickFillItem(item) {
      if (this.isMechanicShipmentMode) {
        return this.canManageMechanicAssetSelection;
      }
      if (this.isMechanicAcceptanceMode) {
        return this.canAccept;
      }
      return (this.canShip && this.canShipItem(item)) || this.canAccept;
    },
    getQuickFillRowTitle(item) {
      if (!this.canQuickFillItem(item)) {
        return 'Вы не назначены исполнителем на эту строку';
      }
      if (this.shouldQuickFillMechanicRepairCompletion(item)) {
        return 'Отметить всю технику в этой строке как готовую';
      }
      if (this.isMechanicShipmentMode) {
        return 'Отгрузили всё, что запрошено в этой строке';
      }
      if (this.isMechanicAcceptanceMode || (this.canAccept && !this.canShip)) {
        return 'Получили всё по этой строке';
      }
      return 'Отгрузили всё, что запрошено в этой строке';
    },
    getRequestedQuantity(item) {
      const quantity = Number(item && item.quantity);
      if (Number.isFinite(quantity) && quantity > 0) {
        return quantity;
      }
      return this.isMechanicAssetCatalog && !this.isMechanicQuantityRequest(item) ? 1 : 0;
    },
    getAcceptedQuickFillQuantity(item, edited) {
      const requested = this.getRequestedQuantity(item);
      const shipped = Number(
          edited && edited.shipped_quantity !== undefined
              ? edited.shipped_quantity
              : item && item.shipped_quantity
      );
      if (Number.isFinite(shipped) && shipped > 0) {
        return Math.min(requested, shipped);
      }
      return requested;
    },
    shouldQuickFillMechanicRepairCompletion(item) {
      if (!this.isMechanicRepairCompletionMode) {
        return false;
      }
      const edited = this.getEditedItem(item);
      const shipped = Number(
          edited && edited.shipped_quantity !== undefined
              ? edited.shipped_quantity
              : item && item.shipped_quantity
      );
      return Number.isFinite(shipped) && shipped > 0;
    },

    isOwnItem(item) {
      if (!this.isMultiExecutor) return false;
      if (!this.currentUserId) return false;
      const execId = (item.executor && item.executor.user_id) || item.executor_id;
      return execId === this.currentUserId;
    },

    /**
     * Класс строки таблицы. В multi-режиме подсвечиваем «свои» строки (на которые назначен пользователь).
     */
    rowClass(item) {
      if (!item) return '';
      if (this.isMultiExecutor && this.isOwnItem(item)) {
        return 'own-executor-row';
      }
      return '';
    },

    getItemExecutorId(item) {
      const executorId = item && item.executor
          ? item.executor.user_id
          : (item && item.executor_id ? item.executor_id : null);
      return executorId ? Number(executorId) : null;
    },

    getItemExecutorName(item) {
      if (item && item.executor) {
        const name = `${item.executor.last_name || ''} ${item.executor.first_name || ''}`.trim();
        return name || `#${item.executor.user_id}`;
      }
      const executorId = this.getItemExecutorId(item);
      return executorId ? `#${executorId}` : 'Не назначен';
    },

    getNumericValue(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },

    isBulkReassignOpenItem(item) {
      const requested = this.getNumericValue(item && item.quantity);
      const shipped = this.getNumericValue(item && item.shipped_quantity);
      if (requested <= 0) {
        return true;
      }
      return shipped < requested;
    },

    getBulkReassignItemProgress(item) {
      return `отгружено ${this.formatNumber(item && item.shipped_quantity || 0)} из ${this.formatNumber(item && item.quantity || 0)} ${item && item.unit || ''}`.trim();
    },

    setDefaultBulkReassignSelection() {
      const openItems = this.bulkReassignCandidateItems.filter(item => this.isBulkReassignOpenItem(item));
      const source = openItems.length ? openItems : this.bulkReassignCandidateItems;
      this.bulkReassignSelectedIds = source.map(item => item.id);
    },

    selectOpenBulkReassignItems() {
      this.bulkReassignSelectedIds = this.bulkReassignCandidateItems
          .filter(item => this.isBulkReassignOpenItem(item))
          .map(item => item.id);
    },

    selectAllBulkReassignItems() {
      this.bulkReassignSelectedIds = this.bulkReassignCandidateItems.map(item => item.id);
    },

    openBulkReassignModal() {
      const sourceOptions = this.bulkReassignSourceOptions;
      this.bulkReassignFromExecutorId = sourceOptions.length ? sourceOptions[0].value : null;
      this.bulkReassignExecutorId = null;
      this.$nextTick(() => this.setDefaultBulkReassignSelection());
      this.showBulkReassignModal = true;
    },

    openReassignModal(item) {
      this.reassignTarget = item;
      this.reassignExecutorId = null;
      this.showReassignModal = true;
    },

    canShowSelfAssign(item) {
      if (!this.canSelfAssignRows) return false;
      if (!this.currentUserId) return false;
      const executorId = (item.executor && item.executor.user_id) || item.executor_id || null;
      return executorId !== this.currentUserId;
    },

    canShowSelfRelease(item) {
      if (!this.canSelfReleaseItemExecutor) return false;
      if (!this.currentUserId) return false;
      const executorId = (item.executor && item.executor.user_id) || item.executor_id || null;
      return executorId === this.currentUserId;
    },

    async confirmSelfAssign(item) {
      if (!item || !item.id) return;
      const takeoverText = item.executor
          ? `Сейчас за эту позицию отвечает ${item.executor.last_name || ''} ${item.executor.first_name || ''}. Взять её на себя?`
          : 'Взять эту позицию на себя?';
      const confirmed = await this.$bvModal.msgBoxConfirm(takeoverText, { contentClass: 'argus-glass-dialog',
        title: 'Взять на себя',
        okVariant: 'primary',
        okTitle: 'Подтвердить',
        cancelTitle: 'Отмена',
        centered: true
      });
      if (!confirmed) return;
      this.reassigning = true;
      try {
        await this.$store.dispatch('requestCard/requestCard_selfAssignItemExecutor', { itemId: item.id });
        this.$emit('saved');
      } catch (error) {
        const errorMsg = error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : (error && error.message) || 'Не удалось взять позицию на себя';
        this.$bvToast && this.$bvToast.toast(errorMsg, {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.reassigning = false;
      }
    },

    async confirmSelfRelease(item) {
      if (!item || !item.id) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(
          'Отказаться от этой позиции? Её сможет взять другой исполнитель.',
          { contentClass: 'argus-glass-dialog',
            title: 'Освободить позицию',
            okVariant: 'warning',
            okTitle: 'Снять',
            cancelTitle: 'Отмена',
            centered: true
          }
      );
      if (!confirmed) return;
      this.reassigning = true;
      try {
        await this.$store.dispatch('requestCard/requestCard_selfReleaseItemExecutor', { itemId: item.id });
        this.$emit('saved');
      } catch (error) {
        const errorMsg = error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : (error && error.message) || 'Не удалось освободить позицию';
        this.$bvToast && this.$bvToast.toast(errorMsg, {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.reassigning = false;
      }
    },

    async confirmSelfAssignBulk() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
          'Взять на себя все позиции без исполнителя?',
          { contentClass: 'argus-glass-dialog',
            title: 'Взять свободные позиции',
            okVariant: 'primary',
            okTitle: 'Назначить',
            cancelTitle: 'Отмена',
            centered: true
          }
      );
      if (!confirmed) return;
      this.reassigning = true;
      try {
        await this.$store.dispatch('requestCard/requestCard_selfAssignBulk');
        this.$emit('saved');
      } catch (error) {
        const errorMsg = error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : (error && error.message) || 'Не удалось выполнить массовое самоназначение';
        this.$bvToast && this.$bvToast.toast(errorMsg, {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.reassigning = false;
      }
    },

    resetReassignForm() {
      this.reassignTarget = null;
      this.reassignExecutorId = null;
      this.reassigning = false;
    },

    resetBulkReassignForm() {
      this.showBulkReassignModal = false;
      this.bulkReassignFromExecutorId = null;
      this.bulkReassignExecutorId = null;
      this.bulkReassignSelectedIds = [];
      this.reassigning = false;
    },

    positionModalSelectDropdown: positionVueSelectDropdown,

    async confirmBulkReassign() {
      if (!this.canConfirmBulkReassign) return;

      this.reassigning = true;
      try {
        await this.$store.dispatch('requestCard/requestCard_bulkReassignItemExecutor', {
          itemIds: this.bulkReassignSelectedIds,
          executorId: this.bulkReassignExecutorId
        });
        this.showBulkReassignModal = false;
        this.$emit('saved');
      } catch (error) {
        const errorMsg = error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : (error && error.message) || 'Не удалось массово переназначить исполнителя';
        this.$bvToast && this.$bvToast.toast(errorMsg, {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.reassigning = false;
      }
    },

    async confirmReassign() {
      if (!this.canConfirmReassign || !this.reassignTarget) return;

      this.reassigning = true;
      try {
        await this.$store.dispatch('requestCard/requestCard_reassignItemExecutor', {
          itemId: this.reassignTarget.id,
          executorId: this.reassignExecutorId
        });
        this.showReassignModal = false;
        this.$emit('saved');
      } catch (error) {
        const errorMsg = error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : (error && error.message) || 'Не удалось переназначить исполнителя';
        this.$bvToast && this.$bvToast.toast(errorMsg, {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.reassigning = false;
      }
    },

    fillAllQuantities() {
      const sourceItems = this.isMechanicShipmentMode ? this.localMaterials : this.materials;
      sourceItems.forEach(item => this.fillRowQuantity(item, { silent: true }));
      if (this.isMechanicShipmentMode) {
        this.onMaterialFieldChange();
      }
    },
    fillRowQuantity(item, options = {}) {
      if (!this.canQuickFillItem(item)) {
        return;
      }

      if (this.isMechanicShipmentMode) {
        const edited = this.getEditedItem(item);
        const requested = this.getRequestedQuantity(item);
        const localMaterial = this.localMaterials.find(material =>
            (material.id && material.id === item.id) ||
            (material._tempId && material._tempId === item._tempId)
        );
        if (this.shouldQuickFillMechanicRepairCompletion(item)) {
          const accepted = this.getAcceptedQuickFillQuantity(item, edited);
          this.setMechanicRepairAcceptedQuantity(item, accepted, options);
          return;
        }
        if (this.shouldEditMechanicFulfillmentShippedQuantity(item)) {
          this.setMechanicShipmentQuantityFromColumn(item, requested, options);
          return;
        }
        this.$set(edited, 'shipped_quantity', requested);
        if (localMaterial) {
          this.$set(localMaterial, 'shipped_quantity', requested);
        }

        if (!options.silent) {
          this.onMaterialFieldChange();
        }
        return;
      }

      const edited = this.editedItems[item.id];
      if (!edited) return;

      if (this.canShip && this.canShipItem(item)) {
        edited.shipped_quantity = this.getRequestedQuantity(item);
      }

      if (this.canAccept) {
        edited.accepted_quantity = this.getAcceptedQuickFillQuantity(item, edited);
      }

      if (this.canShip && this.canShipItem(item)) {
        this.onShippedChange(item);
      }
    },
    collectOperationChanges() {
      const shippedChanges = [];
      const acceptedChanges = [];
      const shippingQuantityChangedItemIds = [];
      const acceptanceQuantityChangedItemIds = [];

      this.materials.forEach(material => {
          const edited = this.editedItems[material.id];
          const original = this.originalItems[material.id];
          if (!edited || !original) return;
          const shippingCommentChanged =
              this.normalizeCommentValue(edited.shipping_comment) !== this.normalizeCommentValue(original.shipping_comment);
          const acceptanceCommentChanged =
              this.normalizeCommentValue(edited.acceptance_comment) !== this.normalizeCommentValue(original.acceptance_comment);
          const shippingQuantityChanged =
              Number(edited.shipped_quantity || 0) !== Number(original.shipped_quantity || 0);
          const acceptanceQuantityChanged =
              Number(edited.accepted_quantity || 0) !== Number(original.accepted_quantity || 0);

          if (this.canShip
              && this.canShipItem(material)
              && (
                  shippingQuantityChanged ||
                  shippingCommentChanged
              )) {
            shippedChanges.push({
              item_id: material.id,
              shipped_quantity: edited.shipped_quantity,
              shipping_comment: this.normalizeCommentValue(edited.shipping_comment)
            });
            if (shippingQuantityChanged) {
              shippingQuantityChangedItemIds.push(material.id);
            }
          }

          if (this.canAccept && (
              edited.accepted_quantity !== original.accepted_quantity ||
              acceptanceCommentChanged
          )) {
            acceptedChanges.push({
              item_id: material.id,
              accepted_quantity: edited.accepted_quantity,
              acceptance_comment: this.normalizeCommentValue(edited.acceptance_comment)
            });
            if (acceptanceQuantityChanged) {
              acceptanceQuantityChangedItemIds.push(material.id);
            }
          }
      });

      return {
        shippedChanges,
        acceptedChanges,
        shippingQuantityChangedItemIds,
        acceptanceQuantityChangedItemIds
      };
    },
    getShippingCompletionPreview(shippingQuantityChangedItemIds) {
      if (!shippingQuantityChangedItemIds.length) return null;

      const changedIds = new Set(shippingQuantityChangedItemIds.map(id => Number(id)));
      const getNextShipped = item => {
        const edited = this.editedItems[item.id];
        return Number(edited && edited.shipped_quantity !== undefined
            ? edited.shipped_quantity
            : item.shipped_quantity || 0);
      };
      const isFullyShipped = item => getNextShipped(item) >= Number(item.quantity || 0);

      if (!this.isMultiExecutor) {
        return this.materials.length > 0 && this.materials.every(isFullyShipped)
            ? {executorCount: 1, currentUserOnly: true, willSendToAcceptance: true}
            : null;
      }

      const affectedExecutorIds = new Set(this.materials
          .filter(item => changedIds.has(Number(item.id)))
          .map(item => Number(this.getItemExecutorId(item)))
          .filter(id => Number.isInteger(id) && id > 0));
      const activeExecutorIds = new Set((this.executors || [])
          .filter(executor => executor && executor.status === 'active')
          .map(executor => Number(executor.user_id || executor.userId || executor.id))
          .filter(id => Number.isInteger(id) && id > 0));
      const completedExecutorIds = Array.from(affectedExecutorIds).filter(executorId => {
        if (!activeExecutorIds.has(executorId)) return false;
        const ownItems = this.materials.filter(item => Number(this.getItemExecutorId(item)) === executorId);
        return ownItems.length > 0 && ownItems.every(isFullyShipped);
      });

      if (!completedExecutorIds.length) return null;

      const remainingActiveExecutorIds = Array.from(activeExecutorIds)
          .filter(executorId => !completedExecutorIds.includes(executorId));
      return {
        executorCount: completedExecutorIds.length,
        completedExecutorIds,
        currentUserOnly: completedExecutorIds.length === 1 &&
            completedExecutorIds[0] === Number(this.currentUserId),
        willSendToAcceptance: remainingActiveExecutorIds.length === 0
      };
    },
    acceptanceWillCompleteRequest(acceptanceQuantityChangedItemIds, shippingCompletionPreview) {
      if (!acceptanceQuantityChangedItemIds.length) return false;

      const hasAnyShipment = this.materials.some(item => {
        const edited = this.editedItems[item.id];
        return Number(edited && edited.shipped_quantity !== undefined
            ? edited.shipped_quantity
            : item.shipped_quantity || 0) > 0;
      });
      if (!hasAnyShipment) return false;

      const allShippedAccepted = this.materials.every(item => {
        const edited = this.editedItems[item.id];
        const shipped = Number(edited && edited.shipped_quantity !== undefined
            ? edited.shipped_quantity
            : item.shipped_quantity || 0);
        const accepted = Number(edited && edited.accepted_quantity !== undefined
            ? edited.accepted_quantity
            : item.accepted_quantity || 0);
        return accepted >= shipped;
      });
      if (!allShippedAccepted) return false;

      if (!this.isMultiExecutor) return true;
      if (!(this.executors || []).length) return false;

      const completedByShipping = new Set(
          shippingCompletionPreview && shippingCompletionPreview.completedExecutorIds || []
      );
      return !(this.executors || []).some(executor => {
        const executorId = Number(executor && (executor.user_id || executor.userId || executor.id));
        return executor && executor.status === 'active' && !completedByShipping.has(executorId);
      });
    },
    getOperationSaveConfirmation(completionPreview, acceptanceWillComplete) {
      if (this.fulfillmentControlled && acceptanceWillComplete) return {
        message: 'Сохраним приёмку. Если поставка завершена и нет нерешённых вопросов, заявка закроется автоматически.',
        title: 'Сохранить приёмку?', okTitle: 'Сохранить'
      };
      if (this.fulfillmentControlled) return {
        message: this.isMechanicRepairMode
            ? 'Сохраним указанные количества. Затем укажите результат ремонта в карточке заявки. Если готово не всё, нужно будет решить, что делать дальше.'
            : 'Сохраним указанные количества. Затем завершите отгрузку или приёмку в карточке заявки. Если получили не всё, нужно будет решить, что делать дальше.',
        title: 'Сохранить количества?', okTitle: 'Сохранить'
      };
      if (acceptanceWillComplete) {
        return {
          message: completionPreview
              ? 'Всё отгруженное будет принято. Работа исполнителей будет завершена, а заявка закроется. Сохранить?'
              : 'Всё отгруженное будет принято, а заявка закроется. Сохранить?',
          title: 'Завершение заявки',
          okTitle: 'Сохранить и завершить заявку'
        };
      }

      if (!completionPreview) {
        return {
          message: 'Сохранить указанные количества отгрузки и приёмки?',
          title: 'Сохранение изменений',
          okTitle: 'Да, сохранить'
        };
      }

      if (!this.isMultiExecutor) {
        return {
          message: 'Все позиции будут полностью отгружены. После сохранения заявка автоматически перейдёт на приёмку. Продолжить?',
          title: 'Полная отгрузка заявки',
          okTitle: 'Сохранить и отправить на приёмку'
        };
      }

      const subject = completionPreview.currentUserOnly
          ? 'Вы отгрузили всё по своим позициям. Ваша работа по заявке будет завершена.'
          : `Всё отгрузили ${completionPreview.executorCount} исполнителей. Их работа по заявке будет завершена.`;
      const result = completionPreview.willSendToAcceptance
          ? 'Все исполнители закончили. Заявка перейдёт на приёмку.'
          : 'Заявка останется в работе у остальных исполнителей.';

      return {
        message: `${subject} ${result} Продолжить?`,
        title: 'Завершение работы по заявке',
        okTitle: completionPreview.willSendToAcceptance
            ? 'Сохранить и отправить на приёмку'
            : 'Сохранить и завершить свою часть'
      };
    },
    async saveChanges() {
      if (this.saving) return;
      if (!this.hasChanges) return;
      if (this.hasRequestChanges || this.savingRequest) {
        this.$emit('request-action-blocked', 'Сначала сохраните или отмените изменения заявки в нижней панели, затем сохраните отгрузку или приёмку.');
        return;
      }

      const {
        shippedChanges,
        acceptedChanges,
        shippingQuantityChangedItemIds,
        acceptanceQuantityChangedItemIds
      } = this.collectOperationChanges();
      const shippingCompletionPreview = this.getShippingCompletionPreview(
          shippingQuantityChangedItemIds
      );
      const confirmation = this.getOperationSaveConfirmation(
          shippingCompletionPreview,
          this.acceptanceWillCompleteRequest(
              acceptanceQuantityChangedItemIds,
              shippingCompletionPreview
          )
      );
      const confirmed = await this.$bvModal.msgBoxConfirm(confirmation.message, { contentClass: 'argus-glass-dialog',
        title: confirmation.title,
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'primary',
        okTitle: confirmation.okTitle,
        cancelTitle: 'Отмена',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      });

      if (!confirmed) return;

      if (this.hasRequestChanges || this.savingRequest) {
        this.$emit('request-action-blocked', 'Сначала сохраните или отмените изменения заявки в нижней панели.');
        return;
      }

      this.saving = true;
      try {

        // Отправляем изменения на сервер
        if (shippedChanges.length > 0) {
          await this.$store.dispatch('requests_bulkShipItems', {
            requestId: this.requestId,
            items: shippedChanges
          });
        }

        if (acceptedChanges.length > 0) {
          await this.$store.dispatch('requests_bulkAcceptItems', {
            requestId: this.requestId,
            items: acceptedChanges
          });
        }

        this.$emit('saved');
        this.isEditing = false;
        this.editedItems = {};
        this.originalItems = {};
        this.expandedOperationComments = {};

        this.$bvToast && this.$bvToast.toast('Изменения успешно сохранены', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        const errorMsg = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Не удалось сохранить изменения';
        this.$bvToast && this.$bvToast.toast(errorMsg, {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.saving = false;
      }
    },
    isCatalogEmployeeItem(item) {
      const catalogItemId = Number(item && item.catalog_item_id);
      const catalogType = (item && item.catalog_type) || this.catalogType;
      return Boolean(
          item &&
          catalogType === 'employees' &&
          Number.isInteger(catalogItemId) &&
          catalogItemId > 0 &&
          item.is_custom !== true
      );
    },
    getCatalogEmployeeLinkUser(item) {
      const catalogItemId = Number(item && item.catalog_item_id);
      return {
        user_id: catalogItemId,
        full_name: (item && item.item_name) || (catalogItemId ? `Сотрудник #${catalogItemId}` : '')
      };
    },
    getShippedPercent(item) {
      // Проверяем, что item существует
      if (!item) return 0;

      // Преобразуем значения в числа и проверяем на валидность
      const quantity = parseFloat(item.quantity);
      const shipped = parseFloat(item.shipped_quantity || 0);

      // Проверяем, что значения являются валидными числами
      if (isNaN(quantity) || isNaN(shipped)) return 0;

      // Проверяем, что quantity не равен нулю и не Infinity
      if (quantity === 0 || !isFinite(quantity)) return 0;

      // Проверяем, что shipped не Infinity
      if (!isFinite(shipped)) return 0;

      // Вычисляем процент и проверяем результат
      const percent = (shipped / quantity) * 100;
      if (isNaN(percent) || !isFinite(percent)) return 0;

      return Math.round(percent);
    },
    getAcceptedPercent(item) {
      // Проверяем, что item существует
      if (!item) return 0;

      // Преобразуем значения в числа и проверяем на валидность
      const shipped = parseFloat(item.shipped_quantity);
      const accepted = parseFloat(item.accepted_quantity || 0);

      // Проверяем, что значения являются валидными числами
      if (isNaN(shipped) || isNaN(accepted)) return 0;

      // Проверяем, что shipped_quantity не равен нулю и не Infinity
      if (shipped === 0 || !isFinite(shipped)) return 0;

      // Проверяем, что accepted не Infinity
      if (!isFinite(accepted)) return 0;

      // Вычисляем процент и проверяем результат
      const percent = (accepted / shipped) * 100;
      if (isNaN(percent) || !isFinite(percent)) return 0;

      return Math.round(percent);
    },
    getProgressVariant(percent) {
      if (percent === 0) return 'secondary';
      if (percent < 50) return 'danger';
      if (percent < 100) return 'warning';
      return 'success';
    },
    getQuantityClass(current, max) {
      if (!current || current === 0) return 'text-muted';
      if (!max || max === 0) return 'text-dark';
      const percent = (current / max) * 100;
      if (percent >= 100) return 'text-success font-weight-bold';
      if (percent >= 50) return 'text-warning';
      return 'text-danger';
    },
    getStatusVariant(item) {
      if (this.isEmployeeCatalog || this.isFuelCardCatalog) {
        if (this.requestStatus === 'completed') return 'success';
        if (this.requestStatus === 'awaiting_acceptance') return 'info';
        if (this.requestStatus === 'submitted' || this.requestStatus === 'in_review') return 'warning';
        if (this.requestStatus === 'rejected' || this.requestStatus === 'cancelled') return 'danger';
        return 'secondary';
      }

      const shippedPercent = this.getShippedPercent(item);
      const acceptedPercent = this.getAcceptedPercent(item);

      if (acceptedPercent === 100) return 'success';
      if (acceptedPercent > 100) return 'danger';
      if (shippedPercent === 100 && acceptedPercent > 0) return 'info';
      if (shippedPercent === 100) return 'warning';
      if (shippedPercent > 100) return 'danger';
      if (shippedPercent > 0) return 'primary';

      return 'secondary';
    },
    getStatusText(item) {
      if (this.isEmployeeCatalog || this.isFuelCardCatalog) {
        if (this.requestStatus === 'completed') return 'Подтверждено';
        if (this.requestStatus === 'awaiting_acceptance') return 'Ожидает подтверждения';
        if (this.requestStatus === 'submitted' || this.requestStatus === 'in_review') return 'На согласовании';
        if (this.requestStatus === 'rejected') return 'Отклонено';
        if (this.requestStatus === 'cancelled') return 'Отменено';
        return 'Подготовка';
      }

      const shippedPercent = this.getShippedPercent(item);
      const acceptedPercent = this.getAcceptedPercent(item);

      if (acceptedPercent === 100) return '✅ Принято';
      if (acceptedPercent > 100) return '❌ Принято больше, чем отгружено';
      if (shippedPercent === 100 && acceptedPercent > 0) return '⏳ На приемке';
      if (shippedPercent === 100) return '📦 Отгружено';
      if (shippedPercent > 100) return '❌ Отгружено больше, чем заказано';
      if (shippedPercent > 0) return '⚠️ Частично';
      return '❌ Не начато';
    },
    formatNumber(value) {
      if (value === null || value === undefined) return '0';
      return parseFloat(value).toFixed(3).replace(/\.?0+$/, '');
    },
    normalizeWholeNumberInput(value) {
      const parsed = Number(value);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return 0;
      }
      return Math.floor(parsed);
    },
    getMechanicRepairAcceptedInputValue(item) {
      return this.normalizeWholeNumberInput(this.getEditedItem(item).accepted_quantity);
    },
    getMechanicRepairAcceptedInputMax(item) {
      const edited = this.getEditedItem(item);
      const max = edited.shipped_quantity || item.shipped_quantity || item.quantity;
      return Math.max(0, this.normalizeWholeNumberInput(max));
    },
    normalizeCommentValue(value) {
      return String(value || '').trim();
    },
    operationCommentKey(item, type) {
      return `${type}-${item && item.id ? item.id : 'new'}`;
    },
    isOperationCommentOpen(item, type) {
      return !!this.expandedOperationComments[this.operationCommentKey(item, type)];
    },
    toggleOperationComment(item, type) {
      const key = this.operationCommentKey(item, type);
      this.$set(this.expandedOperationComments, key, !this.expandedOperationComments[key]);
    },
    getUserId(user) {
      return user ? (user.user_id || user.userId || user.id) : null;
    },
    isActiveHumanUser(user) {
      if (!user) {
        return false;
      }
      const active = user.active;
      const isActive = active === true || active === 1 || active === '1' || active === 'true';
      const isBot = user.isBot === true || user.isBot === 1 || user.isBot === '1' || user.isBot === 'true';
      return isActive && !isBot;
    },
    getFuelCardRecipientOptions(item) {
      const options = this.fuelCardRecipientOptions.slice();
      const currentUserId = Number(
          (item && item.target_user_id) ||
          (item && item.target_user && this.getUserId(item.target_user)) ||
          0
      );
      if (currentUserId && !options.some(option => Number(option.value) === currentUserId)) {
        options.push({
          value: currentUserId,
          text: item && item.target_user ? this.formatUserName(item.target_user) : `Пользователь #${currentUserId}`
        });
      }
      return options;
    },
    getMechanicAssetUserOptions(item) {
      const options = this.mechanicAssetUserOptions.slice();
      const edited = this.isEditingList && item ? this.getEditedItem(item) : null;
      const currentUserId = Number(
          (edited && edited.target_user_id) ||
          (item && item.target_user_id) ||
          (item && item.target_user && this.getUserId(item.target_user)) ||
          0
      );
      const currentUser = item && item.target_user ? item.target_user : null;
      if (
          currentUserId &&
          currentUser &&
          this.isActiveHumanUser(currentUser) &&
          !options.some(option => Number(option.value) === currentUserId)
      ) {
        options.push({
          value: currentUserId,
          text: this.formatUserName(currentUser)
        });
      }
      return options;
    },
    sanitizeFuelLimits(value) {
      return sanitizeFuelLimitsValue(value);
    },
    formatFuelLimits(value) {
      return formatFuelLimitsValue(value, this.fuelTypeById, this.formatNumber);
    },
    cloneFuelLimits(value) {
      return cloneFuelLimitsValue(value);
    },
    normalizeFuelLimitsComparable(value) {
      return JSON.stringify(this.sanitizeFuelLimits(value));
    },
    getFuelCardLimitsValue(item) {
      if (this.isEditingList) {
        return this.getEditedItem(item).fuel_limits || [];
      }
      return item && item.fuel_limits ? item.fuel_limits : [];
    },
    setFuelLimitType(limit, value) {
      setFuelLimitTypeValue(limit, value, (target, key, normalizedValue) => this.$set(target, key, normalizedValue));
    },
    setFuelLimitValue(limit, value) {
      setFuelLimitAmountValue(limit, value, (target, key, normalizedValue) => this.$set(target, key, normalizedValue));
    },
    getFuelLimitDraftByIndex(index) {
      return Array.isArray(this.fuelCardLimitsDraft)
          ? (this.fuelCardLimitsDraft[index] || null)
          : null;
    },
    syncFuelLimitDraftControls() {
      if (typeof document === 'undefined') {
        return;
      }

      const modalSelector = `[data-fuel-limit-modal-id="${this._uid}"]`;
      syncFuelLimitControls({
        root: document,
        typeSelector: `${modalSelector}[data-fuel-limit-draft-type]`,
        valueSelector: `${modalSelector}[data-fuel-limit-draft-value]`,
        getLimit: control => {
          const index = Number(control.getAttribute('data-fuel-limit-draft-index'));
          return this.getFuelLimitDraftByIndex(index);
        },
        setter: (target, key, value) => this.$set(target, key, value)
      });
    },
    async flushPendingInputChanges() {
      this.syncFuelLimitDraftControls();
      if (typeof document !== 'undefined' && document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }
      await this.$nextTick();
      await this.$nextTick();
      this.syncFuelLimitDraftControls();
    },
    openFuelCardLimitsModal(item) {
      if (!item || !(item.id || item._tempId) || !this.canEditFuelCardLimits) {
        return;
      }
      this.fuelCardLimitsTarget = item;
      const currentLimits = this.cloneFuelLimits(this.getFuelCardLimitsValue(item));
      this.fuelCardLimitsDraft = currentLimits.length
          ? currentLimits
          : [{ fuel_type_id: null, value: null }];
      this.showFuelCardLimitsModal = true;
    },
    resetFuelCardLimitsModal() {
      this.fuelCardLimitsTarget = null;
      this.fuelCardLimitsDraft = [];
      this.fuelCardLimitsBusy = false;
    },
    addFuelCardLimitDraftRow() {
      this.fuelCardLimitsDraft.push({ fuel_type_id: null, value: null });
    },
    removeFuelCardLimitDraftRow(index) {
      this.fuelCardLimitsDraft.splice(index, 1);
    },
    async saveFuelCardLimits() {
      if (!this.canSaveFuelCardLimits) {
        return;
      }

      await this.flushPendingInputChanges();
      this.fuelCardLimitsBusy = true;
      try {
        if (this.isEditingList) {
          const key = this.fuelCardLimitsTarget.id || this.fuelCardLimitsTarget._tempId;
          const limits = this.cloneFuelLimits(this.sanitizedFuelCardLimitsDraft);
          const edited = this.getEditedItem(this.fuelCardLimitsTarget);
          this.$set(edited, 'fuel_limits', limits);

          const localMaterial = this.localMaterials.find(material =>
              (material.id && material.id === this.fuelCardLimitsTarget.id) ||
              (material._tempId && material._tempId === this.fuelCardLimitsTarget._tempId)
          );
          if (localMaterial) {
            this.$set(localMaterial, 'fuel_limits', limits);
          }
          if (this.editedListItems[key]) {
            this.$set(this.editedListItems[key], 'fuel_limits', limits);
          }
          this.showFuelCardLimitsModal = false;
          this.$emit('materials-list-changed', this.getMaterialsChanges());
          this.$bvToast && this.$bvToast.toast('Суточные лимиты обновлены', {
            title: 'Успех',
            variant: 'success',
            solid: true
          });
          return;
        }

        await this.$store.dispatch('requestCard/requestCard_updateFuelCardLimitsForItem', {
          itemId: this.fuelCardLimitsTarget.id,
          fuelLimits: this.sanitizedFuelCardLimitsDraft
        });
        this.showFuelCardLimitsModal = false;
        this.$bvToast && this.$bvToast.toast('Суточные лимиты обновлены', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Не удалось сохранить суточные лимиты';
        this.$bvToast && this.$bvToast.toast(message, {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.fuelCardLimitsBusy = false;
      }
    },
    formatUserName(user) {
      if (!user) return '';
      return `${user.last_name || ''} ${user.first_name || ''}`.trim()
          || user.full_name
          || `Пользователь #${user.user_id || user.id}`;
    },
    getFuelCardSelectionValue(item) {
      if (!item) return null;
      const busyValue = this.fuelCardSelectionBusy[item.id];
      if (busyValue === true) {
        return item.catalog_item_id || null;
      }
      return item.catalog_item_id || null;
    },
    getFuelCardTargetObjectValue(item) {
      if (!item) return null;
      const busyValue = this.fuelCardTargetObjectBusy[item.id];
      if (busyValue === true) {
        return item.target_building_process_id || null;
      }
      return item.target_building_process_id || null;
    },
    getMechanicTargetObjectName(item) {
      const targetId = this.normalizeSelectId(item && item.target_building_process_id);
      if (!targetId) {
        return '';
      }
      const process = item && item.target_building_process;
      const processName = process && (process.object_name || process.name || process.text);
      if (processName) {
        return processName;
      }
      const option = this.mechanicAssetObjectOptions.find(row => Number(row.value) === targetId);
      return option && option.text ? option.text : `Объект #${targetId}`;
    },
    normalizeSelectId(value) {
      const parsed = Number(value);
      return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    },
    clonePlainObject(value) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return {};
      }
      return JSON.parse(JSON.stringify(value));
    },
    getMechanicActionResult(item) {
      const edited = this.isEditingList && item ? this.getEditedItem(item) : null;
      const value = (edited && edited.mechanic_action_result) ||
          (item && item.mechanic_action_result) ||
          {};
      return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    },
    getMechanicRepairScenario(item) {
      const result = this.getMechanicActionResult(item);
      const scenario = String(result.repair_scenario || result.repairScenario || '').trim();
      return scenario === MECHANIC_REPAIR_SCENARIO_SPECIALIST_ON_OBJECT
          ? MECHANIC_REPAIR_SCENARIO_SPECIALIST_ON_OBJECT
          : MECHANIC_REPAIR_SCENARIO_SELF_ON_OBJECT;
    },
    getMechanicRepairScenarioLabel(item) {
      return this.getMechanicRepairScenario(item) === MECHANIC_REPAIR_SCENARIO_SPECIALIST_ON_OBJECT
          ? 'Выезд специалиста на объект'
          : 'Самостоятельный ремонт на объекте';
    },
    getDefaultMechanicReturnObjectId(item) {
      const canPrepareRepairReturn = this.isMechanicRepairCompletionMode ||
          (this.isMechanicRepairMode && this.canManageMechanicAssetSelection);
      if (!canPrepareRepairReturn || !this.mechanicAssetShowsReturnObject || !item) {
        return null;
      }
      const source = this.isEditingList ? this.getEditedItem(item) : item;
      if (this.normalizeSelectId(source && source.target_warehouse_id)) {
        return null;
      }
      return this.requestBuildingProcessIdValue;
    },
    getMechanicTargetWarehouseValue(item) {
      const source = this.isEditingList && item ? this.getEditedItem(item) : item;
      const targetWarehouseId = this.normalizeSelectId(source && source.target_warehouse_id);
      if (!this.isMechanicRepairMode || targetWarehouseId) {
        return targetWarehouseId;
      }
      return this.getMechanicRepairScenario(item);
    },
    setMechanicTargetWarehouse(item, value) {
      if (!this.isMechanicRepairMode) {
        this.setEditedField(item, 'target_warehouse_id', this.normalizeSelectId(value));
        return;
      }
      this.setMechanicRepairScenario(item, value);
    },
    setMechanicRepairScenario(item, value, options = {}) {
      const targetWarehouseId = this.normalizeSelectId(value);
      const scenario = value === MECHANIC_REPAIR_SCENARIO_SPECIALIST_ON_OBJECT
          ? MECHANIC_REPAIR_SCENARIO_SPECIALIST_ON_OBJECT
          : MECHANIC_REPAIR_SCENARIO_SELF_ON_OBJECT;
      const edited = this.getEditedItem(item);
      const applyToRecord = record => {
        if (!record) {
          return;
        }
        this.$set(record, 'target_warehouse_id', targetWarehouseId);
        if (targetWarehouseId) {
          this.$set(record, 'target_building_process_id', null);
        } else if (!this.normalizeSelectId(record.target_building_process_id)) {
          const defaultReturnObjectId = this.getDefaultMechanicReturnObjectId(record);
          if (defaultReturnObjectId) {
            this.$set(record, 'target_building_process_id', defaultReturnObjectId);
          }
        }

        const actionResult = this.clonePlainObject(record.mechanic_action_result);
        delete actionResult.repairScenario;
        if (targetWarehouseId || scenario === MECHANIC_REPAIR_SCENARIO_SELF_ON_OBJECT) {
          delete actionResult.repair_scenario;
        } else {
          actionResult.repair_scenario = scenario;
        }
        this.$set(record, 'mechanic_action_result', actionResult);

        if (Array.isArray(record.mechanic_fulfillments)) {
          record.mechanic_fulfillments.forEach(fulfillment => {
            this.$set(fulfillment, 'target_warehouse_id', targetWarehouseId);
            if (targetWarehouseId) {
              this.$set(fulfillment, 'target_building_process_id', null);
            } else if (!this.normalizeSelectId(fulfillment.target_building_process_id)) {
              const defaultReturnObjectId = this.getDefaultMechanicReturnObjectId(record);
              if (defaultReturnObjectId) {
                this.$set(fulfillment, 'target_building_process_id', defaultReturnObjectId);
              }
            }
          });
        }
      };

      applyToRecord(edited);

      const localMaterial = this.localMaterials.find(material =>
          (material.id && material.id === item.id) ||
          (material._tempId && material._tempId === item._tempId)
      );
      if (localMaterial) {
        applyToRecord(localMaterial);
        if (Array.isArray(edited.mechanic_fulfillments)) {
          this.$set(localMaterial, 'mechanic_fulfillments', this.cloneMechanicFulfillments(edited.mechanic_fulfillments));
        }
      }

      if (options.silent !== true) {
        this.onMaterialFieldChange();
      }
    },
    getMechanicSelectionMeta(item) {
      const result = this.getMechanicActionResult(item);
      const meta = result.mechanic_selection || {};
      return meta && typeof meta === 'object' && !Array.isArray(meta) ? meta : {};
    },
    getMechanicRequestedCatalogItemId(item) {
      if (!this.isMechanicAssetCatalog || !item) {
        return null;
      }
      const edited = this.isEditingList ? this.getEditedItem(item) : null;
      const meta = this.getMechanicSelectionMeta(item);
      return this.normalizeSelectId(
          meta.requested_catalog_item_id ||
          (edited && edited._requestedCatalogItemId) ||
          item._requestedCatalogItemId ||
          item.catalog_item_id
      );
    },
    isMechanicSelectionExplicitlyConfirmed(item) {
      const meta = this.getMechanicSelectionMeta(item);
      return meta.confirmed === true;
    },
    isMechanicSelectionExplicitlyRejected(item) {
      const meta = this.getMechanicSelectionMeta(item);
      return meta.confirmed === false;
    },
    isMechanicStoredShipmentSelectionConfirmed(item) {
      if (!this.isMechanicAssetCatalog || !item) {
        return false;
      }
      const result = item.mechanic_action_result &&
      typeof item.mechanic_action_result === 'object' &&
      !Array.isArray(item.mechanic_action_result)
          ? item.mechanic_action_result
          : {};
      const meta = result.mechanic_selection &&
      typeof result.mechanic_selection === 'object' &&
      !Array.isArray(result.mechanic_selection)
          ? result.mechanic_selection
          : {};
      if (meta.confirmed === true) {
        return true;
      }
      if (meta.confirmed === false) {
        return false;
      }
      const shipped = Number(item.shipped_quantity);
      const accepted = Number(item.accepted_quantity);
      if ((Number.isFinite(shipped) && shipped > 0) || (Number.isFinite(accepted) && accepted > 0)) {
        return Boolean(this.normalizeSelectId(item.catalog_item_id));
      }
      return false;
    },
    isMechanicShipmentSelectionConfirmed(item) {
      if (!this.isMechanicAssetCatalog || !item) {
        return false;
      }
      if (this.isMechanicSelectionExplicitlyConfirmed(item)) {
        return true;
      }
      if (this.isMechanicSelectionExplicitlyRejected(item)) {
        return false;
      }
      const edited = this.isEditingList ? this.getEditedItem(item) : null;
      if (edited && edited._mechanicSelectionTouched === true) {
        return Boolean(this.normalizeSelectId(edited.catalog_item_id));
      }
      const shipped = Number((edited && edited.shipped_quantity !== undefined)
          ? edited.shipped_quantity
          : item.shipped_quantity);
      const accepted = Number((edited && edited.accepted_quantity !== undefined)
          ? edited.accepted_quantity
          : item.accepted_quantity);
      if ((Number.isFinite(shipped) && shipped > 0) || (Number.isFinite(accepted) && accepted > 0)) {
        return Boolean(this.normalizeSelectId((edited && edited.catalog_item_id) || item.catalog_item_id));
      }
      return false;
    },
    hasMechanicShipmentSelection(item) {
      if (this.getEditedMechanicAssetId(item)) {
        return true;
      }
      const edited = this.isEditingList ? this.getEditedItem(item) : null;
      const fulfillments = Array.isArray((edited && edited.mechanic_fulfillments) || item.mechanic_fulfillments)
          ? ((edited && edited.mechanic_fulfillments) || item.mechanic_fulfillments)
          : [];
      return fulfillments.some(row => this.normalizeSelectId(row.asset_unit_id || row.catalog_item_id));
    },
    hasMechanicPendingRequestedAsset(item) {
      return this.isMechanicShipmentMode &&
          this.isMechanicAssetCatalog &&
          Boolean(this.getMechanicRequestedCatalogItemId(item)) &&
          !this.hasMechanicShipmentSelection(item) &&
          !this.isMechanicShipmentSelectionConfirmed(item);
    },
    getMechanicRequestedAssetOption(item) {
      const requestedId = this.getMechanicRequestedCatalogItemId(item);
      return requestedId ? this.findMechanicAssetOption(requestedId) : null;
    },
    getMechanicRequestedAssetName(item) {
      const option = this.getMechanicRequestedAssetOption(item);
      if (option && option.text) {
        return option.text;
      }
      return (item && (item.mechanic_asset_name || item.asset_name || item.item_name)) ||
          (this.getMechanicRequestedCatalogItemId(item) ? `Техника #${this.getMechanicRequestedCatalogItemId(item)}` : 'Техника');
    },
    getMechanicRequestedAssetDetails(item) {
      const option = this.getMechanicRequestedAssetOption(item);
      const details = [];
      if (option && option.inventory_number) {
        details.push(`инв. ${option.inventory_number}`);
      } else if (item && item.inventory_number) {
        details.push(`инв. ${item.inventory_number}`);
      }
      if (option && option.catalog_name) {
        details.push(`группа: ${option.catalog_name}`);
      } else if (item && item.catalog_name) {
        details.push(`группа: ${item.catalog_name}`);
      }
      return details.join(' / ');
    },
    mechanicAssetSelectPlaceholder(item) {
      if (this.hasMechanicPendingRequestedAsset(item)) {
        return 'Выберите другую технику или подтвердите предложенную';
      }
      return this.isMechanicSelectionRequired(item)
          ? 'Выберите конкретную технику'
          : 'Сопоставить со справочником';
    },
    getMechanicShipmentSelectionBadgeText(item) {
      if (!this.isMechanicShipmentMode || !this.getEditedMechanicAssetId(item)) {
        return '';
      }
      const requestedId = this.getMechanicRequestedCatalogItemId(item);
      const selectedId = this.getEditedMechanicAssetId(item);
      if (requestedId && selectedId && requestedId !== selectedId) {
        return 'Замена';
      }
      return 'Выбрано исполнителем';
    },
    getMechanicShipmentSelectionBadgeVariant(item) {
      const requestedId = this.getMechanicRequestedCatalogItemId(item);
      const selectedId = this.getEditedMechanicAssetId(item);
      return requestedId && selectedId && requestedId !== selectedId ? 'warning' : 'success';
    },
    isMechanicQuantityRequest(item) {
      if (!this.isMechanicAssetCatalog || !item) {
        return false;
      }
      const edited = this.isEditingList ? this.getEditedItem(item) : item;
      if (item.is_custom === true || item.isCustom === true || (edited && edited.isCustom === true)) {
        return true;
      }
      const accountingType = edited.accounting_type || item.accounting_type;
      if (accountingType === 'quantity') {
        return true;
      }
      if (this.normalizeSelectId(edited.catalog_item_id || item.catalog_item_id)) {
        return false;
      }
      const quantity = Number(edited.quantity === undefined ? item.quantity : edited.quantity);
      const unit = String(edited.unit || item.unit || '').trim().toLowerCase();
      return (Number.isFinite(quantity) && quantity !== 1) ||
          (unit && unit !== 'ед.' && unit !== 'ед');
    },
    isMechanicManualEditable(item) {
      if (!this.isMechanicAssetCatalog || !item) {
        return false;
      }
      const edited = this.isEditingList ? this.getEditedItem(item) : item;
      return item.is_custom === true || item.isCustom === true || (edited && edited.isCustom === true);
    },
    cloneMechanicFulfillments(value) {
      return Array.isArray(value)
          ? value.map((row, index) => ({
            id: row.id || null,
            _key: row._key || `fulfillment-${row.id || 'new'}-${index}-${Date.now()}`,
            asset_unit_id: this.normalizeSelectId(row.asset_unit_id || row.catalog_item_id),
            catalog_item_id: this.normalizeSelectId(row.asset_unit_id || row.catalog_item_id),
            quantity: Number(row.quantity || 1) > 0 ? Number(row.quantity || 1) : 1,
            shipped_quantity: Number(row.shipped_quantity || 0) > 0 ? Number(row.shipped_quantity || 0) : 0,
            accepted_quantity: Number(row.accepted_quantity || 0) > 0 ? Number(row.accepted_quantity || 0) : 0,
            target_user_id: this.normalizeSelectId(row.target_user_id),
            target_building_process_id: this.normalizeSelectId(row.target_building_process_id),
            target_warehouse_id: this.normalizeSelectId(row.target_warehouse_id),
            meter_type: row.meter_type || null,
            meter_value: row.meter_value === undefined ? null : row.meter_value,
            condition: row.condition || null,
            mechanic_action_result: row.mechanic_action_result || {},
            rental_counterparty: row.rental_counterparty || null,
            rental_operator_user_id: this.normalizeSelectId(row.rental_operator_user_id),
            rental_expected_return_at: row.rental_expected_return_at || null,
            shipping_comment: row.shipping_comment || null,
            acceptance_comment: row.acceptance_comment || null,
            mechanic_asset_name: row.mechanic_asset_name || null,
            asset_name: row.asset_name || null,
            catalog_name: row.catalog_name || null,
            accounting_type: row.accounting_type || null,
            unit_of_measurement: row.unit_of_measurement || row.unit || null,
            inventory_number: row.inventory_number || null,
            status: row.status || null,
            status_label: row.status_label || null,
            assignment_details: row.assignment_details || null,
            active_transfer: row.active_transfer || null,
            target_user: row.target_user || null
          }))
          : [];
    },
    normalizeMechanicFulfillmentsComparable(value) {
      return JSON.stringify(this.cloneMechanicFulfillments(value).map(row => ({
        id: row.id || null,
        asset_unit_id: row.asset_unit_id || null,
        quantity: Number(row.quantity || 0),
        shipped_quantity: Number(row.shipped_quantity || 0),
        accepted_quantity: Number(row.accepted_quantity || 0),
        target_user_id: row.target_user_id || null,
        target_building_process_id: row.target_building_process_id || null,
        target_warehouse_id: row.target_warehouse_id || null,
        meter_type: row.meter_type || null,
        meter_value: row.meter_value === undefined ? null : row.meter_value,
        condition: row.condition || null,
        rental_counterparty: row.rental_counterparty || null,
        rental_operator_user_id: row.rental_operator_user_id || null,
        rental_expected_return_at: row.rental_expected_return_at || null
      })));
    },
    shouldUseMechanicFulfillments(item) {
      return this.isMechanicShipmentMode && this.isMechanicAssetCatalog && Boolean(item);
    },
    shouldShowMechanicMainAssetSelect(item) {
      return this.isMechanicShipmentMode && this.isMechanicAssetCatalog && Boolean(item);
    },
    getEditedMechanicFulfillments(item) {
      const edited = this.getEditedItem(item);
      if (!Array.isArray(edited.mechanic_fulfillments)) {
        this.$set(edited, 'mechanic_fulfillments', this.cloneMechanicFulfillments(item.mechanic_fulfillments));
      }
      return edited.mechanic_fulfillments;
    },
    getMechanicMainShipmentSelectedCount(item) {
      return this.shouldUseMechanicFulfillments(item) && this.getEditedMechanicAssetId(item) ? 1 : 0;
    },
    isMechanicAssetOptionQuantity(assetId) {
      const option = this.findMechanicAssetOption(assetId);
      return option && option.accounting_type === 'quantity';
    },
    isMechanicMainShipmentQuantityAsset(item) {
      return this.shouldUseMechanicFulfillments(item) &&
          this.isMechanicAssetOptionQuantity(this.getEditedMechanicAssetId(item));
    },
    getMechanicMainShipmentQuantity(item) {
      const assetId = this.getEditedMechanicAssetId(item);
      if (!this.shouldUseMechanicFulfillments(item) || !assetId) {
        return 0;
      }
      if (this.isMechanicMainShipmentQuantityAsset(item)) {
        const requested = this.getRequestedQuantity(item);
        const childShipped = this.getMechanicFulfillmentChildShippedCount(item);
        return Math.max(0, requested - childShipped);
      }
      return 1;
    },
    getMechanicFulfillmentSelectedCount(item) {
      const childCount = this.getEditedMechanicFulfillments(item)
          .filter(row => this.normalizeSelectId(row.asset_unit_id || row.catalog_item_id))
          .length;
      return childCount + this.getMechanicMainShipmentSelectedCount(item);
    },
    getMechanicFulfillmentRowsSummary(item) {
      const selected = this.getMechanicFulfillmentSelectedCount(item);
      const requested = this.getRequestedQuantity(item);
      const requestedText = Number(requested) > 0
          ? ` / запрошено: ${this.formatNumber(requested)}`
          : '';
      return `Строк отгрузки: ${selected}${requestedText}`;
    },
    getMechanicFulfillmentChildShippedCount(item) {
      return this.getEditedMechanicFulfillments(item)
          .reduce((sum, row) => sum + (Number(row.shipped_quantity || 0) > 0 ? Number(row.shipped_quantity || 0) : 0), 0);
    },
    getMechanicFulfillmentInventoryShippedCount(item) {
      return this.getEditedMechanicFulfillments(item)
          .filter(row => !this.isMechanicFulfillmentQuantity(row))
          .reduce((sum, row) => sum + (Number(row.shipped_quantity || 0) > 0 ? Number(row.shipped_quantity || 0) : 0), 0) +
          (this.getMechanicMainShipmentSelectedCount(item) > 0 && !this.isMechanicMainShipmentQuantityAsset(item) ? 1 : 0);
    },
    getMechanicQuantityFulfillmentRows(item) {
      return this.getEditedMechanicFulfillments(item)
          .filter(row => this.normalizeSelectId(row.asset_unit_id || row.catalog_item_id))
          .filter(row => this.isMechanicFulfillmentQuantity(row));
    },
    hasMechanicQuantityShipmentSelection(item) {
      return this.shouldUseMechanicFulfillments(item) &&
          (this.isMechanicMainShipmentQuantityAsset(item) ||
              this.getMechanicQuantityFulfillmentRows(item).length > 0);
    },
    shouldEditMechanicFulfillmentShippedQuantity(item) {
      return this.isMechanicShipmentMode &&
          this.shouldUseMechanicFulfillments(item) &&
          this.hasMechanicQuantityShipmentSelection(item);
    },
    shouldShowMechanicFulfillmentAutoShipment(item) {
      return this.isMechanicAssetCatalog &&
          this.isMechanicShipmentMode &&
          this.shouldUseMechanicFulfillments(item) &&
          this.hasMechanicFulfillmentRows(item) &&
          !this.shouldEditMechanicFulfillmentShippedQuantity(item);
    },
    getMechanicFulfillmentShippedCount(item) {
      if (this.shouldEditMechanicFulfillmentShippedQuantity(item)) {
        return this.getMechanicShipmentQuantityEditValue(item);
      }
      return this.getMechanicFulfillmentChildShippedCount(item) + this.getMechanicMainShipmentQuantity(item);
    },
    hasMechanicFulfillmentRows(item) {
      return this.getEditedMechanicFulfillments(item).length > 0 || this.getMechanicMainShipmentQuantity(item) > 0;
    },
    syncMechanicFulfillmentParentQuantity(item) {
      const edited = this.getEditedItem(item);
      const shipped = this.getMechanicFulfillmentShippedCount(item);
      this.$set(edited, 'shipped_quantity', shipped);
      const localMaterial = this.localMaterials.find(material =>
          (material.id && material.id === item.id) ||
          (material._tempId && material._tempId === item._tempId)
      );
      if (localMaterial) {
        this.$set(localMaterial, 'shipped_quantity', shipped);
        this.$set(localMaterial, 'mechanic_fulfillments', this.cloneMechanicFulfillments(edited.mechanic_fulfillments));
      }
    },
    getMechanicShipmentSelectionItems() {
      if (this.isEditingList && Array.isArray(this.localMaterials)) {
        return this.localMaterials;
      }
      return Array.isArray(this.materials) ? this.materials : [];
    },
    getMechanicAssetUsageMarkerId(item) {
      if (!item) {
        return null;
      }
      return this.getEditedMechanicAssetId(item) ||
          this.getMechanicRequestedCatalogItemId(item) ||
          this.normalizeSelectId(item.catalog_item_id || item.materialDirectoryId);
    },
    isSameMechanicShipmentItem(left, right) {
      if (!left || !right) {
        return false;
      }
      if (left === right) {
        return true;
      }
      if (left.id && right.id && Number(left.id) === Number(right.id)) {
        return true;
      }
      return Boolean(left._tempId && right._tempId && left._tempId === right._tempId);
    },
    getMechanicShipmentUsedAssetIds(options = {}) {
      const currentItem = options.currentItem || null;
      const currentFulfillment = options.currentFulfillment || null;
      const ignoreCurrentItemMain = options.ignoreCurrentItemMain === true;
      const usedIds = new Set();

      this.getMechanicShipmentSelectionItems().forEach(row => {
        const sameItem = this.isSameMechanicShipmentItem(row, currentItem);
        const mainAssetId = this.getMechanicAssetUsageMarkerId(row);

        if (mainAssetId && !(sameItem && ignoreCurrentItemMain)) {
          usedIds.add(mainAssetId);
        }

        const edited = this.isEditingList ? this.getEditedItem(row) : row;
        const fulfillments = Array.isArray(edited && edited.mechanic_fulfillments)
            ? edited.mechanic_fulfillments
            : [];
        fulfillments.forEach(fulfillment => {
          if (fulfillment === currentFulfillment) {
            return;
          }
          const fulfillmentAssetId = this.normalizeSelectId(fulfillment.asset_unit_id || fulfillment.catalog_item_id);
          if (fulfillmentAssetId) {
            usedIds.add(fulfillmentAssetId);
          }
        });
      });

      return usedIds;
    },
    getMechanicShipmentSameRowUsedAssetIds(options = {}) {
      const currentItem = options.currentItem || null;
      const currentFulfillment = options.currentFulfillment || null;
      const ignoreCurrentItemMain = options.ignoreCurrentItemMain === true;
      const usedIds = new Set();
      if (!currentItem) {
        return usedIds;
      }

      const mainAssetId = this.getEditedMechanicAssetId(currentItem);
      if (mainAssetId && !ignoreCurrentItemMain) {
        usedIds.add(mainAssetId);
      }

      const edited = this.isEditingList ? this.getEditedItem(currentItem) : currentItem;
      const fulfillments = Array.isArray(edited && edited.mechanic_fulfillments)
          ? edited.mechanic_fulfillments
          : [];
      fulfillments.forEach(fulfillment => {
        if (fulfillment === currentFulfillment) {
          return;
        }
        const fulfillmentAssetId = this.normalizeSelectId(fulfillment.asset_unit_id || fulfillment.catalog_item_id);
        if (fulfillmentAssetId) {
          usedIds.add(fulfillmentAssetId);
        }
      });

      return usedIds;
    },
    getMechanicShipmentOtherRowUsedAssetIds(options = {}) {
      const currentItem = options.currentItem || null;
      const usedIds = new Set();

      this.getMechanicShipmentSelectionItems().forEach(row => {
        if (this.isSameMechanicShipmentItem(row, currentItem)) {
          return;
        }

        const mainAssetId = this.getMechanicAssetUsageMarkerId(row);
        if (mainAssetId) {
          usedIds.add(mainAssetId);
        }

        const edited = this.isEditingList ? this.getEditedItem(row) : row;
        const fulfillments = Array.isArray(edited && edited.mechanic_fulfillments)
            ? edited.mechanic_fulfillments
            : [];
        fulfillments.forEach(fulfillment => {
          const fulfillmentAssetId = this.normalizeSelectId(fulfillment.asset_unit_id || fulfillment.catalog_item_id);
          if (fulfillmentAssetId) {
            usedIds.add(fulfillmentAssetId);
          }
        });
      });

      return usedIds;
    },
    getMechanicAssetUsageCount(assetId) {
      const normalizedId = this.normalizeSelectId(assetId);
      if (!normalizedId) {
        return 0;
      }

      let count = 0;
      this.getMechanicShipmentSelectionItems().forEach(row => {
        if (this.getMechanicAssetUsageMarkerId(row) === normalizedId) {
          count += 1;
        }

        const edited = this.isEditingList ? this.getEditedItem(row) : row;
        const fulfillments = Array.isArray(edited && edited.mechanic_fulfillments)
            ? edited.mechanic_fulfillments
            : [];
        fulfillments.forEach(fulfillment => {
          const fulfillmentAssetId = this.normalizeSelectId(fulfillment.asset_unit_id || fulfillment.catalog_item_id);
          if (fulfillmentAssetId === normalizedId) {
            count += 1;
          }
        });
      });

      return count;
    },
    isMechanicAssetUsedMoreThanOnce(item) {
      if (!this.isMechanicAssetCatalog || !item) {
        return false;
      }
      return this.getMechanicAssetUsageCount(this.getMechanicAssetUsageMarkerId(item)) > 1;
    },
    isMechanicFulfillmentAssetUsedMoreThanOnce(fulfillment) {
      if (!this.isMechanicAssetCatalog || !fulfillment) {
        return false;
      }
      const assetId = this.normalizeSelectId(fulfillment.asset_unit_id || fulfillment.catalog_item_id);
      return this.getMechanicAssetUsageCount(assetId) > 1;
    },
    getMechanicInventoryAssetOptionsForFulfillment(item, fulfillment) {
      const currentId = this.normalizeSelectId(fulfillment && (fulfillment.asset_unit_id || fulfillment.catalog_item_id));
      const selectedIds = this.getMechanicShipmentSameRowUsedAssetIds({
        currentItem: item,
        currentFulfillment: fulfillment,
        ignoreCurrentItemMain: false
      });
      return this.mechanicAssetOptions.filter(option => {
        const optionId = Number(option.value);
        return optionId === currentId || !selectedIds.has(optionId);
      });
    },
    getMechanicFulfillmentOption(fulfillment) {
      if (!fulfillment) {
        return null;
      }
      return this.findMechanicAssetOption(fulfillment.asset_unit_id || fulfillment.catalog_item_id);
    },
    getMechanicFulfillmentAssetId(fulfillment) {
      return this.normalizeSelectId(fulfillment && (fulfillment.asset_unit_id || fulfillment.catalog_item_id));
    },
    hasMechanicFulfillments(item) {
      return this.isMechanicAssetCatalog &&
          item &&
          Array.isArray(item.mechanic_fulfillments) &&
          item.mechanic_fulfillments.length > 0;
    },
    shouldShowMechanicReadonlyMainFulfillment(item) {
      if (!this.hasMechanicFulfillments(item)) {
        return false;
      }
      const mainAssetId = this.getEditedMechanicAssetId(item);
      if (!mainAssetId) {
        return false;
      }
      return !item.mechanic_fulfillments.some(fulfillment => {
        const fulfillmentAssetId = this.normalizeSelectId(fulfillment.asset_unit_id || fulfillment.catalog_item_id);
        return fulfillmentAssetId && fulfillmentAssetId === mainAssetId;
      });
    },
    getMechanicFulfillmentDisplayName(fulfillment) {
      if (!fulfillment) {
        return 'Техника';
      }
      const option = this.getMechanicFulfillmentOption(fulfillment);
      return fulfillment.mechanic_asset_name ||
          fulfillment.asset_name ||
          (option && option.text) ||
          (fulfillment.asset_unit_id ? `Техника #${fulfillment.asset_unit_id}` : 'Техника не выбрана');
    },
    getMechanicFulfillmentInventoryNumber(fulfillment) {
      const option = this.getMechanicFulfillmentOption(fulfillment);
      return (fulfillment && fulfillment.inventory_number) ||
          (option && option.inventory_number) ||
          '';
    },
    mechanicAssetCardRoute(assetId) {
      return {
        name: 'MechanicAssetCard',
        params: {id: String(assetId)}
      };
    },
    getMechanicFulfillmentStatusText(fulfillment) {
      if (!fulfillment) {
        return '';
      }
      const option = this.getMechanicFulfillmentOption(fulfillment);
      return fulfillment.status_label ||
          fulfillment.status ||
          (option && this.getMechanicAssetOptionStatusText(option)) ||
          '';
    },
    getMechanicFulfillmentStatusVariant(fulfillment) {
      const option = this.getMechanicFulfillmentOption(fulfillment);
      return this.getMechanicAssetOptionStatusVariant({
        status: fulfillment && fulfillment.status ? fulfillment.status : (option && option.status),
        status_label: fulfillment && fulfillment.status_label ? fulfillment.status_label : (option && option.status_label)
      });
    },
    isMechanicFulfillmentQuantity(fulfillment) {
      const option = this.getMechanicFulfillmentOption(fulfillment);
      const accountingType = fulfillment && fulfillment.accounting_type ||
          option && option.accounting_type;
      return accountingType === 'quantity';
    },
    getMechanicFulfillmentUnit(fulfillment) {
      const option = this.getMechanicFulfillmentOption(fulfillment);
      return fulfillment && (fulfillment.unit || fulfillment.unit_of_measurement) ||
          option && option.unit_of_measurement ||
          'шт';
    },
    getMechanicShipmentQuantityEditValue(item) {
      const source = this.isEditingList ? this.getEditedItem(item) : item;
      const value = Number(source && source.shipped_quantity);
      return Number.isFinite(value) && value >= 0 ? value : 0;
    },
    getMechanicShipmentQuantityUnit(item) {
      const quantityRows = this.getMechanicQuantityFulfillmentRows(item);
      if (quantityRows.length === 1) {
        return this.getMechanicFulfillmentUnit(quantityRows[0]);
      }
      if (this.isMechanicMainShipmentQuantityAsset(item)) {
        const option = this.findMechanicAssetOption(this.getEditedMechanicAssetId(item));
        return option && option.unit_of_measurement || this.getEditedItem(item).unit || item.unit || 'ед.';
      }
      return this.getEditedItem(item).unit || item.unit || 'ед.';
    },
    setMechanicShipmentQuantityFromColumn(item, value, options = {}) {
      const quantity = Number(value);
      const normalizedQuantity = Number.isFinite(quantity) && quantity >= 0 ? quantity : 0;
      const edited = this.getEditedItem(item);
      this.$set(edited, 'shipped_quantity', normalizedQuantity);

      if (this.shouldUseMechanicFulfillments(item)) {
        const quantityRows = this.getMechanicQuantityFulfillmentRows(item);
        const mainIsQuantity = this.isMechanicMainShipmentQuantityAsset(item);
        if (!mainIsQuantity && quantityRows.length === 1) {
          const quantityPart = Math.max(0, normalizedQuantity - this.getMechanicFulfillmentInventoryShippedCount(item));
          this.$set(quantityRows[0], 'shipped_quantity', quantityPart);
          this.$set(quantityRows[0], 'quantity', quantityPart);
        }
      }

      const localMaterial = this.localMaterials.find(material =>
          (material.id && material.id === item.id) ||
          (material._tempId && material._tempId === item._tempId)
      );
      if (localMaterial) {
        this.$set(localMaterial, 'shipped_quantity', normalizedQuantity);
        if (this.shouldUseMechanicFulfillments(item)) {
          this.$set(localMaterial, 'mechanic_fulfillments', this.cloneMechanicFulfillments(edited.mechanic_fulfillments));
        }
      }

      if (!options.silent) {
        this.onMaterialFieldChange();
      }
    },
    getMechanicFulfillmentDefaultQuantity(item, index, option) {
      const requested = this.getRequestedQuantity(item);
      const rows = this.getEditedMechanicFulfillments(item);
      const used = rows.reduce((sum, row, rowIndex) => {
        if (rowIndex === index) {
          return sum;
        }
        return sum + (Number(row.shipped_quantity || 0) > 0 ? Number(row.shipped_quantity || 0) : 0);
      }, this.getMechanicMainShipmentQuantity(item));
      const remaining = Math.max(0, requested - used);
      const available = Number(option && option.available_quantity);
      if (Number.isFinite(available) && available >= 0) {
        return Math.min(remaining || available, available);
      }
      return remaining || requested || 0;
    },
    setMechanicFulfillmentQuantity(item, index, value) {
      const rows = this.getEditedMechanicFulfillments(item);
      const row = rows[index];
      if (!row) {
        return;
      }
      const quantity = Number(value);
      this.$set(row, 'shipped_quantity', Number.isFinite(quantity) && quantity >= 0 ? quantity : 0);
      this.$set(row, 'quantity', Number.isFinite(quantity) && quantity >= 0 ? quantity : 0);
      this.syncMechanicFulfillmentParentQuantity(item);
      this.onMaterialFieldChange();
    },
    getMechanicFulfillmentTransferExtraText(fulfillment) {
      if (!fulfillment || !fulfillment.active_transfer || !fulfillment.active_transfer.active) {
        return '';
      }
      const responsibleName = fulfillment.active_transfer.to && fulfillment.active_transfer.to.responsible_name
          ? fulfillment.active_transfer.to.responsible_name
          : (fulfillment.target_user ? this.formatUserName(fulfillment.target_user) : '');
      return responsibleName ? `МОЛ: ${responsibleName}` : '';
    },
    addMechanicFulfillment(item) {
      const rows = this.getEditedMechanicFulfillments(item);
      rows.push({
        _key: `fulfillment-new-${Date.now()}-${Math.random()}`,
        id: null,
        asset_unit_id: null,
        catalog_item_id: null,
        quantity: 1,
        shipped_quantity: 0,
        accepted_quantity: 0,
        target_user_id: this.normalizeSelectId(this.getEditedItem(item).target_user_id || item.target_user_id),
        target_building_process_id: this.mechanicAssetUsesHeaderObject
            ? null
            : this.normalizeSelectId(this.getEditedItem(item).target_building_process_id ||
                item.target_building_process_id ||
                this.getDefaultMechanicReturnObjectId(item)),
        target_warehouse_id: this.normalizeSelectId(this.getEditedItem(item).target_warehouse_id || item.target_warehouse_id),
        meter_type: this.getEditedItem(item).meter_type || item.meter_type || null,
        meter_value: this.getEditedItem(item).meter_value === undefined ? item.meter_value : this.getEditedItem(item).meter_value,
        condition: this.getEditedItem(item).condition || item.condition || null,
        mechanic_action_result: {},
        rental_counterparty: this.getEditedItem(item).rental_counterparty || item.rental_counterparty || null,
        rental_operator_user_id: this.normalizeSelectId(this.getEditedItem(item).rental_operator_user_id || item.rental_operator_user_id),
        rental_expected_return_at: this.getEditedItem(item).rental_expected_return_at || item.rental_expected_return_at || null
      });
      this.syncMechanicFulfillmentParentQuantity(item);
      this.onMaterialFieldChange();
    },
    removeMechanicFulfillment(item, index) {
      const rows = this.getEditedMechanicFulfillments(item);
      rows.splice(index, 1);
      this.syncMechanicFulfillmentParentQuantity(item);
      this.onMaterialFieldChange();
    },
    openMechanicAssetPicker(item, fulfillmentIndex = null) {
      if (!this.isMechanicShipmentMode || !item) {
        return;
      }
      this.mechanicAssetPicker = {
        show: true,
        item,
        fulfillmentIndex
      };
    },
    selectMechanicAssetFromPicker(material) {
      const item = this.mechanicAssetPickerTargetItem;
      const assetId = this.normalizeSelectId(material && material.id);
      if (!item || !assetId) {
        return;
      }

      this.mergeAvailableCatalogMaterials([material]);
      if (this.mechanicAssetPicker.fulfillmentIndex !== null && this.mechanicAssetPicker.fulfillmentIndex !== undefined) {
        this.setMechanicFulfillmentAsset(item, this.mechanicAssetPicker.fulfillmentIndex, assetId);
        this.applyMechanicReceiptQuantity(
            item,
            material,
            this.mechanicAssetPicker.fulfillmentIndex
        );
      } else {
        this.setEditedMechanicAssetSelection(item, assetId);
        this.applyMechanicReceiptQuantity(item, material, null);
      }
    },
    applyMechanicReceiptQuantity(item, material, fulfillmentIndex) {
      const receiptQuantity = Number(material && material.receipt_quantity);
      if (!Number.isFinite(receiptQuantity) || receiptQuantity <= 0) {
        return;
      }
      const accountingType = material.accounting_type;
      const allocation = accountingType === 'quantity' ? receiptQuantity : 1;
      const requested = this.getRequestedQuantity(item);

      if (fulfillmentIndex !== null && fulfillmentIndex !== undefined) {
        const rows = this.getEditedMechanicFulfillments(item);
        const row = rows[fulfillmentIndex];
        if (!row) {
          return;
        }
        const usedByOtherRows = rows.reduce((sum, current, index) => {
          if (index === fulfillmentIndex) {
            return sum;
          }
          const shipped = Number(current.shipped_quantity);
          return sum + (Number.isFinite(shipped) && shipped > 0 ? shipped : 0);
        }, this.getMechanicMainShipmentQuantity(item));
        const remaining = Math.max(0, requested - usedByOtherRows);
        const shipped = Math.min(allocation, remaining);
        this.$set(row, 'shipped_quantity', shipped);
        this.$set(row, 'quantity', accountingType === 'quantity' ? shipped : 1);
        this.syncMechanicFulfillmentParentQuantity(item);
        this.onMaterialFieldChange();
        return;
      }

      const fulfillmentShipped = this.getEditedMechanicFulfillments(item).reduce((sum, row) => {
        const shipped = Number(row.shipped_quantity);
        return sum + (Number.isFinite(shipped) && shipped > 0 ? shipped : 0);
      }, 0);
      const remaining = Math.max(0, requested - fulfillmentShipped);
      const mainShipped = Math.min(allocation, remaining);
      this.setMechanicShipmentQuantityFromColumn(item, fulfillmentShipped + mainShipped);
    },
    clearMechanicAssetPickerSelection() {
      const item = this.mechanicAssetPickerTargetItem;
      if (!item) {
        return;
      }

      if (this.mechanicAssetPicker.fulfillmentIndex !== null && this.mechanicAssetPicker.fulfillmentIndex !== undefined) {
        this.setMechanicFulfillmentAsset(item, this.mechanicAssetPicker.fulfillmentIndex, null);
      } else {
        this.setEditedMechanicAssetSelection(item, null);
      }
      this.mechanicAssetPicker.show = false;
    },
    setMechanicFulfillmentAsset(item, index, assetId) {
      const rows = this.getEditedMechanicFulfillments(item);
      const row = rows[index];
      if (!row) {
        return;
      }
      const normalizedId = this.normalizeSelectId(assetId);
      const option = this.findMechanicAssetOption(normalizedId);
      const optionIsQuantity = option && option.accounting_type === 'quantity';
      const shippedQuantity = normalizedId
          ? (optionIsQuantity ? this.getMechanicFulfillmentDefaultQuantity(item, index, option) : 1)
          : 0;
      this.$set(row, 'asset_unit_id', normalizedId);
      this.$set(row, 'catalog_item_id', normalizedId);
      this.$set(row, 'shipped_quantity', shippedQuantity);
      this.$set(row, 'quantity', optionIsQuantity ? shippedQuantity : 1);
      this.$set(row, 'mechanic_asset_name', option ? option.text : null);
      this.$set(row, 'catalog_name', option ? option.catalog_name : null);
      this.$set(row, 'inventory_number', option ? option.inventory_number : null);
      this.$set(row, 'accounting_type', option ? option.accounting_type : null);
      this.$set(row, 'unit_of_measurement', option ? option.unit_of_measurement : null);
      this.$set(row, 'status', option ? option.status : null);
      this.$set(row, 'status_label', option ? option.status_label : null);
      this.$set(row, 'assignment_details', option ? option.assignment_details : null);
      this.$set(row, 'active_transfer', option ? option.active_transfer : null);
      this.syncMechanicFulfillmentParentQuantity(item);
      this.onMaterialFieldChange();
    },
    setEditedField(item, field, value) {
      const edited = this.getEditedItem(item);
      this.$set(edited, field, value === '' ? null : value);

      const localMaterial = this.localMaterials.find(material =>
          (material.id && material.id === item.id) ||
          (material._tempId && material._tempId === item._tempId)
      );
      if (localMaterial) {
        this.$set(localMaterial, field, value === '' ? null : value);
      }

      this.onMaterialFieldChange();
    },
    applyMechanicBulkTargetUser(value) {
      const userId = this.normalizeSelectId(value);
      if (!userId) {
        return;
      }
      this.applyMechanicBulkShipmentField('target_user_id', userId);
    },
    applyMechanicBulkTargetObject(value) {
      const buildingProcessId = this.normalizeSelectId(value);
      if (!buildingProcessId) {
        return;
      }
      this.applyMechanicBulkShipmentField('target_building_process_id', buildingProcessId);
    },
    applyMechanicBulkTargetWarehouse(value) {
      if (this.isMechanicRepairMode) {
        this.applyMechanicBulkRepairScenario(value);
        return;
      }
      this.applyMechanicBulkShipmentField('target_warehouse_id', this.normalizeSelectId(value));
    },
    applyMechanicBulkRepairScenario(value) {
      if (!this.isMechanicShipmentMode || !Array.isArray(this.localMaterials)) {
        return;
      }

      this.localMaterials.forEach(material => {
        if (!material || !this.isMechanicRepairAcceptedVisible(material)) {
          return;
        }
        this.setMechanicRepairScenario(material, value, {silent: true});
      });

      this.onMaterialFieldChange();
    },
    applyMechanicBulkShipmentField(field, value) {
      if (!this.isMechanicShipmentMode || !Array.isArray(this.localMaterials)) {
        return;
      }

      this.localMaterials.forEach(material => {
        if (!material) {
          return;
        }
        if (field === 'target_building_process_id' &&
            this.mechanicAssetShowsReturnObject &&
            !this.isMechanicReturnObjectVisible(material)) {
          return;
        }
        if ((field === 'target_user_id' || field === 'target_warehouse_id') &&
            this.isMechanicRepairMode &&
            !this.isMechanicRepairAcceptedVisible(material)) {
          return;
        }

        const edited = this.getEditedItem(material);
        this.$set(edited, field, value);
        this.$set(material, field, value);

        if (Array.isArray(edited.mechanic_fulfillments)) {
          edited.mechanic_fulfillments.forEach(fulfillment => {
            this.$set(fulfillment, field, value);
          });
        }
        if (Array.isArray(material.mechanic_fulfillments)) {
          material.mechanic_fulfillments.forEach(fulfillment => {
            this.$set(fulfillment, field, value);
          });
        }
      });

      this.onMaterialFieldChange();
    },
    setMechanicRepairAcceptedQuantity(item, quantity, options = {}) {
      const edited = this.getEditedItem(item);
      const normalizedQuantity = this.normalizeWholeNumberInput(quantity);
      let acceptedQuantity = normalizedQuantity;

      if (Array.isArray(edited.mechanic_fulfillments) && edited.mechanic_fulfillments.length > 0) {
        let remaining = normalizedQuantity;
        let distributed = 0;
        edited.mechanic_fulfillments.forEach(row => {
          const rowMax = Math.max(0, Number(row.shipped_quantity || row.quantity || 1) || 0);
          const rowAccepted = Math.min(rowMax, remaining);
          this.$set(row, 'accepted_quantity', rowAccepted);
          remaining = Math.max(0, remaining - rowAccepted);
          distributed += rowAccepted;
        });
        acceptedQuantity = distributed;
      }

      this.$set(edited, 'accepted_quantity', acceptedQuantity);
      if (acceptedQuantity <= 0 && this.mechanicAssetShowsReturnObject && !this.isMechanicRepairCompletionMode) {
        this.$set(edited, 'target_building_process_id', null);
        if (Array.isArray(edited.mechanic_fulfillments)) {
          edited.mechanic_fulfillments.forEach(row => {
            this.$set(row, 'target_building_process_id', null);
          });
        }
      }

      const localMaterial = this.localMaterials.find(material =>
          (material.id && material.id === item.id) ||
          (material._tempId && material._tempId === item._tempId)
      );
      if (localMaterial) {
        this.$set(localMaterial, 'accepted_quantity', acceptedQuantity);
        if (acceptedQuantity <= 0 && this.mechanicAssetShowsReturnObject && !this.isMechanicRepairCompletionMode) {
          this.$set(localMaterial, 'target_building_process_id', null);
        }
        if (Array.isArray(edited.mechanic_fulfillments)) {
          this.$set(localMaterial, 'mechanic_fulfillments', this.cloneMechanicFulfillments(edited.mechanic_fulfillments));
        }
      }

      if (!options.silent) {
        this.onMaterialFieldChange();
      }
    },
    findMechanicAssetOption(assetId) {
      const normalizedId = this.normalizeSelectId(assetId);
      if (!normalizedId) {
        return null;
      }
      return this.mechanicAssetOptions.find(option => Number(option.value) === normalizedId) || null;
    },
    getMechanicAssetOptionsForItem(item) {
      if (!this.isMechanicAssetCatalog) {
        return [];
      }
      if (this.isMechanicShipmentMode) {
        const currentId = this.getEditedMechanicAssetId(item);
        const selectedIds = this.getMechanicShipmentSameRowUsedAssetIds({
          currentItem: item,
          ignoreCurrentItemMain: true
        });
        return this.mechanicAssetOptions.filter(option => {
          const optionId = Number(option.value);
          return optionId === currentId || !selectedIds.has(optionId);
        });
      }
      const quantityRow = this.isMechanicQuantityRequest(item) || this.isMechanicManualEditable(item);
      return this.mechanicAssetOptions.filter(option => {
        const optionIsQuantity = option.accounting_type === 'quantity';
        return quantityRow ? optionIsQuantity : !optionIsQuantity;
      });
    },
    isMechanicSelectionRequired(item) {
      return this.isMechanicAssetCatalog &&
          !this.isMechanicQuantityRequest(item) &&
          !this.isMechanicManualEditable(item);
    },
    shouldHighlightMechanicAssetSelection(item) {
      if (!this.isMechanicShipmentMode || !item || this.hasMechanicShipmentSelection(item)) {
        return false;
      }
      const edited = this.isEditingList ? this.getEditedItem(item) : item;
      const shippedQuantity = Number(edited && edited.shipped_quantity);
      return this.isMechanicSelectionRequired(item) ||
          (Number.isFinite(shippedQuantity) && shippedQuantity > 0);
    },
    getEditedMechanicAssetId(item) {
      if (!this.isEditingList) {
        return this.isMechanicShipmentSelectionConfirmed(item)
            ? this.normalizeSelectId(item && item.catalog_item_id)
            : null;
      }
      const edited = this.getEditedItem(item);
      return this.normalizeSelectId(edited.catalog_item_id);
    },
    getMechanicRequestedDisplayName(item) {
      if (!item) {
        return 'Техника';
      }
      const edited = this.isEditingList ? this.getEditedItem(item) : item;
      return edited._requestedItemName ||
          edited.item_name ||
          item.item_name ||
          'Техника';
    },
    getMechanicAssetSelectedName(item) {
      const assetId = this.getEditedMechanicAssetId(item);
      if (!assetId) {
        return '';
      }
      const option = this.getMechanicAssetSelectedOption(item);
      if (option && option.text) {
        return option.text;
      }
      return item.mechanic_asset_name || item.asset_name || item.item_name || `Техника #${assetId}`;
    },
    getMechanicAssetSelectedOption(item) {
      const assetId = this.getEditedMechanicAssetId(item);
      const option = this.findMechanicAssetOption(assetId);
      const source = item && this.isEditingList ? this.getEditedItem(item) : item;
      if (option) {
        if (!item) {
          return option;
        }
        return {
          ...option,
          assignment_details: source.assignment_details || item.assignment_details || option.assignment_details || null,
          status: source.status || item.status || option.status || null,
          status_label: source.status_label || item.status_label || option.status_label || null,
          active_transfer: source.active_transfer || item.active_transfer || option.active_transfer || null,
          catalog_name: source.catalog_name || item.catalog_name || option.catalog_name || null,
          inventory_number: source.inventory_number || item.inventory_number || option.inventory_number || null,
          accounting_type: source.accounting_type || item.accounting_type || option.accounting_type || 'inventory',
          available_quantity: source.available_quantity === undefined ? option.available_quantity : source.available_quantity,
          unit_of_measurement: option.unit_of_measurement || source.unit_of_measurement || source.unit || 'ед.',
          current_meter_type: source.current_meter_type || item.current_meter_type || option.current_meter_type || null,
          current_meter_value: source.current_meter_value === undefined ? option.current_meter_value : source.current_meter_value
        };
      }
      if (!item || !assetId) {
        return null;
      }
      return {
        value: assetId,
        text: source.mechanic_asset_name || source.asset_name || item.mechanic_asset_name || item.asset_name || item.item_name || `Техника #${assetId}`,
        assignment_details: source.assignment_details || item.assignment_details || null,
        status: source.status || item.status || null,
        status_label: source.status_label || item.status_label || null,
        active_transfer: source.active_transfer || item.active_transfer || null,
        catalog_name: source.catalog_name || item.catalog_name || null,
        inventory_number: source.inventory_number || item.inventory_number || null,
        accounting_type: source.accounting_type || item.accounting_type || 'inventory',
        available_quantity: source.available_quantity === undefined ? item.quantity : source.available_quantity,
        unit_of_measurement: source.unit_of_measurement || item.unit_of_measurement || source.unit || item.unit || 'ед.',
        current_meter_type: source.current_meter_type || item.current_meter_type || null,
        current_meter_value: source.current_meter_value === undefined ? null : source.current_meter_value
      };
    },
    getMechanicAssetSelectedDetails(item) {
      const option = this.getMechanicAssetSelectedOption(item);
      if (option && option.active_transfer && option.active_transfer.active === true) {
        return `перемещается: ${this.getMechanicTransferPointText(option.active_transfer.from)} -> ${this.getMechanicTransferPointText(option.active_transfer.to)}`;
      }
      if (option && option.assignment_details) {
        return option.assignment_details;
      }
      return '';
    },
    getMechanicAssetSelectedInventoryNumber(item) {
      const option = this.getMechanicAssetSelectedOption(item);
      return (option && option.inventory_number) ||
          (item && item.inventory_number) ||
          '';
    },
    isMechanicTransferDetails(details) {
      return /^перемещается:/i.test(String(details || '').trim());
    },
    isMechanicAssetSelectedTransfer(item) {
      return this.isMechanicTransferDetails(this.getMechanicAssetSelectedDetails(item));
    },
    getMechanicTransferPointText(point, fallback) {
      if (!point) {
        return fallback || 'не указано';
      }
      if (typeof point === 'string') {
        return point || fallback || 'не указано';
      }
      return point.object_name ||
          point.warehouse_name ||
          point.renter_name ||
          point.location_label ||
          point.location_type ||
          fallback ||
          'не указано';
    },
    getMechanicAssetSelectedTransfer(item) {
      const option = this.getMechanicAssetSelectedOption(item);
      const activeTransfer = option && option.active_transfer && option.active_transfer.active === true
          ? option.active_transfer
          : null;
      if (activeTransfer) {
        return {
          from: this.getMechanicTransferPointText(activeTransfer.from),
          to: this.getMechanicTransferPointText(activeTransfer.to),
          extra: this.getMechanicTransferTargetResponsibleText(item, activeTransfer)
        };
      }

      const details = this.getMechanicAssetSelectedDetails(item);
      if (!this.isMechanicTransferDetails(details)) {
        return null;
      }
      const body = String(details).replace(/^перемещается:\s*/i, '');
      const parts = body.split(' / ');
      const route = parts.shift() || '';
      const routeParts = route.split(/\s*->\s*/);
      return {
        from: routeParts[0] || 'не указано',
        to: routeParts.slice(1).join(' -> ') || 'не указано',
        extra: parts.join(' / ')
      };
    },
    getMechanicTransferTargetResponsibleText(item, activeTransfer) {
      const responsibleName = activeTransfer && activeTransfer.to && activeTransfer.to.responsible_name
          ? activeTransfer.to.responsible_name
          : (item && item.target_user ? this.formatUserName(item.target_user) : '');
      return responsibleName ? `МОЛ: ${responsibleName}` : '';
    },
    getMechanicTransferFromText(item) {
      const transfer = this.getMechanicAssetSelectedTransfer(item);
      return transfer ? transfer.from : 'не указано';
    },
    getMechanicTransferToText(item) {
      const transfer = this.getMechanicAssetSelectedTransfer(item);
      return transfer ? transfer.to : 'не указано';
    },
    getMechanicTransferExtraText(item) {
      const transfer = this.getMechanicAssetSelectedTransfer(item);
      return transfer && transfer.extra ? transfer.extra : '';
    },
    getMechanicAssetSelectedDetailsText(item) {
      const details = this.getMechanicAssetSelectedDetails(item);
      if (this.isMechanicTransferDetails(details)) {
        return String(details).replace(/^перемещается:\s*/i, '');
      }
      return details;
    },
    getMechanicAssetOptionStatusText(option) {
      return option && (option.status_label || option.status) ? (option.status_label || option.status) : '';
    },
    getMechanicAssetOptionStatusVariant(option) {
      const status = option && option.status ? String(option.status) : '';
      const variants = {
        stock: 'success',
        in_transfer: 'info',
        on_object: 'primary',
        repair: 'warning',
        repair_acceptance: 'warning',
        ready_after_repair: 'info',
        rented_out: 'dark',
        lost: 'danger',
        written_off: 'secondary',
        sold: 'secondary',
        archive: 'secondary'
      };
      return variants[status] || 'secondary';
    },
    shouldShowMechanicAssetStatus(item) {
      if (!this.isMechanicAssetCatalog || !item) {
        return false;
      }
      if (this.isMechanicShipmentMode || this.canManageMechanicAssetSelection) {
        return true;
      }
      const shipped = Number(item.shipped_quantity || 0);
      const accepted = Number(item.accepted_quantity || 0);
      return (Number.isFinite(shipped) && shipped > 0) ||
          (Number.isFinite(accepted) && accepted > 0);
    },
    getMechanicAssetRequestedName(item) {
      if (!item || !item.item_name || !this.getEditedMechanicAssetId(item)) {
        return '';
      }
      const edited = this.getEditedItem(item);
      const requestedName = edited._requestedItemName || item.item_name;
      const option = this.findMechanicAssetOption(this.getEditedMechanicAssetId(item));
      if (option && String(option.text || '').trim() === String(requestedName || '').trim()) {
        return '';
      }
      return requestedName;
    },
    confirmMechanicRequestedAsset(item) {
      const requestedId = this.getMechanicRequestedCatalogItemId(item);
      if (!requestedId) {
        return;
      }
      this.setEditedMechanicAssetSelection(item, requestedId);
    },
    buildMechanicSelectionActionResult(item, selectedAssetId) {
      const selectedId = this.normalizeSelectId(selectedAssetId);
      const edited = item ? this.getEditedItem(item) : null;
      const currentResult = this.clonePlainObject(
          (edited && edited.mechanic_action_result) ||
          (item && item.mechanic_action_result)
      );
      const requestedId = this.normalizeSelectId(
          (edited && edited._requestedCatalogItemId) ||
          currentResult.mechanic_selection &&
          currentResult.mechanic_selection.requested_catalog_item_id ||
          item && item.catalog_item_id ||
          selectedId
      );

      return {
        ...currentResult,
        mechanic_selection: {
          ...(currentResult.mechanic_selection || {}),
          requested_catalog_item_id: requestedId || null,
          selected_catalog_item_id: selectedId || null,
          confirmed: Boolean(selectedId),
          source: selectedId
              ? (requestedId && selectedId !== requestedId ? 'executor_replacement' : 'initiator_suggestion')
              : null,
          confirmed_at: selectedId ? new Date().toISOString() : null
        }
      };
    },
    setMechanicSelectionActionResult(item, selectedAssetId) {
      const edited = this.getEditedItem(item);
      const actionResult = this.buildMechanicSelectionActionResult(item, selectedAssetId);
      this.$set(edited, 'mechanic_action_result', actionResult);
      this.$set(edited, '_mechanicSelectionTouched', true);

      const localMaterial = this.localMaterials.find(material =>
          (material.id && material.id === item.id) ||
          (material._tempId && material._tempId === item._tempId)
      );
      if (localMaterial) {
        this.$set(localMaterial, 'mechanic_action_result', actionResult);
        this.$set(localMaterial, '_mechanicSelectionTouched', true);
      }
    },
    setEditedMechanicAssetSelection(item, assetId) {
      const normalizedId = this.normalizeSelectId(assetId);
      const edited = this.getEditedItem(item);
      const option = this.findMechanicAssetOption(normalizedId);
      const isManualRow = this.isMechanicManualEditable(item);
      const isOptionalMatchRow = this.isMechanicQuantityRequest(item) || isManualRow;
      const requestedAccountingType = edited._requestedAccountingType ||
          edited.accounting_type ||
          (isOptionalMatchRow ? 'quantity' : null);

      this.$set(edited, 'catalog_type', 'mechanic_assets');
      this.$set(edited, 'catalog_item_id', normalizedId);
      this.$set(edited, 'isCustom', isOptionalMatchRow ? (isManualRow || edited.isCustom === true) : (!normalizedId && isManualRow));
      this.setMechanicSelectionActionResult(item, normalizedId);

      if (option) {
        this.$set(edited, 'catalog_name', option.catalog_name || null);
        this.$set(edited, 'mechanic_asset_name', option.text || null);
        this.$set(edited, 'asset_name', option.text || null);
        this.$set(edited, 'accounting_type', isOptionalMatchRow ? requestedAccountingType : (option.accounting_type || 'inventory'));
        this.$set(edited, 'available_quantity', option.available_quantity === undefined ? null : option.available_quantity);
        this.$set(edited, 'inventory_number', option.inventory_number || null);
        this.$set(edited, 'status', option.status || null);
        this.$set(edited, 'status_label', option.status_label || null);
        this.$set(edited, 'assignment_details', option.assignment_details || null);
        this.$set(edited, 'active_transfer', option.active_transfer || null);
        this.$set(edited, 'current_meter_type', option.current_meter_type || null);
        this.$set(edited, 'current_meter_value', option.current_meter_value === undefined ? null : option.current_meter_value);
        if (option.current_meter_type && !edited.meter_type) {
          this.$set(edited, 'meter_type', option.current_meter_type);
        }
      } else {
        this.$set(edited, 'catalog_name', null);
        this.$set(edited, 'mechanic_asset_name', null);
        this.$set(edited, 'asset_name', null);
        this.$set(edited, 'accounting_type', requestedAccountingType);
        this.$set(edited, 'available_quantity', null);
        this.$set(edited, 'inventory_number', null);
        this.$set(edited, 'status', null);
        this.$set(edited, 'status_label', null);
        this.$set(edited, 'assignment_details', null);
        this.$set(edited, 'active_transfer', null);
        this.$set(edited, 'current_meter_type', null);
        this.$set(edited, 'current_meter_value', null);
      }

      const localMaterial = this.localMaterials.find(material =>
          (material.id && material.id === item.id) ||
          (material._tempId && material._tempId === item._tempId)
      );
      if (localMaterial) {
        this.$set(localMaterial, 'catalog_type', 'mechanic_assets');
        this.$set(localMaterial, 'catalog_item_id', normalizedId);
        this.$set(localMaterial, 'isCustom', edited.isCustom === true);
        if (option) {
          this.$set(localMaterial, 'catalog_name', option.catalog_name || null);
          this.$set(localMaterial, 'mechanic_asset_name', option.text || null);
          this.$set(localMaterial, 'asset_name', option.text || null);
          this.$set(localMaterial, 'accounting_type', edited.accounting_type || null);
          this.$set(localMaterial, 'available_quantity', option.available_quantity === undefined ? null : option.available_quantity);
          this.$set(localMaterial, 'inventory_number', option.inventory_number || null);
          this.$set(localMaterial, 'status', option.status || null);
          this.$set(localMaterial, 'status_label', option.status_label || null);
          this.$set(localMaterial, 'assignment_details', option.assignment_details || null);
          this.$set(localMaterial, 'active_transfer', option.active_transfer || null);
          this.$set(localMaterial, 'current_meter_type', option.current_meter_type || null);
          this.$set(localMaterial, 'current_meter_value', option.current_meter_value === undefined ? null : option.current_meter_value);
          if (option.current_meter_type && !localMaterial.meter_type) {
            this.$set(localMaterial, 'meter_type', option.current_meter_type);
          }
        } else {
          this.$set(localMaterial, 'catalog_name', null);
          this.$set(localMaterial, 'mechanic_asset_name', null);
          this.$set(localMaterial, 'asset_name', null);
          this.$set(localMaterial, 'accounting_type', requestedAccountingType);
          this.$set(localMaterial, 'available_quantity', null);
          this.$set(localMaterial, 'inventory_number', null);
          this.$set(localMaterial, 'status', null);
          this.$set(localMaterial, 'status_label', null);
          this.$set(localMaterial, 'assignment_details', null);
          this.$set(localMaterial, 'active_transfer', null);
          this.$set(localMaterial, 'current_meter_type', null);
          this.$set(localMaterial, 'current_meter_value', null);
        }
      }

      if (this.shouldUseMechanicFulfillments(item)) {
        this.syncMechanicFulfillmentParentQuantity(item);
      }
      this.onMaterialFieldChange();
    },
    getMechanicMeterTypeText(value) {
      const option = this.mechanicAssetMeterTypeOptions.find(item => item.value === value);
      return option && option.value ? option.text.toLowerCase() : '';
    },
    formatDateInput(value) {
      if (!value) return null;
      return String(value).slice(0, 10);
    },
    async ensureMechanicAssetDictionaries(options = {}) {
      if (!this.isMechanicAssetCatalog) {
        return;
      }
      const loadReturnObjects = options.loadReturnObjects !== false;
      const mechanicState = this.$store && this.$store.state ? this.$store.state.mechanicAssets : null;
      const hasWarehouses = mechanicState && Array.isArray(mechanicState.warehouses) && mechanicState.warehouses.length > 0;
      if (!hasWarehouses) {
        await this.$store.dispatch('mechanicAssets/fetchDictionaries');
      }
      const hasObjects = this.$store.state.objectsInProcess && this.$store.state.objectsInProcess.length > 0;
      if (!hasObjects) {
        await this.$store.dispatch('getObjectsInProcess');
      }
      if (loadReturnObjects && this.mechanicAssetShowsReturnObject) {
        await this.ensureMechanicAssetReturnObjectOptions();
      }
      const hasUsers = this.$store.state.users && this.$store.state.users.length > 0;
      if (!hasUsers && (this.mechanicAssetNeedsTargetUser || this.mechanicAssetNeedsRental)) {
        await this.$store.dispatch('getUsers').catch(() => {});
      }
    },
    ensureMechanicAssetCatalogLoaded() {
      if (!this.isMechanicAssetCatalog) {
        return Promise.resolve();
      }
      return this.loadMaterials();
    },
    ensureMechanicAssetReturnObjectOptions() {
      if (!this.mechanicAssetShowsReturnObject) {
        return Promise.resolve();
      }
      return this.loadMechanicAssetReturnObjectOptions();
    },
    async loadMechanicAssetReturnObjectOptions() {
      if (this.mechanicAssetReturnObjectOptions.length > 0) {
        return Promise.resolve(this.mechanicAssetReturnObjectOptions);
      }
      if (this.mechanicAssetReturnObjectOptionsPromise) {
        return this.mechanicAssetReturnObjectOptionsPromise;
      }

      this.mechanicAssetReturnObjectOptionsLoading = true;
      this.mechanicAssetReturnObjectOptionsPromise = api.get(`/api/requests/${this.requestId}/mechanic-asset-target-object-options`)
          .then(({ data }) => {
            this.mechanicAssetReturnObjectOptions = Array.isArray(data) ? data : [];
            return this.mechanicAssetReturnObjectOptions;
          })
          .catch(() => {
            this.mechanicAssetReturnObjectOptions = [];
            return [];
          })
          .finally(() => {
            this.mechanicAssetReturnObjectOptionsLoading = false;
            this.mechanicAssetReturnObjectOptionsPromise = null;
          });

      return this.mechanicAssetReturnObjectOptionsPromise;
    },
    async loadFuelCardOptions() {
      if (!this.isFuelCardIssueToRecipient || !this.canManageFuelCardSelection || this.fuelCardOptions.length > 0) {
        return;
      }

      this.fuelCardOptionsLoading = true;
      try {
        const options = await this.$store.dispatch('requestCard/requestCard_getFuelCardOptions');
        this.fuelCardOptions = Array.isArray(options) ? options : [];
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Не удалось загрузить топливные карты';
        this.$bvToast && this.$bvToast.toast(message, {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
        this.fuelCardOptions = [];
      } finally {
        this.fuelCardOptionsLoading = false;
      }
    },
    async selectFuelCardForItem(item, fuelCardId) {
      if (!item || !item.id || !fuelCardId) {
        return;
      }

      this.$set(this.fuelCardSelectionBusy, item.id, true);
      try {
        await this.$store.dispatch('requestCard/requestCard_selectFuelCardForItem', {
          itemId: item.id,
          fuelCardId
        });
        this.$bvToast && this.$bvToast.toast('Топливная карта выбрана', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Не удалось выбрать топливную карту';
        this.$bvToast && this.$bvToast.toast(message, {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.$delete(this.fuelCardSelectionBusy, item.id);
      }
    },
    async loadFuelCardTargetObjectOptions() {
      if (!this.isFuelCardMoveToRecipient || !this.canManageFuelCardSelection || this.fuelCardTargetObjectOptions.length > 0) {
        return;
      }

      this.fuelCardTargetObjectOptionsLoading = true;
      try {
        const options = await this.$store.dispatch('requestCard/requestCard_getFuelCardTargetObjectOptions');
        this.fuelCardTargetObjectOptions = Array.isArray(options) ? options : [];
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Не удалось загрузить объекты назначения';
        this.$bvToast && this.$bvToast.toast(message, {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
        this.fuelCardTargetObjectOptions = [];
      } finally {
        this.fuelCardTargetObjectOptionsLoading = false;
      }
    },
    async selectFuelCardTargetObjectForItem(item, buildingProcessId) {
      if (!item || !item.id || !buildingProcessId) {
        return;
      }

      this.$set(this.fuelCardTargetObjectBusy, item.id, true);
      try {
        await this.$store.dispatch('requestCard/requestCard_selectFuelCardTargetObjectForItem', {
          itemId: item.id,
          buildingProcessId
        });
        this.$bvToast && this.$bvToast.toast('Объект назначения выбран', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Не удалось выбрать объект назначения';
        this.$bvToast && this.$bvToast.toast(message, {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.$delete(this.fuelCardTargetObjectBusy, item.id);
      }
    },

    // ========== Методы для редактирования списка материалов ==========

    async startEditingList() {
      if (this.editingListStarting || this.isEditingList) {
        return;
      }

      this.editingListStarting = true;
      await this.waitForUiFrame();

      try {
        await this.ensureMechanicAssetDictionaries({loadReturnObjects: false});
        this.prepareEditingListState();
        await this.hydrateSelectedMechanicAssetCatalogItems();
        this.editingListPrepared = true;
        this.isEditingList = true;
        this.$emit('editing-list-started');
      } finally {
        this.editingListStarting = false;
      }
    },
    updatePpePlan(items) {
      this.localMaterials = items;
      items.forEach(item => {
        const key = item.id || item._tempId;
        if (this.editedListItems[key]) {
          this.$set(this.editedListItems[key], 'ppe_data', item.ppe_data || null);
          this.$set(this.editedListItems[key], 'quantity', item.quantity);
          this.$set(this.editedListItems[key], 'notes', item.notes || '');
          if (item.isCustom || item.is_custom) this.$set(this.editedListItems[key], 'item_name', item.item_name || item.name || '');
        }
      });
      this.$emit('materials-list-changed', this.getMaterialsChanges());
    },

    waitForUiFrame() {
      return new Promise(resolve => {
        this.$nextTick(() => {
          if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => resolve());
            return;
          }
          setTimeout(resolve, 0);
        });
      });
    },

    prepareEditingListState() {
      // Создаем копию материалов для редактирования
      this.localMaterials = JSON.parse(JSON.stringify(this.materials));
      this.editedListItems = {};
      this.localMaterials.forEach(material => {
        const isCustom = material.isCustom || material.is_custom || false;
        const mechanicActionResult = this.clonePlainObject(material.mechanic_action_result);
        const requestedCatalogItemId = this.isMechanicAssetCatalog
            ? this.normalizeSelectId(
                mechanicActionResult.mechanic_selection &&
                mechanicActionResult.mechanic_selection.requested_catalog_item_id ||
                material.catalog_item_id
            )
            : null;
        const mechanicSelectionConfirmed = !this.isMechanicAssetCatalog ||
            !this.isMechanicShipmentMode ||
            this.isMechanicStoredShipmentSelectionConfirmed(material);
        const editableCatalogItemId = this.isMechanicAssetCatalog && this.isMechanicShipmentMode && !mechanicSelectionConfirmed
            ? null
            : (material.catalog_item_id || null);
        this.$set(this.editedListItems, material.id || material._tempId, {
          id: material.id,
          item_name: material.item_name,
          notes: material.notes || '',
          ppe_data: material.ppe_data || null,
          quantity: material.quantity,
          unit: material.unit,
          shipped_quantity: material.shipped_quantity || 0,
          accepted_quantity: material.accepted_quantity || 0,
          shipping_comment: material.shipping_comment || '',
          catalog_type: material.catalog_type || null,
          catalog_item_id: editableCatalogItemId,
          catalog_name: material.catalog_name || null,
          mechanic_asset_name: material.mechanic_asset_name || material.asset_name || null,
          asset_name: material.asset_name || material.mechanic_asset_name || null,
          accounting_type: material.accounting_type || null,
          available_quantity: material.available_quantity === undefined ? null : material.available_quantity,
          inventory_number: material.inventory_number || null,
          status: material.status || null,
          status_label: material.status_label || null,
          assignment_details: material.assignment_details || null,
          active_transfer: material.active_transfer || null,
          current_meter_type: material.current_meter_type || null,
          current_meter_value: material.current_meter_value === undefined ? null : material.current_meter_value,
          target_user_id: material.target_user_id || this.getUserId(material.target_user) || null,
          target_building_process_id: this.mechanicAssetUsesHeaderObject
              ? null
              : (material.target_building_process_id || this.getDefaultMechanicReturnObjectId(material)),
          target_warehouse_id: material.target_warehouse_id || null,
          meter_type: material.meter_type || null,
          meter_value: material.meter_value === undefined ? null : material.meter_value,
          condition: material.condition || '',
          rental_counterparty: material.rental_counterparty || '',
          rental_operator_user_id: material.rental_operator_user_id || null,
          rental_expected_return_at: material.rental_expected_return_at || null,
          mechanic_action_result: mechanicActionResult,
          mechanic_fulfillments: this.cloneMechanicFulfillments(material.mechanic_fulfillments),
          fuel_limits: this.cloneFuelLimits(material.fuel_limits),
          isCustom,
          _tempId: material._tempId,
          _requestedItemName: material.item_name || '',
          _requestedUnit: material.unit || 'ед.',
          _requestedAccountingType: material.accounting_type || null,
          _requestedCatalogItemId: requestedCatalogItemId,
          _mechanicSelectionTouched: false
        });
        if (this.isMechanicAssetCatalog && this.normalizeCommentValue(material.shipping_comment)) {
          this.$set(this.expandedOperationComments, this.operationCommentKey(material, 'shipping'), true);
        }
      });
    },

    getEditedItem(item) {
      const key = item.id || item._tempId;
      if (!this.editedListItems[key]) {
        const isCustom = item.isCustom || item.is_custom || false;
        const mechanicActionResult = this.clonePlainObject(item.mechanic_action_result);
        const requestedCatalogItemId = this.isMechanicAssetCatalog
            ? this.normalizeSelectId(
                mechanicActionResult.mechanic_selection &&
                mechanicActionResult.mechanic_selection.requested_catalog_item_id ||
                item.catalog_item_id
            )
            : null;
        const mechanicSelectionConfirmed = !this.isMechanicAssetCatalog ||
            !this.isMechanicShipmentMode ||
            this.isMechanicStoredShipmentSelectionConfirmed(item);
        const editableCatalogItemId = this.isMechanicAssetCatalog && this.isMechanicShipmentMode && !mechanicSelectionConfirmed
            ? null
            : (item.catalog_item_id || null);
        this.$set(this.editedListItems, key, {
          id: item.id,
          item_name: item.item_name,
          notes: item.notes || '',
          ppe_data: item.ppe_data || null,
          quantity: item.quantity,
          unit: item.unit,
          shipped_quantity: item.shipped_quantity || 0,
          accepted_quantity: item.accepted_quantity || 0,
          shipping_comment: item.shipping_comment || '',
          catalog_type: item.catalog_type || null,
          catalog_item_id: editableCatalogItemId,
          catalog_name: item.catalog_name || null,
          mechanic_asset_name: item.mechanic_asset_name || item.asset_name || null,
          asset_name: item.asset_name || item.mechanic_asset_name || null,
          accounting_type: item.accounting_type || null,
          available_quantity: item.available_quantity === undefined ? null : item.available_quantity,
          inventory_number: item.inventory_number || null,
          status: item.status || null,
          status_label: item.status_label || null,
          assignment_details: item.assignment_details || null,
          active_transfer: item.active_transfer || null,
          current_meter_type: item.current_meter_type || null,
          current_meter_value: item.current_meter_value === undefined ? null : item.current_meter_value,
          target_user_id: item.target_user_id || this.getUserId(item.target_user) || null,
          target_building_process_id: this.mechanicAssetUsesHeaderObject
              ? null
              : (item.target_building_process_id || this.getDefaultMechanicReturnObjectId(item)),
          target_warehouse_id: item.target_warehouse_id || null,
          meter_type: item.meter_type || null,
          meter_value: item.meter_value === undefined ? null : item.meter_value,
          condition: item.condition || '',
          rental_counterparty: item.rental_counterparty || '',
          rental_operator_user_id: item.rental_operator_user_id || null,
          rental_expected_return_at: item.rental_expected_return_at || null,
          mechanic_action_result: mechanicActionResult,
          mechanic_fulfillments: this.cloneMechanicFulfillments(item.mechanic_fulfillments),
          fuel_limits: this.cloneFuelLimits(item.fuel_limits),
          isCustom,
          _tempId: item._tempId,
          _requestedItemName: item.item_name || '',
          _requestedUnit: item.unit || 'ед.',
          _requestedAccountingType: item.accounting_type || null,
          _requestedCatalogItemId: requestedCatalogItemId,
          _mechanicSelectionTouched: false
        });
      }
      return this.editedListItems[key];
    },

    setEditedFuelCardTargetUser(item, value) {
      const edited = this.getEditedItem(item);
      const userId = Number(value);
      const normalizedUserId = Number.isInteger(userId) && userId > 0 ? userId : null;
      this.$set(edited, 'target_user_id', normalizedUserId);
      if (this.isFuelCardIssueToRecipient) {
        const option = this.getFuelCardRecipientOptions(item)
            .find(entry => Number(entry.value) === Number(normalizedUserId));
        const targetName = (option && option.text) || `Пользователь #${normalizedUserId}`;
        const itemName = normalizedUserId
            ? `Получатель: ${targetName}`
            : 'Получатель не выбран';
        this.$set(edited, 'item_name', itemName);
      }

      const localMaterial = this.localMaterials.find(material =>
          (material.id && material.id === item.id) ||
          (material._tempId && material._tempId === item._tempId)
      );
      if (localMaterial) {
        this.$set(localMaterial, 'target_user_id', normalizedUserId);
        if (this.isFuelCardIssueToRecipient) {
          this.$set(localMaterial, 'item_name', edited.item_name);
        }
      }

      this.onMaterialFieldChange();
    },

    validateItemName(item) {
      const edited = this.getEditedItem(item);
      return edited.item_name && edited.item_name.trim() !== '' ? true : false;
    },

    validateQuantity(item) {
      const edited = this.getEditedItem(item);
      return edited.quantity && edited.quantity > 0 ? true : false;
    },

    validateShippedQuantity(item) {
      const edited = this.getEditedItem(item);
      const shipped = Number(edited.shipped_quantity);
      return Number.isFinite(shipped) && shipped >= 0;
    },

    onMechanicShippedQuantityChange(item) {
      const edited = this.getEditedItem(item);
      const shipped = Number(edited.shipped_quantity);
      if (!Number.isFinite(shipped) || shipped < 0) {
        this.$set(edited, 'shipped_quantity', 0);
      }

      const localMaterial = this.localMaterials.find(material =>
          (material.id && material.id === item.id) ||
          (material._tempId && material._tempId === item._tempId)
      );
      if (localMaterial) {
        this.$set(localMaterial, 'shipped_quantity', edited.shipped_quantity || 0);
      }

      this.onMaterialFieldChange();
    },

    isMechanicInventoryFlagChecked(item, field) {
      if (!item || !field) {
        return false;
      }
      const source = this.isEditingList ? this.getEditedItem(item) : item;
      return Number(source[field] || 0) >= 1;
    },
    isMechanicRepairAcceptedVisible(item) {
      if (!this.isMechanicRepairMode || !item) {
        return false;
      }
      const source = this.isEditingList ? this.getEditedItem(item) : item;
      // The selected units already form the pending shipment before its first save.
      // Expose the repair destination now, so that save does not accidentally start onsite repair.
      if (this.isMechanicShipmentMode && Number(this.getEditedMaterialShippedQuantityForPayload(item, source)) > 0) {
        return true;
      }
      if (Number(source.shipped_quantity || 0) > 0) {
        return true;
      }
      return Array.isArray(source.mechanic_fulfillments) &&
          source.mechanic_fulfillments.some(row => Number(row.shipped_quantity || 0) > 0);
    },
    isMechanicReturnObjectVisible(item) {
      if (!this.mechanicAssetShowsReturnObject || !item) {
        return false;
      }
      const source = this.isEditingList ? this.getEditedItem(item) : item;
      if (this.isMechanicRepairCompletionMode && this.isMechanicRepairAcceptedVisible(item)) {
        return true;
      }
      if (Number(source.accepted_quantity || 0) > 0) {
        return true;
      }
      return Array.isArray(source.mechanic_fulfillments) &&
          source.mechanic_fulfillments.some(row => Number(row.accepted_quantity || 0) > 0);
    },
    isMechanicInventoryAcceptedChecked(item) {
      if (!item) {
        return false;
      }
      if (this.isMechanicRepairCompletionMode) {
        return this.isMechanicInventoryFlagChecked(item, 'accepted_quantity');
      }
      const source = this.isEditing && this.editedItems[item.id]
          ? this.editedItems[item.id]
          : item;
      return Number(source.accepted_quantity || 0) >= 1;
    },
    setMechanicInventoryAcceptedFlag(item, checked) {
      if (this.isMechanicRepairCompletionMode) {
        const edited = this.getEditedItem(item);
        const accepted = checked ? this.getAcceptedQuickFillQuantity(item, edited) : 0;
        this.setMechanicRepairAcceptedQuantity(item, accepted);
        return;
      }
      if (!item || !item.id || !this.editedItems[item.id]) {
        return;
      }
      const value = checked ? 1 : 0;
      this.$set(this.editedItems[item.id], 'accepted_quantity', value);
      if (checked) {
        this.$set(this.editedItems[item.id], 'shipped_quantity', Math.max(1, Number(this.editedItems[item.id].shipped_quantity || item.shipped_quantity || 0)));
      }
    },

    setMechanicInventoryFlag(item, field, checked) {
      const edited = this.getEditedItem(item);
      const value = checked ? 1 : 0;

      this.$set(edited, field, value);
      if (field === 'shipped_quantity' && !checked && !this.isMechanicShipmentMode) {
        this.$set(edited, 'accepted_quantity', 0);
      }
      if (field === 'shipped_quantity' && !checked && this.isMechanicRepairMode) {
        this.$set(edited, 'accepted_quantity', 0);
        this.$set(edited, 'target_user_id', null);
        this.$set(edited, 'target_warehouse_id', null);
        this.$set(edited, 'target_building_process_id', null);
        if (Array.isArray(edited.mechanic_fulfillments)) {
          edited.mechanic_fulfillments.forEach(row => {
            this.$set(row, 'accepted_quantity', 0);
            this.$set(row, 'target_user_id', null);
            this.$set(row, 'target_warehouse_id', null);
            this.$set(row, 'target_building_process_id', null);
          });
        }
      }
      if (field === 'accepted_quantity' && checked) {
        this.$set(edited, 'shipped_quantity', 1);
      }

      const localMaterial = this.localMaterials.find(material =>
          (material.id && material.id === item.id) ||
          (material._tempId && material._tempId === item._tempId)
      );
      if (localMaterial) {
        this.$set(localMaterial, 'shipped_quantity', edited.shipped_quantity || 0);
        this.$set(localMaterial, 'accepted_quantity', edited.accepted_quantity || 0);
        if (field === 'shipped_quantity' && !checked && this.isMechanicRepairMode) {
          this.$set(localMaterial, 'target_user_id', null);
          this.$set(localMaterial, 'target_warehouse_id', null);
          this.$set(localMaterial, 'target_building_process_id', null);
          if (Array.isArray(edited.mechanic_fulfillments)) {
            this.$set(localMaterial, 'mechanic_fulfillments', this.cloneMechanicFulfillments(edited.mechanic_fulfillments));
          }
        }
      }

      this.onMaterialFieldChange();
    },

    validateUnit(item) {
      const edited = this.getEditedItem(item);
      return edited.unit && edited.unit.trim() !== '' ? true : false;
    },

    /**
     * Обработчик фокуса на поле количества
     * Автоматически очищает поле если значение равно 0
     */
    onQuantityFocus(event, item) {
      const edited = this.getEditedItem(item);
      const value = edited.quantity;
      if (value === 0 || value === '0' || value === null || value === '') {
        edited.quantity = '';
        this.$nextTick(() => {
          const input = event.target;
          if (input && input.select) {
            input.select();
          }
        });
      } else {
        this.$nextTick(() => {
          const input = event.target;
          if (input && input.select) {
            input.select();
          }
        });
      }
    },

    /**
     * Обработчик потери фокуса на поле количества
     * Устанавливает 0 если поле пустое
     */
    onQuantityBlur(item) {
      const edited = this.getEditedItem(item);
      if (edited.quantity === '' || edited.quantity === null || edited.quantity === undefined) {
        edited.quantity = 0;
      }
    },

    // Обработчик изменения полей материала (для таблицы)
    onMaterialFieldChange() {
      if (this.isEditingList) {
        // Используем $nextTick для гарантии, что изменения применены
        this.$nextTick(() => {
          const changes = this.getMaterialsChanges();
          this.$emit('materials-list-changed', changes);
        });
      }
    },

    /**
     * Обработчик обновления материала из компонента MaterialsList
     */
    onMaterialListUpdate({index, field, value}) {
      const material = this.localMaterials[index];
      if (!material) return;

      // Запрещаем менять имя справочниковых материалов
      if ((field === 'item_name' || field === 'name') && !this.isNameEditable(material)) {
        return;
      }
      // Обновляем поле материала (поддержка разных форматов)
      if (field === 'item_name' || field === 'name') {
        material.item_name = value;
      } else if (field === 'quantity') {
        material.quantity = value;
      } else if (field === 'notes') {
        material.notes = value;
      } else if (field === 'unit' || field === 'unit_of_measurement') {
        material.unit = value;
      }

      // Обновляем editedListItems
      const key = material.id || material._tempId;
      if (this.editedListItems[key]) {
        // Обновляем правильное поле в зависимости от формата
        if (field === 'item_name' || field === 'name') {
          this.editedListItems[key].item_name = value;
        } else if (field === 'unit' || field === 'unit_of_measurement') {
          this.editedListItems[key].unit = value;
        } else {
          this.editedListItems[key][field] = value;
        }
      }

      // Эмитим событие об изменении
      this.$nextTick(() => {
        const changes = this.getMaterialsChanges();
        this.$emit('materials-list-changed', changes);
      });

  },

  /**
   * Обработчик удаления материала из компонента MaterialsList
   */
  onMaterialListRemove(index) {
    this.markForDeletion(this.localMaterials[index]);
  },
  requestSaveList() {
    this.$emit('materials-list-changed', this.getMaterialsChanges());
    this.$nextTick(() => {
      this.$emit('save-requested');
    });
  },

  getItemCatalogRequestContext() {
    return this.isMechanicAssetCatalog && this.canManageMechanicAssetSelection
        ? 'request_card_executor'
        : 'default';
  },

  normalizeAvailableCatalogMaterial(material) {
    return {
      ...material,
      id: material.id,
      name: material.name,
      unit_of_measurement: material.unit_of_measurement || material.measures || (this.isEmployeeCatalog ? 'чел.' : (this.isFuelCardCatalog ? 'карта' : (this.isMechanicAssetCatalog ? 'ед.' : 'шт'))),
      catalog_type: this.catalogType,
      catalog_name: material.catalog_name || material.catalog_group_name || null,
      accounting_type: material.accounting_type || null,
      available_quantity: material.available_quantity === undefined ? material.quantity : material.available_quantity,
      current_meter_type: material.current_meter_type || null,
      current_meter_value: material.current_meter_value === undefined ? null : material.current_meter_value,
      fuel_limits: material.fuel_limits || [],
      selected: false
    };
  },

  mergeAvailableCatalogMaterials(materials) {
    const byId = new Map((this.availableMaterials || [])
        .map(material => [Number(material.id), material])
        .filter(([id]) => Number.isInteger(id) && id > 0));

    (Array.isArray(materials) ? materials : [])
        .map(material => this.normalizeAvailableCatalogMaterial(material))
        .forEach(material => {
          const id = Number(material.id);
          if (!Number.isInteger(id) || id <= 0) {
            return;
          }
          byId.set(id, {
            ...(byId.get(id) || {}),
            ...material
          });
        });

    this.availableMaterials = Array.from(byId.values());
  },

  collectSelectedMechanicAssetIdsForCatalogHydration() {
    if (!this.isMechanicAssetCatalog) {
      return [];
    }

    const ids = new Set();
    const addId = value => {
      const normalizedId = this.normalizeSelectId(value);
      if (normalizedId) {
        ids.add(normalizedId);
      }
    };
    const rows = Array.isArray(this.localMaterials) && this.localMaterials.length > 0
        ? this.localMaterials
        : (Array.isArray(this.materials) ? this.materials : []);

    rows.forEach(item => {
      const key = item && (item.id || item._tempId);
      const edited = key ? this.editedListItems[key] : null;
      addId((edited && edited.catalog_item_id) || (item && item.catalog_item_id));
      addId(this.getMechanicRequestedCatalogItemId(item));

      const fulfillments = Array.isArray(edited && edited.mechanic_fulfillments)
          ? edited.mechanic_fulfillments
          : (Array.isArray(item && item.mechanic_fulfillments) ? item.mechanic_fulfillments : []);
      fulfillments.forEach(fulfillment => {
        addId(fulfillment && (fulfillment.asset_unit_id || fulfillment.catalog_item_id));
      });
    });

    return Array.from(ids);
  },

  async hydrateSelectedMechanicAssetCatalogItems() {
    if (!this.isMechanicAssetCatalog) {
      return;
    }

    const selectedIds = this.collectSelectedMechanicAssetIdsForCatalogHydration();
    if (selectedIds.length === 0) {
      return;
    }

    const catalogContext = this.getItemCatalogRequestContext();
    const existingIds = new Set((this.availableMaterials || [])
        .map(material => Number(material.id))
        .filter(id => Number.isInteger(id) && id > 0));
    const shouldRefreshExisting = Boolean(this.loadedCatalogContext && this.loadedCatalogContext !== catalogContext);
    const itemIds = selectedIds.filter(id => shouldRefreshExisting || !existingIds.has(id));
    if (itemIds.length === 0) {
      return;
    }

    this.materialsLoading = true;
    try {
      const materials = await this.$store.dispatch('requests_getItemCatalog', {
        requestTypeId: this.requestTypeId,
        requestId: this.requestId,
        context: catalogContext === 'request_card_executor' ? catalogContext : undefined,
        itemIds
      });
      this.mergeAvailableCatalogMaterials(materials);
    } catch (error) {
      this.$bvToast && this.$bvToast.toast('Не удалось загрузить выбранные позиции справочника', {
        title: 'Ошибка',
        variant: 'danger',
        solid: true
      });
    } finally {
      this.materialsLoading = false;
    }
  },

  markForDeletion(item) {
    const index = this.localMaterials.findIndex(m =>
        (m.id && m.id === item.id) || (m._tempId && m._tempId === item._tempId)
    );

    if (index !== -1) {
      this.localMaterials.splice(index, 1);
      this.$emit('materials-list-changed', this.getMaterialsChanges());
    }
  },

  async loadMaterials() {
    const catalogContext = this.getItemCatalogRequestContext();
    if (this.availableMaterials.length > 0 && this.loadedCatalogContext === catalogContext) {
      return Promise.resolve(this.availableMaterials);
    }
    if (this.materialsLoadPromise) {
      return this.materialsLoadPromise;
    }

    this.materialsLoading = true;
    this.materialsLoadPromise = (async () => {
      if (this.isMechanicAssetCatalog) {
        await this.ensureMechanicAssetDictionaries({loadReturnObjects: false});
      }

      try {
        const materials = await this.$store.dispatch('requests_getItemCatalog', {
          requestTypeId: this.requestTypeId,
          requestId: this.requestId,
          context: catalogContext === 'request_card_executor'
              ? catalogContext
              : undefined
        });
        if (materials && Array.isArray(materials)) {
          this.availableMaterials = materials.map(material => this.normalizeAvailableCatalogMaterial(material));
        } else {
          this.availableMaterials = [];
        }
        this.loadedCatalogContext = catalogContext;
      } catch (error) {
        this.$bvToast && this.$bvToast.toast('Ошибка загрузки материалов', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
        this.availableMaterials = [];
        this.loadedCatalogContext = null;
      }
      return this.availableMaterials;
    })()
        .finally(() => {
          this.materialsLoading = false;
          this.materialsLoadPromise = null;
        });

    return this.materialsLoadPromise;
  },

  async openMaterialSelector() {
    if (this.isMechanicAssetCatalog) {
      this.showMechanicMaterialSelector = true;
      return;
    }
    this.showMaterialSelector = true;
    await this.loadMaterials();
  },

  addMechanicCatalogPickerMaterials(materials) {
    const selectedIds = new Set((Array.isArray(materials) ? materials : [])
        .map(material => this.normalizeSelectId(material && material.id))
        .filter(Boolean));
    if (!selectedIds.size) {
      return;
    }

    this.mergeAvailableCatalogMaterials(materials);
    this.availableMaterials.forEach(material => {
      this.$set(material, 'selected', selectedIds.has(this.normalizeSelectId(material && material.id)));
    });
    this.addSelectedMaterials();
  },

  addSelectedMaterials() {
    const previousCount = this.localMaterials.length;
    const selected = this.availableMaterials.filter(m => m.selected);
    selected.forEach(material => {
      // Проверяем, нет ли уже такого материала из справочника в локальном списке
      const existsInLocal = !this.isMechanicAssetCatalog && this.localMaterials.find(m =>
          (m.materialDirectoryId === material.id || m.catalog_item_id === material.id) && (m.isCustom === false || m.isCustom === undefined)
      );
      if (!existsInLocal) {
        const accountingType = material.accounting_type || 'inventory';
        const isQuantity = this.isMechanicAssetCatalog && accountingType === 'quantity';
        const quantity = (this.isEmployeeCatalog || this.isFuelCardCatalog || (this.isMechanicAssetCatalog && !isQuantity))
            ? 1
            : 1;
        // Используем _tempId для новых материалов из справочника, чтобы отличить их от существующих в заявке
        const tempId = 'dir_' + material.id + '_' + Date.now();
        const newMaterial = {
          _tempId: tempId,
          materialDirectoryId: material.id, // Сохраняем ID из справочника для справки
          catalog_type: this.catalogType,
          catalog_item_id: material.id,
          catalog_name: material.catalog_name || null,
          accounting_type: accountingType,
          available_quantity: material.available_quantity === undefined ? null : material.available_quantity,
          target_user_id: material.target_user_id || null,
          target_building_process_id: this.mechanicAssetUsesHeaderObject ? null : (material.target_building_process_id || null),
          target_warehouse_id: material.target_warehouse_id || null,
          meter_type: material.current_meter_type || null,
          meter_value: null,
          condition: '',
          rental_counterparty: '',
          rental_operator_user_id: null,
          rental_expected_return_at: null,
          fuel_limits: material.fuel_limits || [],
          item_name: material.name,
          quantity,
          unit: material.unit_of_measurement,
          item_type: 'material',
          shipped_quantity: 0,
          accepted_quantity: 0,
          isCustom: false // Явно указываем, что это материал из справочника
        };
        this.localMaterials.push(newMaterial);

        // Добавляем в editedListItems
        this.$set(this.editedListItems, tempId, {
          _tempId: tempId,
          item_name: material.name,
          notes: '',
          quantity,
          unit: material.unit_of_measurement,
          shipped_quantity: 0,
          accepted_quantity: 0,
          catalog_type: this.catalogType,
          catalog_item_id: material.id,
          catalog_name: material.catalog_name || null,
          accounting_type: accountingType,
          available_quantity: material.available_quantity === undefined ? null : material.available_quantity,
          target_user_id: material.target_user_id || null,
          target_building_process_id: this.mechanicAssetUsesHeaderObject ? null : (material.target_building_process_id || null),
          target_warehouse_id: material.target_warehouse_id || null,
          meter_type: material.current_meter_type || null,
          meter_value: null,
          condition: '',
          rental_counterparty: '',
          rental_operator_user_id: null,
          rental_expected_return_at: null,
          fuel_limits: material.fuel_limits || [],
          isCustom: false // Явно указываем, что это материал из справочника
        });
      }
    });

    this.clearMaterialSelection();
    if (this.$refs.ppePlanner) this.$refs.ppePlanner.prepareAddedItems(this.localMaterials.slice(previousCount));
    this.$emit('materials-list-changed', this.getMaterialsChanges());
  },

  clearMaterialSelection() {
    this.availableMaterials.forEach(material => {
      material.selected = false;
    });
    this.materialSearch = '';
  },

  addCustomMaterial(bvModalEvt) {
    if (bvModalEvt) {
      bvModalEvt.preventDefault();
    }

    if (!this.canAddCustomCatalogItem) {
      return;
    }

    // Валидация
    if (!this.customMaterial.name.trim() ||
        (!this.isEmployeeCatalog && (
        !this.customMaterial.unit_of_measurement.trim() ||
        this.customMaterial.quantity <= 0))) {
      this.$bvToast && this.$bvToast.toast('Заполните все поля корректно', {
        title: 'Ошибка',
        variant: 'danger',
        solid: true
      });
      return;
    }

    // Добавляем материал в локальный список
    const tempId = 'temp_' + Date.now();
    const customUnit = this.isEmployeeCatalog
        ? 'чел.'
        : (this.customMaterial.unit_of_measurement || (this.isMechanicAssetCatalog ? 'ед.' : ''));
    const newMaterial = {
      _tempId: tempId,
      item_name: this.customMaterial.name,
      notes: this.customMaterial.notes || '',
      quantity: this.isEmployeeCatalog ? 1 : this.customMaterial.quantity,
      unit: customUnit,
      item_type: 'material',
      shipped_quantity: 0,
      accepted_quantity: 0,
      catalog_type: this.isMechanicAssetCatalog ? 'mechanic_assets' : null,
      catalog_item_id: null,
      accounting_type: this.isMechanicAssetCatalog ? 'quantity' : null,
      target_user_id: null,
      target_building_process_id: null,
      target_warehouse_id: null,
      meter_type: null,
      meter_value: null,
      condition: '',
      rental_counterparty: '',
      rental_operator_user_id: null,
      rental_expected_return_at: null,
      mechanic_fulfillments: [],
      isCustom: true
    };

    this.localMaterials.push(newMaterial);

    // Добавляем в editedListItems
    this.$set(this.editedListItems, tempId, {
      _tempId: tempId,
      item_name: this.customMaterial.name,
      notes: this.customMaterial.notes || '',
      quantity: this.isEmployeeCatalog ? 1 : this.customMaterial.quantity,
      unit: customUnit,
      shipped_quantity: 0,
      accepted_quantity: 0,
      catalog_type: this.isMechanicAssetCatalog ? 'mechanic_assets' : null,
      catalog_item_id: null,
      accounting_type: this.isMechanicAssetCatalog ? 'quantity' : null,
      target_user_id: null,
      target_building_process_id: null,
      target_warehouse_id: null,
      meter_type: null,
      meter_value: null,
      condition: '',
      rental_counterparty: '',
      rental_operator_user_id: null,
      rental_expected_return_at: null,
      mechanic_fulfillments: [],
      isCustom: true
    });

    if (this.$refs.ppePlanner) this.$refs.ppePlanner.prepareAddedItems(this.localMaterials.slice(-1));
    this.$nextTick(() => {
      this.showAddCustomMaterial = false;
    });

    this.$emit('materials-list-changed', this.getMaterialsChanges());
  },

  resetCustomMaterialForm() {
    this.customMaterial = {
      name: '',
      quantity: (this.isEmployeeCatalog || this.isFuelCardCatalog || this.isMechanicAssetCatalog) ? 1 : 0,
      unit_of_measurement: this.isEmployeeCatalog ? 'чел.' : (this.isFuelCardCatalog ? 'карта' : (this.isMechanicAssetCatalog ? 'ед.' : '')),
      notes: ''
    };
  },

  getEditedMaterialShippedQuantityForPayload(material, edited) {
    if (this.isMechanicAssetCatalog && this.shouldUseMechanicFulfillments(material)) {
      return this.getMechanicFulfillmentShippedCount(material);
    }
    return edited && edited.shipped_quantity ? edited.shipped_quantity : 0;
  },

  getEditedMaterialCatalogItemIdForPayload(material, edited) {
    if (this.isMechanicAssetCatalog && this.isMechanicShipmentMode) {
      if (edited && edited._mechanicSelectionTouched === true) {
        return this.normalizeSelectId(edited.catalog_item_id);
      }
      if (!this.isMechanicStoredShipmentSelectionConfirmed(material)) {
        return this.normalizeSelectId(material.catalog_item_id);
      }
      return this.normalizeSelectId(edited && edited.catalog_item_id);
    }
    return edited && edited.isCustom && !this.isMechanicAssetCatalog
        ? null
        : (this.normalizeSelectId(edited && edited.catalog_item_id) ||
            this.normalizeSelectId(material && material.catalog_item_id) ||
            this.normalizeSelectId(material && material.materialDirectoryId));
  },

  getEditedMechanicActionResultForPayload(material, edited) {
    if (!this.isMechanicAssetCatalog) {
      return {};
    }
    const value = (edited && edited.mechanic_action_result) ||
        (material && material.mechanic_action_result) ||
        {};
    return this.clonePlainObject(value);
  },

  normalizeMechanicActionResultComparable(value) {
    return JSON.stringify(this.clonePlainObject(value));
  },

  getMaterialsChanges() {
    const changes = {
      added: [],
      updated: [],
      deleted: []
    };

    // Находим добавленные и измененные материалы
    this.localMaterials.forEach(material => {
      const key = material.id || material._tempId;
      const edited = this.editedListItems[key];

      // Если edited не существует, пропускаем
      if (!edited) {
        return;
      }
      const effectiveShippedQuantity = this.getEditedMaterialShippedQuantityForPayload(material, edited);
      const effectiveCatalogItemId = this.getEditedMaterialCatalogItemIdForPayload(material, edited);
      const effectiveMechanicActionResult = this.getEditedMechanicActionResultForPayload(material, edited);

      // Новый материал - если есть _tempId (кастомный или из справочника, но еще не в заявке)
      if (material._tempId && !material.id) {
        const shouldKeepCatalogType = edited.isCustom !== true || this.isMechanicAssetCatalog;
        changes.added.push({
          item_name: edited.item_name || '',
          notes: edited.notes || '',
          ppe_data: edited.ppe_data || null,
          quantity: edited.quantity || 0,
          unit: edited.unit || '',
          shipped_quantity: effectiveShippedQuantity,
          accepted_quantity: edited.accepted_quantity || 0,
          shipping_comment: this.normalizeCommentValue(edited.shipping_comment),
          catalog_type: shouldKeepCatalogType
              ? (edited.catalog_type || material.catalog_type || this.catalogType || null)
              : null,
          catalog_item_id: effectiveCatalogItemId,
          accounting_type: edited.accounting_type || material.accounting_type || null,
          target_user_id: edited.target_user_id || null,
          target_building_process_id: this.mechanicAssetUsesHeaderObject ? null : (edited.target_building_process_id || null),
          target_warehouse_id: edited.target_warehouse_id || null,
          meter_type: edited.meter_type || null,
          meter_value: edited.meter_value === undefined ? null : edited.meter_value,
          condition: edited.condition || null,
          rental_counterparty: edited.rental_counterparty || null,
          rental_operator_user_id: edited.rental_operator_user_id || null,
          rental_expected_return_at: edited.rental_expected_return_at || null,
          mechanic_action_result: effectiveMechanicActionResult,
          mechanic_fulfillments: this.cloneMechanicFulfillments(edited.mechanic_fulfillments || []),
          fuel_limits: this.cloneFuelLimits(edited.fuel_limits || material.fuel_limits || []),
          isCustom: edited.isCustom || false
        });
      } else if (material.id) {
        // Существующий материал (есть id - это ID записи в request_items) - проверяем изменения
        const original = this.materials.find(m => m.id === material.id);
        if (original) {
          // Сравниваем значения, учитывая возможные типы (числа/строки)
          const nameChanged = String(edited.item_name || '').trim() !== String(original.item_name || '').trim();
          const ppeChanged = JSON.stringify(edited.ppe_data || null) !== JSON.stringify(original.ppe_data || null);
          const notesChanged = String(edited.notes || '').trim() !== String(original.notes || '').trim();
          const quantityChanged = parseFloat(edited.quantity || 0) !== parseFloat(original.quantity || 0);
          const unitChanged = String(edited.unit || '').trim() !== String(original.unit || '').trim();
          const shippedQuantityChanged = parseFloat(effectiveShippedQuantity || 0) !== parseFloat(original.shipped_quantity || 0);
          const acceptedQuantityChanged = parseFloat(edited.accepted_quantity || 0) !== parseFloat(original.accepted_quantity || 0);
          const shippingCommentChanged =
              this.normalizeCommentValue(edited.shipping_comment) !==
              this.normalizeCommentValue(original.shipping_comment);
          const targetUserChanged = Number(edited.target_user_id || 0) !== Number(original.target_user_id || 0);
          const effectiveEditedTargetBuildingProcessId = this.mechanicAssetUsesHeaderObject
              ? null
              : edited.target_building_process_id;
          const targetBuildingProcessChanged = Number(effectiveEditedTargetBuildingProcessId || 0) !== Number(original.target_building_process_id || 0);
          const targetWarehouseChanged = Number(edited.target_warehouse_id || 0) !== Number(original.target_warehouse_id || 0);
          const catalogTypeChanged = String(edited.catalog_type || '') !== String(original.catalog_type || '');
          const catalogItemChanged = Number(effectiveCatalogItemId || 0) !== Number(original.catalog_item_id || 0);
          const isCustomChanged = Boolean(edited.isCustom) !== Boolean(original.isCustom || original.is_custom);
          const meterTypeChanged = String(edited.meter_type || '') !== String(original.meter_type || '');
          const meterValueChanged = Number(edited.meter_value || 0) !== Number(original.meter_value || 0);
          const conditionChanged = String(edited.condition || '').trim() !== String(original.condition || '').trim();
          const rentalCounterpartyChanged = String(edited.rental_counterparty || '').trim() !== String(original.rental_counterparty || '').trim();
          const rentalOperatorChanged = Number(edited.rental_operator_user_id || 0) !== Number(original.rental_operator_user_id || 0);
          const rentalExpectedReturnChanged = String(edited.rental_expected_return_at || '').slice(0, 10) !==
              String(original.rental_expected_return_at || '').slice(0, 10);
          const fuelLimitsChanged =
              this.normalizeFuelLimitsComparable(edited.fuel_limits || []) !==
              this.normalizeFuelLimitsComparable(original.fuel_limits || []);
          const mechanicFulfillmentsChanged =
              this.normalizeMechanicFulfillmentsComparable(edited.mechanic_fulfillments || []) !==
              this.normalizeMechanicFulfillmentsComparable(original.mechanic_fulfillments || []);
          const mechanicActionResultChanged =
              this.normalizeMechanicActionResultComparable(effectiveMechanicActionResult) !==
              this.normalizeMechanicActionResultComparable(original.mechanic_action_result || {});

          if (ppeChanged || nameChanged || notesChanged || quantityChanged || unitChanged || targetUserChanged ||
              targetBuildingProcessChanged || targetWarehouseChanged || catalogTypeChanged || catalogItemChanged ||
              shippedQuantityChanged || acceptedQuantityChanged || isCustomChanged || meterTypeChanged || meterValueChanged || conditionChanged || rentalCounterpartyChanged || rentalOperatorChanged ||
              rentalExpectedReturnChanged || fuelLimitsChanged || mechanicFulfillmentsChanged || mechanicActionResultChanged || shippingCommentChanged) {
            changes.updated.push({
              id: material.id,
              item_name: edited.item_name || '',
              notes: edited.notes || '',
              ppe_data: edited.ppe_data || null,
              quantity: edited.quantity || 0,
              unit: edited.unit || '',
              shipped_quantity: effectiveShippedQuantity,
              accepted_quantity: edited.accepted_quantity || 0,
              shipping_comment: this.normalizeCommentValue(edited.shipping_comment),
              catalog_type: edited.catalog_type || material.catalog_type || null,
              catalog_item_id: effectiveCatalogItemId,
              accounting_type: edited.accounting_type || material.accounting_type || null,
              isCustom: edited.isCustom === true,
              target_user_id: edited.target_user_id || null,
              target_building_process_id: this.mechanicAssetUsesHeaderObject ? null : (edited.target_building_process_id || null),
              target_warehouse_id: edited.target_warehouse_id || null,
              meter_type: edited.meter_type || null,
              meter_value: edited.meter_value === undefined ? null : edited.meter_value,
              condition: edited.condition || null,
              rental_counterparty: edited.rental_counterparty || null,
              rental_operator_user_id: edited.rental_operator_user_id || null,
              rental_expected_return_at: edited.rental_expected_return_at || null,
              mechanic_action_result: effectiveMechanicActionResult,
              mechanic_fulfillments: this.cloneMechanicFulfillments(edited.mechanic_fulfillments || []),
              fuel_limits: this.cloneFuelLimits(edited.fuel_limits || [])
            });
          }
        }
      }
    });

    // Находим удаленные материалы (есть в оригинале, но нет в localMaterials)
    this.materials.forEach(original => {
      if (!original.id) return;

      const exists = this.localMaterials.find(m => m.id === original.id);
      if (!exists) {
        changes.deleted.push({
          id: original.id,
          _delete: true
        });
      }
    });

    return changes;
  },

  async cancelEditingList() {
    const hasAnyChanges =
        (this.localMaterials && this.localMaterials.length !== this.materials.length) ||
        (this.getMaterialsChanges && (
            this.getMaterialsChanges().added.length ||
            this.getMaterialsChanges().updated.length ||
            this.getMaterialsChanges().deleted.length
        ));

    if (hasAnyChanges) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
          'Закрыть редактирование списка? Несохранённые изменения будут потеряны. ' +
          'Изменения сохранятся по кнопке «Сохранить изменения» в нижней панели заявки.',
          { contentClass: 'argus-glass-dialog',
            title: 'Подтверждение',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'warning',
            okTitle: 'Не сохранять',
            cancelTitle: 'Продолжить редактирование',
            centered: true
          }
      );
      if (!confirmed) return;
    }

    this.isEditingList = false;
    this.editingListStarting = false;
    this.editingListPrepared = false;
    this.localMaterials = [];
    this.editedListItems = {};
    this.showMechanicMaterialSelector = false;
    this.$emit('editing-list-cancelled');
  },

  initHorizontalDrag() {
    const el = this.$refs.materialsScroll;
    this.destroyHorizontalDrag();

    if (!el || !this.enableHorizontalDrag) return;

    // не мешаем кликам по интерактивным элементам
    const isInteractive = (target) => {
      const t = target && target.closest
          ? target.closest('input, textarea, select, button, a, label, .btn, .form-control, .custom-control, .b-form')
          : null;
      return !!t;
    };

    const onMouseDown = (e) => {
      if (e.button !== 0) return; // только ЛКМ
      if (isInteractive(e.target)) return;

      this._dragActive = true;
      this._dragStartX = e.pageX;
      this._dragStartScrollLeft = el.scrollLeft;

      el.classList.add('dragging');
    };

    const onMouseMove = (e) => {
      if (!this._dragActive) return;
      e.preventDefault();
      const dx = e.pageX - this._dragStartX;
      el.scrollLeft = this._dragStartScrollLeft - dx;
    };

    const endDrag = () => {
      this._dragActive = false;
      el.classList.remove('dragging');
    };

    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mousemove', onMouseMove);
    el.addEventListener('mouseleave', endDrag);
    document.addEventListener('mouseup', endDrag);

    // сохраним для очистки
    this._dragHandlers = {el, onMouseDown, onMouseMove, endDrag};
  },
  updateHorizontalScrollState() {
    const el = this.$refs.materialsScroll;
    if (!el) {
      this.hasHorizontalScroll = false;
      return;
    }

    const has = el.scrollWidth > el.clientWidth + 2;
    if (has !== this.hasHorizontalScroll) {
      this.hasHorizontalScroll = has;
    }
  },
  finishEditingListAfterSave() {
    this.isEditingList = false;
    this.editingListStarting = false;
    this.editingListPrepared = false;
    this.localMaterials = [];
    this.editedListItems = {};
    this.materialSearch = '';
    this.showAddCustomMaterial = false;
    this.showMaterialSelector = false;
    this.showMechanicMaterialSelector = false;
    if (this.isMechanicAssetCatalog) {
      this.availableMaterials = [];
      this.loadedCatalogContext = null;
    }

    // чтобы RequestCard дернул requestCard_cancelEditingMaterialsList()
    this.$emit('editing-list-cancelled');
  },
  hasDirtyState() {
    return this.isEditing || this.isEditingList || this.hasChanges || this.showFuelCardLimitsModal;
  },
  isNameEditable(item) {
    const edited = this.getEditedItem(item);

    // кастомный (вручную) — разрешаем
    const isCustom =
        !!item.is_custom ||
        !!item.isCustom ||
        !!(edited && edited.isCustom);

    // справочник — запрещаем
    return isCustom;
  }
  },


  mounted() {
    this.enableHorizontalDrag = this.canUseHorizontalDrag();
    if (!this.$store.state.fuelTypes || this.$store.state.fuelTypes.length === 0) {
      this.$store.dispatch('getTypesOfFuel').catch(() => {});
    }
    this.loadFuelCardOptions();
    this.loadFuelCardTargetObjectOptions();

    this.$nextTick(() => {
      this.initHorizontalDrag();
      this.updateHorizontalScrollState();
    });

    window.addEventListener('resize', this.updateHorizontalScrollState, { passive: true });
  },
  updated() {
    this.$nextTick(() => this.updateHorizontalScrollState());
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateHorizontalScrollState);
    this.destroyHorizontalDrag();
  },


  watch: {
        savedMaterialsRevision() {
            // Подтверждённые строки больше не являются черновиками с временными ID,
            // даже если следующий шаг (смена объекта) завершился ошибкой.
            this.localMaterials = JSON.parse(JSON.stringify(this.materials));
            this.editedListItems = {};
            if (this.isEditingList) this.localMaterials.forEach(item => this.getEditedItem(item));
        },
        bulkReassignFromExecutorId(newVal) {
            if (Number(this.bulkReassignExecutorId) === Number(newVal)) {
                this.bulkReassignExecutorId = null;
            }
            if (this.showBulkReassignModal) {
                this.setDefaultBulkReassignSelection();
            }
        },
        // Отслеживаем изменения в режиме редактирования списка
        isEditingList(newVal) {
            if (newVal) {
                // Синхронизируем localMaterials с materials при входе в режим редактирования
                if (!this.editingListPrepared) {
                    this.localMaterials = JSON.parse(JSON.stringify(this.materials));
                }
            } else {
                this.editingListPrepared = false;
            }
        },
        // Синхронизируем изменения editedListItems с localMaterials
        // Этот watcher используется как резервный механизм для синхронизации данных
        // Основная эмиссия событий происходит через onMaterialFieldChange
        editedListItems: {
            handler() {
                if (this.isEditingList) {
                    // Синхронизируем localMaterials с editedListItems
                    this.localMaterials.forEach(material => {
                        const key = material.id || material._tempId;
                        const edited = this.editedListItems[key];
                        if (edited) {
                            material.item_name = edited.item_name;
                            material.notes = edited.notes;
                            material.quantity = edited.quantity;
                            material.unit = edited.unit;
                            material.shipped_quantity = edited.shipped_quantity;
                            material.accepted_quantity = edited.accepted_quantity;
                            material.shipping_comment = edited.shipping_comment;
                            material.catalog_type = edited.catalog_type;
                            material.catalog_item_id = edited.catalog_item_id;
                            material.catalog_name = edited.catalog_name;
                            material.mechanic_asset_name = edited.mechanic_asset_name;
                            material.asset_name = edited.asset_name;
                            material.accounting_type = edited.accounting_type;
                            material.available_quantity = edited.available_quantity;
                            material.status = edited.status;
                            material.status_label = edited.status_label;
                            material.assignment_details = edited.assignment_details;
                            material.active_transfer = edited.active_transfer;
                            material.current_meter_type = edited.current_meter_type;
                            material.current_meter_value = edited.current_meter_value;
                            material.isCustom = edited.isCustom;
                            material.target_user_id = edited.target_user_id;
                            material.target_building_process_id = edited.target_building_process_id;
                            material.target_warehouse_id = edited.target_warehouse_id;
                            material.meter_type = edited.meter_type;
                            material.meter_value = edited.meter_value;
                            material.condition = edited.condition;
                            material.rental_counterparty = edited.rental_counterparty;
                            material.rental_operator_user_id = edited.rental_operator_user_id;
                            material.rental_expected_return_at = edited.rental_expected_return_at;
                            material.mechanic_action_result = edited.mechanic_action_result;
                            material._requestedCatalogItemId = edited._requestedCatalogItemId;
                            material._mechanicSelectionTouched = edited._mechanicSelectionTouched;
                            material.mechanic_fulfillments = this.cloneMechanicFulfillments(edited.mechanic_fulfillments);
                        }
                    });
                    // Эмиссия события через watcher отключена, чтобы избежать двойной эмиссии
                    // События эмитятся через onMaterialFieldChange при изменении полей
                }
            },
            deep: true
        },
        canManageFuelCardSelection() {
            this.loadFuelCardOptions();
            this.loadFuelCardTargetObjectOptions();
        },
        fuelCardOperation() {
            this.loadFuelCardOptions();
            this.loadFuelCardTargetObjectOptions();
        }
    }
};
</script>

<style scoped>
/* Используем только Bootstrap классы и минимальные кастомные стили */
.materials-table >>> .table {
    font-size: 0.9rem;
}

.materials-table {
    max-width: 100%;
    min-width: 0;
}

.materials-table >>> .mechanic-table-fit {
    table-layout: fixed;
    width: 100%;
}

.materials-table >>> .mechanic-table-fit th,
.materials-table >>> .mechanic-table-fit td {
    min-width: 0;
    overflow-wrap: anywhere;
}

/* Подсветка «своих» строк исполнителя в multi-режиме */
.materials-table >>> .own-executor-row > td {
    background-color: rgba(40, 167, 69, 0.10) !important;
}
.materials-table >>> .own-executor-row > td:first-child {
    box-shadow: inset 4px 0 0 0 #28a745;
}

.bulk-reassign-list {
    max-height: 320px;
    overflow-y: auto;
}

.bulk-reassign-list >>> .custom-control {
    margin-bottom: .35rem;
}

.bulk-reassign-list >>> .custom-control:last-child {
    margin-bottom: 0;
}

.materials-table >>> .table thead th {
    position: sticky;
    top: 0;
    z-index: 10;
    border-top: none;
    font-weight: 600;
    padding: 0.75rem 0.5rem;
    vertical-align: middle;
}

.materials-table >>> .table td {
    padding: 0.5rem;
    vertical-align: middle;
}

.mechanic-shipment-bulk-controls__field {
    min-width: 260px;
}

.materials-edit-loading {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    border: 1px solid #bee5eb;
    border-left: 4px solid #17a2b8;
    border-radius: 4px;
    background: #e8f7fa;
    color: #0c5460;
    font-weight: 600;
}

/* Ячейка с названием материала выравнивается по верху */
.materials-table >>> .table td:has(.material-name-edit),
.materials-table >>> .table td:has(.material-name) {
    vertical-align: top;
}

.material-name {
    line-height: 1.4;
}

.material-name-edit {
    min-width: 100%;
}

.mechanic-asset-selection-cell {
    min-width: 0;
    padding: 8px;
    border: 2px solid rgba(23, 162, 184, 0.32);
    border-radius: 6px;
    background: #eef9fb;
    box-shadow: inset 4px 0 0 rgba(23, 162, 184, 0.45);
    transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.mechanic-asset-selection-cell--required {
    border-color: #fd7e14;
    background: #fff3cd;
    box-shadow:
        inset 5px 0 0 #fd7e14,
        0 0 0 2px rgba(253, 126, 20, 0.22);
}

.mechanic-asset-selection-cell--required .badge-warning {
    color: #5f3200;
    background-color: #ffc107;
}

.mechanic-asset-selection-cell--manual {
    border-color: #6f42c1;
    background: #f5f0ff;
    box-shadow: inset 5px 0 0 rgba(111, 66, 193, 0.72);
}

.mechanic-asset-selection-cell--suggested {
    border-color: #f0ad4e;
    background: #fff8e8;
    box-shadow: inset 5px 0 0 rgba(240, 173, 78, 0.9);
}

.mechanic-asset-selection-cell--manual ::v-deep(.vs__dropdown-toggle) {
    border-color: rgba(111, 66, 193, 0.65);
    background: #fff;
}

.mechanic-asset-selection-hint {
    display: flex;
    align-items: center;
    margin: -1px 0 6px;
    color: #7a3f00;
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.2;
    text-transform: uppercase;
}

.mechanic-asset-selection-hint--manual {
    color: #4f2d86;
}

.mechanic-asset-selection-cell--required ::v-deep(.vs__dropdown-toggle) {
    min-height: 38px;
    border: 2px solid #fd7e14;
    background: #fffdf5;
    box-shadow: 0 0 0 0.18rem rgba(255, 193, 7, 0.28);
}

.mechanic-asset-selection-cell--required ::v-deep(.vs__search::placeholder) {
    color: #8a4b00;
    font-weight: 600;
}

.mechanic-requested-asset-suggestion {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
    padding: 7px;
    border: 1px solid rgba(240, 173, 78, 0.55);
    border-radius: 4px;
    background: #fff;
}

.mechanic-requested-asset-suggestion__body {
    min-width: 0;
    line-height: 1.25;
}

.mechanic-requested-asset-suggestion__action {
    flex-shrink: 0;
    white-space: nowrap;
}

.mechanic-asset-select {
    min-width: 260px;
}

.mechanic-asset-picker-control,
.mechanic-fulfillment-picker {
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    gap: 8px;
    min-width: 260px;
    padding: 7px;
    border: 1px solid rgba(23, 162, 184, 0.28);
    border-radius: 4px;
    background: #fff;
}

.mechanic-asset-picker-control__summary,
.mechanic-fulfillment-picker__summary,
.mechanic-asset-picker-control__empty,
.mechanic-fulfillment-picker__empty {
    flex: 1 1 auto;
    min-width: 0;
    align-self: center;
    line-height: 1.25;
}

.mechanic-asset-picker-control__title,
.mechanic-fulfillment-picker__title {
    font-weight: 700;
    overflow-wrap: anywhere;
}

.mechanic-asset-picker-control__empty,
.mechanic-fulfillment-picker__empty {
    color: #6c757d;
    font-size: 0.85rem;
}

.mechanic-asset-picker-control__actions,
.mechanic-fulfillment-picker__actions {
    display: flex;
    flex: 0 0 auto;
    gap: 6px;
    align-items: center;
}

.mechanic-fulfillments {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.mechanic-fulfillment-row {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) auto;
    gap: 6px;
    align-items: flex-start;
    padding: 6px;
    border: 1px solid rgba(111, 66, 193, 0.24);
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.72);
}

.mechanic-fulfillment-select {
    min-width: 0;
}

.mechanic-fulfillment-meta {
    grid-column: 1 / -1;
    min-width: 0;
}

.mechanic-asset-selected-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 6px;
    margin-top: 4px;
}

.mechanic-asset-selected-title {
    display: flex;
    min-width: 0;
    flex-wrap: wrap;
    align-items: center;
    gap: 3px 4px;
    font-weight: 700;
    line-height: 1.25;
}

.mechanic-asset-card-link {
    color: #0056b3;
    font-weight: 600;
    text-decoration: none;
}

.mechanic-asset-card-link:hover {
    color: #003f86;
    text-decoration: underline;
}

.mechanic-asset-card-link--name {
    min-width: 0;
    max-width: 100%;
    overflow-wrap: anywhere;
}

.mechanic-fulfillment-quantity {
    grid-column: 1 / -1;
    max-width: 180px;
}

.mechanic-fulfillments-readonly {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.mechanic-fulfillment-readonly-row {
    padding: 6px 8px;
    border-left: 4px solid #6f42c1;
    border-radius: 4px;
    background: #f5f0ff;
}

.mechanic-asset-details-line {
    display: block;
    margin-top: 4px;
    color: #6c757d;
    font-size: 0.82rem;
    line-height: 1.25;
    overflow-wrap: anywhere;
}

.material-name-edit .form-control,
.material-name-edit .form-control-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    height: calc(1.5em + 0.5rem + 2px);
}

.material-name-edit textarea {
    min-height: 32px;
    padding: 0.25rem 0.5rem;
    resize: vertical;
    font-size: 0.875rem;
}

.quantity-edit {
    min-width: 140px;
    white-space: nowrap;
}

.quantity-badge {
    display: inline-block;
}

.shipped-input,
.accepted-input {
    max-width: 150px;
}

.operation-comment-input {
    min-width: 150px;
    resize: vertical;
}

.operation-comment-toggle {
    font-size: 0.8rem;
    line-height: 1.2;
}

.operation-comment-text {
    max-width: 170px;
    white-space: pre-line;
    overflow-wrap: anywhere;
}

.linkified-text {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}

.progress-container {
    min-width: 120px;
}

.status-badge {
    font-size: 0.8rem;
}

.fuel-card-target-user-cell {
    min-width: 220px;
}

.fuel-card-selection-cell {
    min-width: 380px;
}

.fuel-card-target-object-cell {
    min-width: 260px;
}

.fuel-card-limits-cell {
    min-width: 280px;
}

.materials-table >>> .mechanic-table-fit .mechanic-asset-select,
.materials-table >>> .mechanic-table-fit .mechanic-asset-picker-control,
.materials-table >>> .mechanic-table-fit .mechanic-fulfillment-picker,
.materials-table >>> .mechanic-table-fit .fuel-card-target-user-cell,
.materials-table >>> .mechanic-table-fit .fuel-card-target-object-cell,
.materials-table >>> .mechanic-table-fit .fuel-card-limits-cell,
.materials-table >>> .mechanic-table-fit .quantity-edit,
.materials-table >>> .mechanic-table-fit .shipped-input,
.materials-table >>> .mechanic-table-fit .accepted-input,
.materials-table >>> .mechanic-table-fit .operation-comment-input,
.materials-table >>> .mechanic-table-fit .operation-comment-text,
.materials-table >>> .mechanic-table-fit .progress-container {
    max-width: 100%;
    min-width: 0;
}

.materials-table >>> .mechanic-table-fit .vs__selected {
    max-width: calc(100% - 28px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.quick-fill-all-btn,
.quick-fill-row-btn {
    transition: all 0.2s ease;
}

.quick-fill-all-btn:hover,
.quick-fill-row-btn:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.table-scroll {
  width: 100%;
  max-width: 100%;
  overflow-x: scroll;            /* чтобы полоса появлялась чаще */
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable;      /* где поддерживается — резервируем место */
  cursor: auto;
}

.table-scroll.dragging {
  cursor: grabbing;
  user-select: none;
}

/* Толстый и заметный скроллбар (Chromium/WebKit). В Telegram Desktop обычно сработает. */
.table-scroll::-webkit-scrollbar {
  height: 14px;
}
.table-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 123, 255, 0.55);  /* “синий Bootstrap” */
  border-radius: 10px;
}
.table-scroll::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.06);
}

/* Firefox (если вдруг) */
.table-scroll {
  scrollbar-width: auto;
  scrollbar-color: rgba(0, 123, 255, 0.55) rgba(0, 0, 0, 0.06);
}

/* Подсказка */
.scroll-hint {
  padding: 0.45rem 0.75rem;
  font-size: 0.8rem;
  color: #6c757d;
  background: #f8f9fa;
  border-top: 1px solid rgba(0,0,0,0.075);
}

/* На тач-устройствах оставляем drag-подсказку и курсор */
.scroll-hint--mouse { display: none; }

@media (pointer: coarse) {
  .table-scroll {
    cursor: grab;
  }

  .scroll-hint--touch { display: block; }
}

@media (pointer: fine) {
  .scroll-hint--touch { display: none; }
}

@media (max-width: 767.98px) {
  .table-scroll {
    overflow-x: visible;
    overflow-y: visible;
    cursor: auto;
    scrollbar-width: none;
  }

  .table-scroll::-webkit-scrollbar {
    display: none;
  }

  .scroll-hint {
    display: none !important;
  }

  .materials-table >>> .b-table-stacked-md {
    display: block;
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
  }

  .materials-table >>> .b-table-stacked-md > tbody {
    display: block;
    counter-reset: materials-mobile-row;
    padding: .15rem 0;
  }

  .materials-table >>> .b-table-stacked-md > tbody > tr {
    display: block;
    counter-increment: materials-mobile-row;
    margin: 0 .5rem 1rem;
    border: 1px solid #cfd8e3;
    border-left: 5px solid #17a2b8;
    border-radius: .55rem;
    background: #fff;
    box-shadow: 0 4px 12px rgba(15, 23, 42, .08);
    overflow: hidden;
  }

  .materials-table >>> .b-table-stacked-md > tbody > tr:nth-child(even) {
    border-left-color: #6f42c1;
    background: #fcfbff;
  }

  .materials-table >>> .b-table-stacked-md > tbody > tr::before {
    content: "Позиция " counter(materials-mobile-row);
    display: flex;
    align-items: center;
    min-height: 34px;
    padding: .45rem .75rem;
    border-bottom: 1px solid #dfe6ee;
    background: #eef8fb;
    color: #22313f;
    font-size: .78rem;
    font-weight: 800;
    line-height: 1.2;
  }

  .materials-table >>> .b-table-stacked-md > tbody > tr:nth-child(even)::before {
    background: #f4efff;
  }

  .materials-table >>> .b-table-stacked-md > tbody > tr > td {
    display: flex !important;
    align-items: flex-start;
    justify-content: space-between;
    gap: .75rem;
    width: 100% !important;
    min-width: 0;
    padding: .6rem .75rem;
    border-top: 1px solid #edf1f5;
    text-align: left !important;
    white-space: normal;
    background: transparent;
  }

  .materials-table >>> .b-table-stacked-md > tbody > tr > td:first-child {
    border-top: 0;
  }

  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="№"] {
    display: none !important;
  }

  .materials-table >>> .b-table-stacked-md > tbody > tr > td::before {
    content: attr(data-label);
    display: block;
    flex: 0 0 38%;
    max-width: 42%;
    margin: 0;
    padding: 0;
    color: #6c757d;
    font-size: .78rem;
    font-weight: 700;
    line-height: 1.25;
    text-align: left;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .materials-table >>> .b-table-stacked-md > tbody > tr > td > * {
    flex: 1 1 0;
    min-width: 0;
    max-width: 100%;
  }

  .materials-table >>> .b-table-stacked-md > tbody > tr > td.mechanic-mobile-full-cell,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Отгружаем"],
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Техника / инструмент"],
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="МОЛ / получатель"],
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Ответственный за ремонт"],
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Сценарий ремонта"],
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Объект назначения"],
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Вернуть на объект"],
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Куда: склад / ремзона"],
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Аренда"],
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Пробег / моточасы"],
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Состояние"] {
    display: block !important;
  }

  .materials-table >>> .b-table-stacked-md > tbody > tr > td.mechanic-mobile-full-cell::before,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Отгружаем"]::before,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Техника / инструмент"]::before,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="МОЛ / получатель"]::before,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Ответственный за ремонт"]::before,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Сценарий ремонта"]::before,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Объект назначения"]::before,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Вернуть на объект"]::before,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Куда: склад / ремзона"]::before,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Аренда"]::before,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Пробег / моточасы"]::before,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Состояние"]::before {
    flex: none;
    max-width: none;
    margin-bottom: .4rem;
    color: #495057;
  }

  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="МОЛ / получатель"]::before {
    white-space: nowrap;
    overflow-wrap: normal;
  }

  .materials-table >>> .b-table-stacked-md > tbody > tr > td.mechanic-mobile-full-cell > *,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Отгружаем"] > *,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Техника / инструмент"] > *,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="МОЛ / получатель"] > *,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Ответственный за ремонт"] > *,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Сценарий ремонта"] > *,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Объект назначения"] > *,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Вернуть на объект"] > *,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Куда: склад / ремзона"] > *,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Аренда"] > *,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Пробег / моточасы"] > *,
  .materials-table >>> .b-table-stacked-md > tbody > tr > td[data-label="Состояние"] > * {
    width: 100%;
    min-width: 0;
    max-width: 100%;
  }

  .materials-table >>> .own-executor-row {
    box-shadow:
        inset 4px 0 0 #28a745,
        0 4px 12px rgba(15, 23, 42, .08);
  }

  .materials-table >>> .own-executor-row > td:first-child {
    box-shadow: none;
  }

  .materials-table >>> .b-table-stacked-md .form-control,
  .materials-table >>> .b-table-stacked-md .custom-select,
  .materials-table >>> .b-table-stacked-md .input-group {
    width: 100%;
    max-width: none !important;
  }

  .materials-table >>> .b-table-stacked-md .progress-container,
  .materials-table >>> .b-table-stacked-md .quantity-edit,
  .materials-table >>> .b-table-stacked-md .shipped-input,
  .materials-table >>> .b-table-stacked-md .accepted-input,
  .materials-table >>> .b-table-stacked-md .operation-comment-input,
  .materials-table >>> .b-table-stacked-md .operation-comment-text,
  .materials-table >>> .b-table-stacked-md .mechanic-asset-select,
  .materials-table >>> .b-table-stacked-md .mechanic-asset-picker-control,
  .materials-table >>> .b-table-stacked-md .mechanic-fulfillment-picker,
  .materials-table >>> .b-table-stacked-md .fuel-card-target-user-cell,
  .materials-table >>> .b-table-stacked-md .fuel-card-selection-cell,
  .materials-table >>> .b-table-stacked-md .fuel-card-target-object-cell,
  .materials-table >>> .b-table-stacked-md .fuel-card-limits-cell {
    width: 100%;
    min-width: 0;
    max-width: 100%;
  }

  .materials-table >>> .b-table-stacked-md .fuel-card-limits-cell,
  .materials-table >>> .b-table-stacked-md .mechanic-fulfillment-row {
    display: flex;
    flex-direction: column;
    gap: .5rem;
  }

  .materials-table >>> .b-table-stacked-md .mechanic-requested-asset-suggestion {
    flex-direction: column;
    align-items: stretch;
  }

  .materials-table >>> .b-table-stacked-md .mechanic-requested-asset-suggestion__action {
    width: 100%;
  }

  .materials-table >>> .b-table-stacked-md .mechanic-fulfillment-row {
    grid-template-columns: minmax(0, 1fr);
    width: 100%;
  }

  .materials-table >>> .b-table-stacked-md .mechanic-asset-selection-cell,
  .materials-table >>> .b-table-stacked-md .mechanic-fulfillments,
  .materials-table >>> .b-table-stacked-md .mechanic-asset-picker-control,
  .materials-table >>> .b-table-stacked-md .mechanic-fulfillment-picker,
  .materials-table >>> .b-table-stacked-md .mechanic-fulfillments-readonly,
  .materials-table >>> .b-table-stacked-md .mechanic-fulfillment-readonly-row,
  .materials-table >>> .b-table-stacked-md .mechanic-asset-details-line--transfer,
  .materials-table >>> .b-table-stacked-md .mechanic-asset-selected-meta,
  .materials-table >>> .b-table-stacked-md .mechanic-fulfillment-meta {
    width: 100%;
    min-width: 0;
    max-width: 100%;
  }

  .materials-table >>> .b-table-stacked-md .mechanic-asset-selection-cell {
    padding: .65rem;
  }

  .materials-table >>> .b-table-stacked-md .mechanic-asset-picker-control,
  .materials-table >>> .b-table-stacked-md .mechanic-fulfillment-picker {
    flex-direction: column;
    gap: .5rem;
  }

  .materials-table >>> .b-table-stacked-md .mechanic-asset-picker-control__actions,
  .materials-table >>> .b-table-stacked-md .mechanic-fulfillment-picker__actions {
    width: 100%;
  }

  .materials-table >>> .b-table-stacked-md .mechanic-asset-picker-control__actions .btn:first-child,
  .materials-table >>> .b-table-stacked-md .mechanic-fulfillment-picker__actions .btn:first-child {
    flex: 1 1 auto;
  }

  .materials-table >>> .b-table-stacked-md .mechanic-asset-selected-title,
  .materials-table >>> .b-table-stacked-md .mechanic-asset-selected-meta,
  .materials-table >>> .b-table-stacked-md .mechanic-fulfillment-meta {
    align-items: flex-start;
  }

  .materials-table >>> .b-table-stacked-md .mechanic-asset-card-link--name {
    display: inline;
    max-width: 100%;
    overflow-wrap: anywhere;
  }

  .materials-table >>> .b-table-stacked-md .mechanic-fulfillment-quantity {
    width: 100%;
    max-width: none;
  }

  .materials-table >>> .b-table-stacked-md .status-badge {
    white-space: normal;
    text-align: left;
  }

  .fuel-card-target-user-cell,
  .fuel-card-selection-cell,
  .fuel-card-target-object-cell,
  .fuel-card-limits-cell {
    min-width: 0;
    width: 100%;
    max-width: 100%;
  }

  .fuel-card-limits-cell {
    gap: 0.5rem;
  }

}


</style>
